import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type {
  ChatMessage,
  MailDocumentPayload,
  MessageWhomPayload,
  PageNavigationExtraction,
  PatientOption,
  BloodTestPrescriptionExtraction,
  PrescriptionExtraction,
  RenewOrdonnanceExtraction,
  SecretaryMessagePayload,
} from '../types/chat'
import {
  postSportsCertificateMail,
} from '../api/sportsCertificateApi'
import {
  buildMedicalLinkNavigationUrl,
  buildMedicalLinkPatientDossierUrl,
  buildOfficeEmbedViewerUrl,
  buildVoiceNavUrl,
  fetchVoiceNavVersionInfo,
  fetchVoiceNavChatHistory,
  fetchVoiceNavSessionContext,
  postVoiceNavWorkflowAction,
  isMedicalLinkHostname,
  isVoiceNavHostname,
  postVoiceNavLogout,
  postVoiceNavSessionActivePatient,
  postVoiceNavSessionPendingUiReplay,
  flushVoiceNavSessionChatHistory,
  replaceVoiceNavSessionChatHistory,
  voicnavSessionClientDebug,
  type VoiceNavSessionActivePatientPayload,
  type VoiceNavSessionContextResponse,
  type VoiceNavVersionInfo,
} from '../api/apiConfig'
import type { NormalizedUnreadMessage } from '../types/unreadMessages'
import {
  consumePendingModalityReplayAfterReload,
  stagePendingModalityReplayAfterReload,
} from '../utils/pendingModalityAfterReload'
import {
  chatHasPatientSwitchConfirmFollowUp,
  chatHasPatientSwitchYesReply,
  clearPatientSwitchUserCommitted,
  countPatientSwitchYesUserMessages,
  dedupePatientSwitchYesUserMessages,
  findPendingPatientSwitchConfirm,
  hasPatientSwitchUserAnswerAfter,
  hasPendingRemindConfirmation,
  isPatientSwitchUserCommitted,
  markAllPatientSwitchConfirmationsResolved,
  markPatientSwitchUserCommitted,
  parsePatientSwitchConfirmFromMetadata,
  parsePatientSwitchVoiceAnswer,
  enrichPatientSwitchConfirmOnMessage,
  inferPatientSwitchConfirmFromContent,
  isPatientSwitchConfirmationMessage,
  patientSwitchConfirmBubbleText,
  patientSwitchConfirmIsActionable,
  patientNamesLikelyEqual,
  looksLikePatientChartSlotLabel,
  userTextIsPatientSwitchConfirmFollowUp,
} from '../utils/patientSwitchConfirm'
import {
  isReadModalityConfirmationMessage,
  markReadModalityConfirmResolved,
  parseReadModalityConfirmFromMetadata,
  readModalityConfirmIsActionable,
  readModalityUiFrench,
  findPendingReadModalityConfirm,
  parseReadModalityConfirmAnswer,
  readModalityShouldBypassPatientSwitch,
  shouldUseHostedChatForReadModalityConfirm,
} from '../utils/readModalityConfirm'
import {
  isCheckExamReceiptConfirmationMessage,
  markCheckExamReceiptConfirmResolved,
  parseCheckExamReceiptConfirmFromMetadata,
  checkExamReceiptConfirmIsActionable,
  checkExamReceiptUiFrench,
  checkExamReceiptShouldBypassPatientSwitch,
  findPendingCheckExamReceiptConfirm,
  parseCheckExamReceiptOpenConfirmAnswer,
  shouldUseHostedChatForCheckExamReceiptConfirm,
} from '../utils/checkExamReceiptConfirm'
import {
  apiRowsHasDeferredOpenEcgOpenPatient,
  findLatestPrescriptionDocumentToAutoOpen,
  mergedChatCrossPatientOpenBeforeDeferredEcg,
  resolvePendingModalityExamRowsAfterDeferredReplay,
  shouldDeferDocumentAutoOpen,
  shouldReloadHostBeforeDeferredCrossPatientEcg,
  stripStalePrescriptionAutoOpenAfterCrossPatientSwitch,
  utteranceLooksLikeHostedModalityOpen,
} from '../utils/crossPatientEcgFlow'
import {
  findPendingUnreadReadAloudMessageId,
  parseUnreadReadAloudIntent,
  parseUnreadThreadOpenIndex,
} from '../utils/unreadReadAloudIntent'
import {
  isSecretaryPreviewActionable,
  userTextIsSecretaryInlineEditCommand,
  userTextIsSecretaryUrgencyCommand,
} from '../utils/secretaryPreviewFollowupText'
import {
  applyMailDocumentFieldUpdate,
  buildMailInlineEditCommand,
  isGenericMailDocumentPreviewMessage,
  isMailDocumentPreviewEditable,
  mailDocumentDraftForField,
  type MailDocumentInlineEditField,
} from '../utils/mailDocumentInlineEdit'
import type { PatientContext, WidgetConfig, WidgetTheme } from '../types/widget'
import { resolveVoiceNavApiBaseUrl, resolveVoiceNavWebSocketTranscribeUrl } from '../config/env'
import {
  getMedicalLinkCredentialsFromSession,
  logMedicalLinkCredentialSourceOnce,
  medicalLinkCredentialsToApiPayload,
} from '../config/medicalLinkSessionCredentials'
import { logAwsTranscribeMode, useVoiceStreaming } from '../hooks/useVoiceStreaming'
import { useWakePhraseRecognition } from '../hooks/useWakePhraseRecognition'
import { copilotInitialMessages, mockReplyFor } from '../mock/messages'
import {
  chatMessagesToWire,
  wireMessagesToChatMessages,
  wireRowsToLiveChatMessages,
} from '../utils/chatHistoryPersistence'
import { withoutPostRefreshUiDebugMessages } from '../utils/sessionPendingCommandsDebug'
import {
  appendPendingUiReplayDebugMessage,
  SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID,
  summarizePendingUiReplayResponse,
} from '../utils/pendingUiReplayOutcome'
import {
  modalitiesQueuedSwitchChartMatches,
  pendingSwitchRequestedNameFromMessages,
} from '../utils/modalitySwitchHostSyncRecovery'
import {
  getInitialChatPanelOpen,
  writeChatPanelEverOpened,
  writeChatPanelExplicitlyClosed,
} from '../utils/chatPanelPersistence'
import {
  applyDocumentLang,
  getChatUiStrings,
  getSupportedLanguage,
  isFrenchLanguage,
  resolveChatPanelTitle,
  resolveChatPlaceholder,
} from '../utils/chatUiLocale'
import { normalizeCommandTranscript } from '../api/normalizeCommand'
import { shouldNormalizeTypedCommand } from '../utils/typedCommandNormalize'
import {
  findPendingNoteConfirm,
  markNoteConfirmResolved,
  parseNoteConfirmAnswer,
  shouldUseHostedChatForNoteConfirm,
} from '../utils/noteTaskConfirm'
import { buildComposerVersionLabel } from '../utils/versionLabel'
import { ChatPanel } from '../components/ChatPanel'
import { LauncherButton } from '../components/LauncherButton'
import { Composer } from '../components/Composer'
import { useGttsSpeakAndTranscribe } from '../hooks/useGttsSpeakAndTranscribe'
import { MessageList, type SportsNocSummaryInlineEditProps } from '../components/MessageList'
import type { ReminderPreviewEditField } from '../components/ReminderPreviewInlineEdit'
import type { SecretaryMessageInlineEditField } from '../components/SecretaryMessageInlineEdit'
import {
  buildReminderInlineEditCommand,
  reminderInlineEditDraftForField,
  wireRemindPreviewPayload,
} from '../utils/reminderPreviewInlineEdit'
import { ReadAloudBar } from '../components/ReadAloudBar'
import { useReadAloudPolly } from '../hooks/useReadAloudPolly'
import { setWidgetPanelControls } from './widgetControls'
import {
  navigateOrClosePendingWindow,
  pickLatestIntentNavigation,
  navigateLatestPatientDossierInHost,
  pickFirstIntentUrl,
  tryNavigateMedilinkMenuSameTab,
} from '../utils/intentNavigation'
import {
  findPendingFolderOptionsMessage,
  findPendingPatientOptionsMessage,
  formatFolderSelectionMessage,
  formatPatientSelectionMessage,
  parseOrdinalChoice,
} from '../utils/patientOptionOrdinal'
import {
  folderOptionsDisplayContent,
  isFolderOptionsAssistantRow,
  resolveFolderOptionsFromAssistantRow,
} from '../utils/folderOptionsFromAssistant'
import { segmentsFromUnreadRows } from '../utils/extractInboxReadAloud'
import { createLlmTokenSessionLogger } from '../utils/sessionLlmTokenLog'
import { createHttpVoiceNavHostAdapter } from '../host/httpVoiceNavHostAdapter'
import { tryReplayDeferredPatientSwitchEcg } from '../domain/patientHost/deferredEcgReplayOrchestration'
import { resolvePatientSwitchConfirmation } from '../domain/patientHost/resolvePatientSwitchConfirmationCore'
import {
  appendChatUserMessages,
  applyInteractivePreviewSupersedeToEarlierPreviews,
  applyMailDocumentOpenAwaitingSupersedeToEarlierPreviews,
  mergePatientOptionsRefresh,
} from '../utils/patientAssistantMerge'
import { transcriptIsShowMorePatients } from '../utils/showMorePatients'
import { chatContentString, normalizeChatMessagesForDisplay } from '../utils/chatContent'
import type { SportsNocAwaitingField, SportsNocYesNoKind } from '../utils/sportsNocFlow'
import {
  markSportsNocYesNoResolvedInMessages,
  shouldUseHostedChatForSportsNoc,
  applySportsNocClarificationSupersedeToEarlierMessages,
  applySportsNocFlowCancelledToEarlierMessages,
  applySportsNocWorkflowSnapshotToMessages,
  findPendingSportsNocLocationOptions,
  buildSportsNocSportsSubmitText,
  normalizeSportsNocLocationOptionsFromApi,
  normalizeSportsNocSportOptionsFromApi,
  toggleSportsNocSportSelectedInMessages,
  sportsNocFieldsFromAssistantMetadata,
  sportsNocShouldBypassPatientSwitch,
  sportsNocMailConfirmLabels,
  normalizeSportsNocEditContextFromApi,
  normalizeSportsNocSummaryFromApi,
  normalizePatientContext,
  parseSportsNocReviewEditIntent,
  sportsNocReviewEditWorkflowAction,
  type SportsNocSummaryEditField,
} from '../utils/sportsNocFlow'
import { resolveSportsNocCertificatePreviewUrlFromMetadata, previewTokenFromSportsNocPreviewUrl } from '../utils/sportsNocPreviewUrl'
import {
  clientReminderRuntimePayload,
  shouldUseHostedChatForReminder,
} from '../utils/reminderWorkflow'
import {
  hasPendingNoteConfirmation,
  shouldUseHostedChatForNoteTask,
  wireNotePreviewPayload,
} from '../utils/noteTaskWorkflow'
import {
  applyClientSideGlobalIntentCancel,
  globalIntentCancelAriaLabel,
  globalIntentCancelCommandText,
  globalIntentCancelDisplayText,
  transcriptIsGlobalIntentCancel,
} from '../utils/globalIntentCancel'
import {
  messageLooksLikeRenewOrdonnancePreview,
  normalizeRenewOrdonnanceMeta,
  RENEW_ORDONNANCE_MAIL_AFTER_SUCCESS_MS,
  RENEW_ORDONNANCE_PREVIEW_SUCCESS_FALLBACK_MS,
  findRenewOrdonnanceStagedRevealTargets,
  renewOrdonnanceAssistantBubbleText,
  renewOrdonnanceFieldsFromAssistantMetadata,
  renewOrdonnanceMailMessageIds,
  renewOrdonnanceRenewingMessage,
  resolveRenewOrdonnancePreviewUrlFromMetadata,
  stripRenewOrdonnanceRenewingMessages,
  utteranceLooksLikeVidalRenewCommand,
} from '../utils/renewOrdonnanceFlow'
import { shouldUseHostedChatForCalendarTimeRetrieval } from '../utils/calendarTimeRetrievalWorkflow'
import { shouldUseHostedChatForMessage } from '../utils/messageDelegationWorkflow'
import {
  resolveSecretaryPreviewFrench,
  secretaryPreviewFrFromMessage,
} from '../utils/secretaryMessagePreviewLocale'
import {
  clearPendingMailSendConfirmInMessages,
  findPendingMailSendConfirm,
  hasPendingMailDocumentOpen,
  mailDocumentPreviewFrameKey,
  mailOpenThenMailFlowActive,
  mailPreviewAnchorsEqual,
  mailSendConfirmLabels,
  parseMailSendConfirmAnswer,
  pendingMailPreviewAnchor,
  shouldSkipDeferredDocumentAutoOpen,
  shouldUseHostedChatForMailConfirm,
  utteranceHasMailOrEmailKeyword,
} from '../utils/inlineMailConfirm'
import { isCloseModalityCommand } from '../utils/closeModalityCommand'

function inferSelectionLangFromText(text: string): 'en' | 'fr' {
  return /[àâäéèêëïîôùûçœ]|deuxième|première|troisième|choisir|sélectionner|premier\b/i.test(text)
    ? 'fr'
    : 'en'
}

function selectionLangForUiClick(): 'en' | 'fr' {
  return getSupportedLanguage()
}

function shouldSilenceSecretaryInlineEditTrigger(
  text: string,
  messages: readonly ChatMessage[],
): boolean {
  const trimmed = (text || '').trim()
  if (
    !trimmed ||
    (!userTextIsSecretaryInlineEditCommand(trimmed) && !userTextIsSecretaryUrgencyCommand(trimmed))
  ) {
    return false
  }
  return messages.some((m) => m.role === 'assistant' && isSecretaryPreviewActionable(m))
}

function sportsNocFollowupFrForLang(langHint?: 'en' | 'fr' | null): boolean {
  if (langHint === 'fr') return true
  if (langHint === 'en') return false
  return isFrenchLanguage()
}

const defaultTheme: WidgetTheme = {
  primary: '#0b5bd3',
  primaryText: '#ffffff',
  panelBg: '#ffffff',
  launcherBg: '#0f172a',
  text: '#0f172a',
  mutedText: '#475569',
  border: '#e2e8f0',
  shadow: 'rgba(15, 23, 42, 0.18)',
  textareaBg: '#f8fafc',
  textareaBorder: '#cbd5e1',
  buttonBg: '#0f172a',
  buttonText: '#ffffff',
  buttonDisabledBg: '#94a3b8',
  buttonDisabledText: '#f8fafc',
}

const uid = () => Math.random().toString(16).slice(2) + '-' + Date.now().toString(16)

/** Client-only row: drives ``MailDocumentSentSuccessCard`` (checkmark hero) after sports / renew mail POST. */
function localMailDocumentSentSuccessPayload(params: {
  documentLabel: string
  recipientName: string
}): MailDocumentPayload {
  const label = params.documentLabel.trim() || 'Document'
  const name = params.recipientName.trim() || 'Patient'
  return {
    currentIntent: 'mail_document',
    documentType: label,
    mailSubject: label,
    mailBody: `${label} enclosed.`,
    recipientType: 'patient',
    recipientName: name,
    awaitingMailConfirmation: false,
    mail_sent_status: 'success',
  }
}

type ApiAssistantRow = {
  id?: string
  role: string
  text: string
  metadata?: Record<string, unknown>
  ui_type?: string
  options?: PatientOption[]
}

/** Server PDF read-aloud: text for client Polly (preferred) or legacy base64 MP3 parts. */
function readAloudRequestFromAssistantRows(
  rows: readonly ApiAssistantRow[],
): { text: string; language: 'en' | 'fr' } | { parts: string[]; language: 'en' | 'fr' } | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const md = rows[i].metadata
    if (!md || typeof md !== 'object') continue
    const langRaw = String(
      md.read_aloud_language ?? md.readAloudLanguage ?? md.response_language ?? md.responseLanguage ?? 'en',
    )
      .trim()
      .toLowerCase()
    const language: 'en' | 'fr' =
      langRaw === 'fr' || langRaw === 'french' || langRaw.startsWith('fr-') ? 'fr' : 'en'
    const text = String(md.read_aloud_text ?? md.readAloudText ?? '').trim()
    if (text) return { text, language }
    const raw = md.read_aloud_parts ?? md.readAloudParts
    if (Array.isArray(raw) && raw.length > 0 && raw.every((p) => typeof p === 'string' && p.length > 0)) {
      return { parts: raw as string[], language }
    }
    break
  }
  return null
}

/** @deprecated Legacy MP3 parts only — prefer {@link readAloudRequestFromAssistantRows}. */
function readAloudPartsFromAssistantRows(rows: readonly ApiAssistantRow[]): string[] | null {
  const req = readAloudRequestFromAssistantRows(rows)
  return req && 'parts' in req ? req.parts : null
}

type MapAssistantOpts = {
  /** For building patient dossier URL when API omits `dossier_url` but sends `patient_id`. */
  loggedUserId?: string
  /** VoiceNav backend base URL for trusting backend-served preview proxy links. */
  apiBaseUrl?: string
}

/** Only allow https dossier links on the expected MediKaLLink host (assistant metadata is not trusted). */
function safeOpenPatientDossierUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const u = raw.trim()
  if (!u) return undefined
  try {
    const parsed = new URL(u)
    if (parsed.protocol !== 'https:') return undefined
    const h = parsed.hostname.toLowerCase()
    if (!isMedicalLinkHostname(h)) return undefined
    return u
  } catch {
    return undefined
  }
}

/**
 * In-app menu URLs: {medicalLink origin}/medecin/{uuid}/{menu}
 * UUID must allow hex in any case (APIs often emit uppercase).
 */
function safeNavigationPageUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const u = raw.trim()
  if (!u) return undefined
  try {
    const parsed = new URL(u)
    if (parsed.protocol !== 'https:') return undefined
    const h = parsed.hostname.toLowerCase()
    if (!isMedicalLinkHostname(h)) return undefined
    const path = parsed.pathname
    if (!/^\/medecin\/[0-9a-fA-F-]{36}\/[a-zA-Z0-9][a-zA-Z0-9_-]*\/?$/.test(path)) return undefined
    return u
  } catch {
    return undefined
  }
}

/** Same shape as backend `medilink_medecin_menu_url` when `navigation_url` is absent but `route` + user id are present. */
function buildNavigationPageUrlFromRoute(
  route: string | null | undefined,
  loggedUserId: string | undefined,
): string | undefined {
  const seg = typeof route === 'string' ? route.trim() : ''
  const uid = (loggedUserId ?? '').trim()
  if (!seg || !uid || seg.includes('/') || seg.includes('..')) return undefined
  if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(seg)) return undefined
  if (!/^[0-9a-fA-F-]{36}$/.test(uid)) return undefined
  const built = buildMedicalLinkNavigationUrl(uid, seg)
  return safeNavigationPageUrl(built)
}

/** Trusted preview links: S3, MediKaLLink, or the configured VoiceNav backend proxy. */
function safeModalityFileOpenUrl(raw: string | undefined, trustedApiBaseUrl?: string): string | undefined {
  if (!raw) return undefined
  const u = raw.trim()
  if (!u) return undefined
  try {
    const parsed = new URL(u)
    const h = parsed.hostname.toLowerCase()
    if (parsed.protocol === 'https:') {
      if (h.endsWith('.amazonaws.com')) return u
      if (isMedicalLinkHostname(h)) return u
      if (isVoiceNavHostname(h, trustedApiBaseUrl)) return u
      return undefined
    }
    // In dev / proxied deployments, the proxy URL can be served over HTTP even when the
    // configured API base is HTTPS (or vice-versa). For preview rendering, we trust only
    // the VoiceNav backend host for HTTP proxy URLs.
    if (parsed.protocol === 'http:' && trustedApiBaseUrl && isVoiceNavHostname(h, trustedApiBaseUrl)) return u
    return undefined
  } catch {
    return undefined
  }
}

function isWordDocumentUrl(u: string): boolean {
  const low = (u || '').toLowerCase()
  return /\.(doc|docx)(\?|#|$)/.test(low)
}

function isPdfDocumentUrl(u: string): boolean {
  return /\.(pdf)(\?|#|$)/i.test((u || '').trim())
}

function buildInlinePreviewUrl(u: string): string {
  if (!isWordDocumentUrl(u)) return u
  return buildOfficeEmbedViewerUrl(u)
}

function pickRenewOrdonnanceDocumentLink(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const root = raw as Record<string, unknown>
  const topLink = root.link
  if (typeof topLink === 'string' && topLink.trim()) return topLink.trim()
  const data = root.data
  if (!data || typeof data !== 'object') return undefined
  const payloadLink = (data as Record<string, unknown>).link
  return typeof payloadLink === 'string' && payloadLink.trim() ? payloadLink.trim() : undefined
}

/** Hint for multipart attachment when the server cannot infer from download ``Content-Type``. */
function guessMimeFromDocumentUrl(url: string, previewKind: 'exam' | 'prescription'): string {
  const u = (url || '').toLowerCase()
  if (previewKind === 'prescription') return 'application/pdf'
  if (u.includes('.pdf')) return 'application/pdf'
  if (/\.(jpe?g)(\?|#|$)/.test(u) || u.includes('.jpg')) return 'image/jpeg'
  if (u.includes('.png')) return 'image/png'
  if (u.includes('.gif')) return 'image/gif'
  if (u.includes('.webp')) return 'image/webp'
  return ''
}

/** True when any user message appears after this index (exclusive of assistant at index). */
function hasAnyUserMessageAfter(messages: readonly ChatMessage[], assistantIndex: number): boolean {
  for (let j = assistantIndex + 1; j < messages.length; j++) {
    if (messages[j]?.role === 'user') return true
  }
  return false
}

/** WebSocket JSON may deliver `metadata` as an object or a serialized JSON string. */
/** Last assistant row still waiting for yes/no confirmation. */
function findPendingBinaryConfirm(messages: ChatMessage[]): 'secretary' | 'mail' | 'general' | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role === 'user') continue
    if (m.role !== 'assistant') continue
    if (hasAnyUserMessageAfter(messages, i)) continue
    if (m.secretaryPreviewSuperseded) continue
    if (m.awaitingMessageEditField) continue
    if (m.secretaryMessage && isSecretaryPreviewActionable(m)) return 'secretary'
    // Mail Yes/No is shown on the in-chat preview card (see MessageList), not the composer bar.
    if (m.awaitingGeneralConfirmation === true) {
      if (isPatientSwitchConfirmationMessage(m)) {
        continue
      }
      if (isReadModalityConfirmationMessage(m)) {
        continue
      }
      if (isCheckExamReceiptConfirmationMessage(m)) {
        continue
      }
      return 'general'
    }
    continue
  }
  return null
}

function binaryConfirmButtonLabels(kind: 'secretary' | 'mail' | 'general'): { yes: string; no: string } {
  if (kind === 'general') {
    if (isFrenchLanguage()) {
      return { yes: 'Oui, continuer', no: 'Non, annuler' }
    }
    return { yes: 'Yes, proceed', no: 'No, cancel' }
  }
  if (kind === 'secretary') {
    if (isFrenchLanguage()) {
      return { yes: 'Oui, envoyer', no: 'Ne pas envoyer' }
    }
    return { yes: 'Yes, send it', no: 'No, do not send' }
  }
  if (isFrenchLanguage()) {
    return { yes: 'Oui, envoyer', no: 'Non, annuler' }
  }
  return { yes: 'Yes, send', no: 'No, cancel' }
}

/** Text sent to the command API (avoids bare ``yes`` being treated as small-talk on stateless backends). */
function binaryConfirmSubmittedText(kind: 'secretary' | 'mail' | 'general', choice: 'yes' | 'no'): string {
  const fr = isFrenchLanguage()
  if (kind === 'general') {
    if (choice === 'yes') return fr ? 'oui continuer' : 'yes proceed'
    return fr ? 'non annuler' : 'no cancel'
  }
  if (kind === 'mail') {
    if (choice === 'yes') return fr ? 'oui envoyer le courriel' : 'yes send the mail'
    return fr ? 'non annuler le courriel' : 'no cancel mail'
  }
  return choice === 'yes' ? 'yes' : 'no'
}

function parseAssistantMetadata(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      if (p && typeof p === 'object' && !Array.isArray(p)) return p as Record<string, unknown>
    } catch {
      return undefined
    }
    return undefined
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>
  return undefined
}

/**
 * Patient paging ("show more"): keep only patient-disambiguation assistant rows so stray
 * unread/navigation rows from a mis-routed turn are never merged into the picker UI.
 */
/** Merge deferred replay assistant rows and unblock ECG/prescription auto-open. */
function mergeMessagesAfterDeferredUiReplay(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  return resolvePendingModalityExamRowsAfterDeferredReplay(
    stripStalePrescriptionAutoOpenAfterCrossPatientSwitch(
      dedupePatientSwitchYesUserMessages(mergePatientOptionsRefresh(prev, incoming)),
    ),
  )
}

function finishDeferredReplayPipeline(): void {
  consumePendingModalityReplayAfterReload()
}

function withVoiceTranscribeProcessing(
  message: ChatMessage,
  transcribeProcessingMs?: number,
): ChatMessage {
  if (transcribeProcessingMs == null || !Number.isFinite(transcribeProcessingMs)) {
    return message
  }
  return { ...message, transcribeProcessingMs }
}

function stampVoiceTranscribeProcessing(
  messages: ChatMessage[],
  transcribeProcessingMs?: number,
): ChatMessage[] {
  if (transcribeProcessingMs == null || !Number.isFinite(transcribeProcessingMs)) {
    return messages
  }
  return messages.map((m) => ({ ...m, transcribeProcessingMs }))
}

function stampIntentProcessing(
  messages: ChatMessage[],
  intentProcessingMs?: number,
): ChatMessage[] {
  if (intentProcessingMs == null || !Number.isFinite(intentProcessingMs)) {
    return messages
  }
  return messages.map((m) => ({ ...m, intentProcessingMs }))
}

function stampVoiceAssistantProcessing(
  messages: ChatMessage[],
  opts?: { transcribeProcessingMs?: number; intentProcessingMs?: number },
): ChatMessage[] {
  if (opts?.intentProcessingMs != null) {
    return stampIntentProcessing(messages, opts.intentProcessingMs)
  }
  return stampVoiceTranscribeProcessing(messages, opts?.transcribeProcessingMs)
}

function withTypedNormalizeProcessing(
  message: ChatMessage,
  typedNormalizeProcessingMs?: number,
): ChatMessage {
  if (typedNormalizeProcessingMs == null || !Number.isFinite(typedNormalizeProcessingMs)) {
    return message
  }
  return { ...message, typedNormalizeProcessingMs }
}

async function resolveTypedCommandForHostedChat(
  raw: string,
  apiBaseUrl: string,
  opts?: { skipCommandNormalize?: boolean; normalize?: boolean },
): Promise<{
  commandText: string
  typedNormalizeProcessingMs?: number
  skipAsrNormalization: boolean
}> {
  const trimmed = raw.trim()
  const normalize =
    !opts?.skipCommandNormalize && (opts?.normalize ?? shouldNormalizeTypedCommand(trimmed))
  if (!normalize) {
    // When the caller (or short yes/no) opts out of typed normalize, also skip
    // server-side ASR/Nova rewrite so button payloads reach the flow intact.
    return {
      commandText: trimmed,
      skipAsrNormalization: Boolean(opts?.skipCommandNormalize) || !shouldNormalizeTypedCommand(trimmed),
    }
  }
  const normStart = performance.now()
  const norm = await normalizeCommandTranscript(apiBaseUrl, trimmed)
  const typedNormalizeProcessingMs = Math.max(0, Math.round(performance.now() - normStart))
  const commandText = (norm.llm_normalized || norm.rule_normalized || trimmed).trim()
  return {
    commandText,
    typedNormalizeProcessingMs,
    skipAsrNormalization: true,
  }
}

function withTypedCommandProcessing(
  message: ChatMessage,
  typedCommandProcessingMs?: number,
): ChatMessage {
  if (typedCommandProcessingMs == null || !Number.isFinite(typedCommandProcessingMs)) {
    return message
  }
  return { ...message, typedCommandProcessingMs }
}

function stampTypedCommandProcessingOnTurn(
  messages: ChatMessage[],
  assistantMsgs: ChatMessage[],
  typedCommandProcessingMs: number,
  userMessageId?: string,
): ChatMessage[] {
  if (!Number.isFinite(typedCommandProcessingMs) || typedCommandProcessingMs < 0) {
    return messages
  }
  const stampIds = new Set(assistantMsgs.map((m) => m.id))
  return messages.map((m) =>
    stampIds.has(m.id) ? { ...m, typedCommandProcessingMs } : m,
  )
}

function elapsedCommandProcessingMs(commandStartedAt?: number): number | undefined {
  if (commandStartedAt == null || !Number.isFinite(commandStartedAt)) return undefined
  return Math.max(0, Math.round(performance.now() - commandStartedAt))
}

function filterAssistantRowsForPatientShowMoreCommand(
  rows: ApiAssistantRow[],
  commandText: string,
  messageHistory: readonly ChatMessage[],
): ApiAssistantRow[] {
  if (!transcriptIsShowMorePatients(commandText)) return rows
  if (!findPendingPatientOptionsMessage([...messageHistory])?.options?.length) return rows
  const out: ApiAssistantRow[] = []
  for (const r of rows) {
    if (r.role !== 'assistant') continue
    const md = parseAssistantMetadata(r.metadata)
    const k = typeof md?.kind === 'string' ? md.kind : ''
    const patientLike =
      k === 'patient_disambiguation' ||
      k === 'patient_appointment_disambiguation' ||
      k === 'patient_disambiguation_notice' ||
      k === 'patient_option_error' ||
      (k === '' && r.ui_type === 'patient_options')
    if (patientLike) out.push(r)
  }
  return out
}

/**
 * Voice WebSocket runs `process_user_message` before `onFinal`. Ordinal selection may already
 * have opened the patient server-side (and cleared pending state). If we still POST `patient_selection`,
 * the server returns intent unknown — so prefer websocket rows when they match the chosen card.
 */
/**
 * Voice WebSocket already ran ``process_user_message`` for patient-switch yes/no.
 * Prefer those assistant rows instead of POSTing ``chat/command`` again (duplicate user bubbles).
 */
function websocketFulfilledPatientSwitchConfirm(
  rows: readonly ApiAssistantRow[],
  choice: 'yes' | 'no',
): boolean {
  if (!rows.length) return false
  if (choice === 'yes') {
    return rows.some((r) => {
      if (r.role !== 'assistant') return false
      const md = parseAssistantMetadata(r.metadata)
      const k = md?.kind
      return k === 'open_patient' || k === 'pending_modality_exam' || k === 'show_modality'
    })
  }
  const opened = rows.some((r) => {
    if (r.role !== 'assistant') return false
    const md = parseAssistantMetadata(r.metadata)
    return md?.kind === 'open_patient' || md?.kind === 'pending_modality_exam'
  })
  if (opened) return false
  return rows.some((r) => r.role === 'assistant' && String(r.text || '').trim().length > 0)
}

function websocketOpenedPatientMatchingSelection(
  rows: unknown[] | undefined,
  expectedSelectionId: string,
): boolean {
  const sid = expectedSelectionId.trim()
  if (!sid || !Array.isArray(rows)) return false
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue
    const row = r as { role?: string; metadata?: unknown }
    if (row.role !== 'assistant') continue
    const md = parseAssistantMetadata(row.metadata)
    if (md?.kind === 'open_patient') {
      const pid = md.patient_id != null ? String(md.patient_id).trim() : ''
      if (pid === sid) return true
    }
  }
  return false
}

function latestOpenPatientPayloadFromRows(rows: ApiAssistantRow[]): VoiceNavSessionActivePatientPayload | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i]
    if (r.role !== 'assistant') continue
    const md = parseAssistantMetadata(r.metadata)
    if (md?.kind !== 'open_patient') continue
    const patient_id = md.patient_id != null ? String(md.patient_id).trim() : ''
    const patient_name = md.patient != null ? String(md.patient).trim() : ''
    const dob = md.dob != null ? String(md.dob).trim() : ''
    const patient_unique_id = md.patient_unique_id != null ? String(md.patient_unique_id).trim() : ''
    const patient_fb_id = md.patient_fb_id != null ? String(md.patient_fb_id).trim() : ''
    if (!patient_id && !patient_name) continue
    return {
      ...(patient_id ? { patient_id } : {}),
      ...(patient_name ? { patient_name } : {}),
      ...(dob ? { dob } : {}),
      ...(patient_unique_id ? { patient_unique_id } : {}),
      ...(patient_fb_id ? { patient_fb_id } : {}),
    }
  }
  return null
}

function latestPendingModalityPatientPayloadFromAssistantRows(
  rows: ApiAssistantRow[],
): VoiceNavSessionActivePatientPayload | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const md = parseAssistantMetadata(rows[i].metadata)
    if (md?.kind !== 'pending_modality_exam') continue
    const patient_name =
      md.target_patient_name != null ? String(md.target_patient_name).trim() : ''
    const patient_id = md.target_patient_id != null ? String(md.target_patient_id).trim() : ''
    if (!patient_name && !patient_id) continue
    return {
      ...(patient_id ? { patient_id } : {}),
      ...(patient_name ? { patient_name } : {}),
    }
  }
  return null
}

function latestPrescriptionPatientPayloadFromRows(
  rows: ApiAssistantRow[],
): VoiceNavSessionActivePatientPayload | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const md = parseAssistantMetadata(rows[i].metadata)
    if (md?.kind !== 'prescription' || !md.prescription || typeof md.prescription !== 'object') {
      continue
    }
    const p = md.prescription as Record<string, unknown>
    const patient_id = p.resolved_patient_id != null ? String(p.resolved_patient_id).trim() : ''
    const patient_name =
      p.resolved_patient_name != null ? String(p.resolved_patient_name).trim() : ''
    if (!patient_id && !patient_name) continue
    return {
      ...(patient_id ? { patient_id } : {}),
      ...(patient_name ? { patient_name } : {}),
    }
  }
  return null
}

function latestBloodTestPatientPayloadFromRows(
  rows: ApiAssistantRow[],
): VoiceNavSessionActivePatientPayload | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const md = parseAssistantMetadata(rows[i].metadata)
    if (
      md?.kind !== 'blood_test_prescription' ||
      !md.blood_test_prescription ||
      typeof md.blood_test_prescription !== 'object'
    ) {
      continue
    }
    const p = md.blood_test_prescription as Record<string, unknown>
    const patient_id = p.resolved_patient_id != null ? String(p.resolved_patient_id).trim() : ''
    const patient_name =
      p.resolved_patient_name != null ? String(p.resolved_patient_name).trim() : ''
    if (!patient_id && !patient_name) continue
    return {
      ...(patient_id ? { patient_id } : {}),
      ...(patient_name ? { patient_name } : {}),
    }
  }
  return null
}

function apiRowsNeedPendingUiReplayAfterActivePatientPersist(rows: ApiAssistantRow[]): boolean {
  for (const r of rows) {
    if (r.role !== 'assistant') continue
    const md = parseAssistantMetadata(r.metadata)
    if (!md) continue
    if (
      md.kind === 'pending_modality_exam' &&
      (md.status === 'pending' || md.replayPending === true || md.replay_pending === true)
    ) {
      return true
    }
    if (
      md.kind === 'open_patient' &&
      (md.deferred_open_document === true ||
        md.deferred_open_document_pending === true ||
        md.deferred_open_ecg === true ||
        md.deferred_open_ecg_pending === true)
    ) {
      return true
    }
  }
  return false
}

/** Prefer registry display over host slot labels (``patient 2``) when merging API rows for session sync. */
function coalescePatientPayloadsForSessionSync(
  open: VoiceNavSessionActivePatientPayload | null,
  presc: VoiceNavSessionActivePatientPayload | null,
  blood: VoiceNavSessionActivePatientPayload | null,
  pending: VoiceNavSessionActivePatientPayload | null,
): VoiceNavSessionActivePatientPayload | null {
  const chain = [open, presc, blood, pending].filter(Boolean) as VoiceNavSessionActivePatientPayload[]
  if (chain.length === 0) return null
  let patient_id = ''
  let patient_unique_id = ''
  let patient_fb_id = ''
  let dob = ''
  for (const p of chain) {
    if (!patient_id && (p.patient_id || '').trim()) patient_id = (p.patient_id || '').trim()
    if (!patient_unique_id && (p.patient_unique_id || '').trim())
      patient_unique_id = (p.patient_unique_id || '').trim()
    if (!patient_fb_id && (p.patient_fb_id || '').trim()) patient_fb_id = (p.patient_fb_id || '').trim()
    if (!dob && (p.dob || '').trim()) dob = (p.dob || '').trim()
  }
  let patient_name = ''
  for (const p of [open, presc, pending]) {
    const n = (p?.patient_name || '').trim()
    if (n && !looksLikePatientChartSlotLabel(n)) {
      patient_name = n
      break
    }
  }
  if (!patient_name) {
    for (const p of [open, presc, pending]) {
      const n = (p?.patient_name || '').trim()
      if (n) {
        patient_name = n
        break
      }
    }
  }
  if (!patient_id && !patient_unique_id && !patient_fb_id && !patient_name) return null
  return {
    ...(patient_id ? { patient_id } : {}),
    ...(patient_unique_id ? { patient_unique_id } : {}),
    ...(patient_fb_id ? { patient_fb_id } : {}),
    ...(patient_name ? { patient_name } : {}),
    ...(dob ? { dob } : {}),
  }
}

function latestActivePatientSignalFromChatMessages(
  messages: readonly ChatMessage[],
): { name: string; blockedByNoActiveSignal: boolean } {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    const pm = m.pendingModalityExam
    const pendingName = (pm?.targetPatientName ?? '').trim()
    if (pendingName) return { name: pendingName, blockedByNoActiveSignal: false }
    const hint = (m.activePatientNameHint ?? '').trim()
    if (hint) return { name: hint, blockedByNoActiveSignal: false }
    if (m.noActivePatientContextHint) {
      return { name: '', blockedByNoActiveSignal: true }
    }
  }
  return { name: '', blockedByNoActiveSignal: false }
}

function mapAssistantApiRows(rows: ApiAssistantRow[], opts?: MapAssistantOpts): ChatMessage[] {
  const batchDeferredPatientOpen = rows.some((r) => {
    if (r.role !== 'assistant') return false
    const md = parseAssistantMetadata(r.metadata)
    return (
      md?.kind === 'open_patient' &&
      (md.deferred_open_document === true ||
        md.deferred_open_document_pending === true ||
        md.deferred_open_ecg === true ||
        md.deferred_open_ecg_pending === true)
    )
  })
  return rows
    .filter((r) => r.role === 'assistant')
    .map((r) => {
      const md = parseAssistantMetadata(r.metadata)
      const uiTypeFromRow =
        (typeof r.ui_type === 'string' && r.ui_type.trim()) ||
        (typeof md?.uiType === 'string' && md.uiType.trim()) ||
        (typeof md?.ui_type === 'string' && md.ui_type.trim()) ||
        undefined
      const optionsFromRow = Array.isArray(r.options)
        ? r.options
        : Array.isArray(md?.options)
          ? md.options
          : undefined
      const folderOptionsResolved = isFolderOptionsAssistantRow(
        md as Record<string, unknown> | null,
        uiTypeFromRow,
      )
        ? resolveFolderOptionsFromAssistantRow(
            md as Record<string, unknown> | null,
            optionsFromRow,
            uiTypeFromRow,
          )
        : null
      const folderOptionsUi = Boolean(folderOptionsResolved?.length)
      const selectionUiOk =
        folderOptionsUi ||
        ((uiTypeFromRow === 'patient_options' ||
          uiTypeFromRow === 'assignee_options' ||
          uiTypeFromRow === 'recipient_type_options' ||
          uiTypeFromRow === 'delivery_channel_options' ||
          uiTypeFromRow === 'prescription_type_options') &&
          Array.isArray(optionsFromRow))
      const options = folderOptionsUi
        ? folderOptionsResolved
        : selectionUiOk
          ? (optionsFromRow as PatientOption[]).filter(
              (o) => o && typeof o.selection_id === 'string' && o.selection_id.length > 0,
            )
          : undefined
      const hasCards = Boolean(options && options.length > 0)
      const locUiOk = uiTypeFromRow === 'sports_noc_location_options' && Array.isArray(optionsFromRow)
      const sportsNocLocationOptions = locUiOk
        ? normalizeSportsNocLocationOptionsFromApi(optionsFromRow)
        : undefined
      const hasLocationCards = Boolean(locUiOk && sportsNocLocationOptions && sportsNocLocationOptions.length > 0)
      const sportUiOk = uiTypeFromRow === 'sports_noc_sports_options' && Array.isArray(optionsFromRow)
      const commentsUiOk = uiTypeFromRow === 'sports_noc_comments_input'
      const sportsNocSportOptions = sportUiOk
        ? normalizeSportsNocSportOptionsFromApi(optionsFromRow)
        : undefined
      let modalityMeta: ChatMessage['modalityMeta']
      if (md?.kind === 'show_modality' && md.modality_found !== false) {
        const rawFile = md.file ?? md.file_path
        const dc = md.date_created
        const filePath = typeof rawFile === 'string' && rawFile.trim() ? rawFile.trim() : undefined
        const dateCreated = typeof dc === 'string' && dc.trim() ? dc.trim() : undefined
        const fn = md.folder_name
        const folderName =
          typeof fn === 'string' && fn.trim() ? fn.trim().toLowerCase() : undefined
        modalityMeta = { filePath, dateCreated, folderName, showExamDetails: false }
      }
      let openPatientDossierUrl: string | undefined =
        md?.kind === 'open_patient'
          ? safeOpenPatientDossierUrl(md.dossier_url)
          : undefined
      let activePatientNameHint =
        md?.kind === 'open_patient' && md.patient != null && String(md.patient).trim()
          ? String(md.patient).trim()
          : undefined
      let activePatientIdHint =
        md?.kind === 'open_patient' && md.patient_id != null && String(md.patient_id).trim()
          ? String(md.patient_id).trim()
          : undefined
      const noActivePatientContextHint = md?.kind === 'no_active_patient' ? true : undefined
      if (!openPatientDossierUrl && md?.kind === 'open_patient') {
        const pid =
          (md.patient_id != null ? String(md.patient_id).trim() : '') ||
          (md.patient_unique_id != null ? String(md.patient_unique_id).trim() : '')
        const uid = (opts?.loggedUserId || '').trim()
        if (pid && uid) {
          const built = buildMedicalLinkPatientDossierUrl(uid, pid)
          openPatientDossierUrl = safeOpenPatientDossierUrl(built)
        }
      }
      if (!activePatientIdHint && md?.kind === 'open_patient') {
        const uidHint =
          md.patient_unique_id != null ? String(md.patient_unique_id).trim() : ''
        if (uidHint) activePatientIdHint = uidHint
      }
      const navPayload =
        md?.kind === 'navigate_page' && md.navigation && typeof md.navigation === 'object'
          ? (md.navigation as PageNavigationExtraction)
          : undefined
      const navNeedsClarification = Boolean(navPayload?.needs_clarification)
      const rawNavUrl =
        md?.navigation_url != null && String(md.navigation_url).trim()
          ? md.navigation_url
          : (md as { navigationUrl?: unknown }).navigationUrl
      let openNavigationUrl: string | undefined =
        md?.kind === 'navigate_page' && !navNeedsClarification && rawNavUrl != null && String(rawNavUrl).trim()
          ? safeNavigationPageUrl(rawNavUrl)
          : undefined
      if (!openNavigationUrl && md?.kind === 'navigate_page' && !navNeedsClarification && navPayload) {
        openNavigationUrl = buildNavigationPageUrlFromRoute(navPayload.route, opts?.loggedUserId)
      }
      let autoOpenModalityFile = Boolean(
        safeModalityFileOpenUrl(modalityMeta?.filePath, opts?.apiBaseUrl),
      )
      let prescriptionMeta: PrescriptionExtraction | undefined
      if (md?.kind === 'prescription' && md.prescription && typeof md.prescription === 'object') {
        prescriptionMeta = md.prescription as PrescriptionExtraction
        const pn = (prescriptionMeta.resolved_patient_name || '').trim()
        const pid = (prescriptionMeta.resolved_patient_id || '').trim()
        if (pn) activePatientNameHint = pn
        if (pid) activePatientIdHint = pid
      }
      let bloodTestPrescriptionMeta: BloodTestPrescriptionExtraction | undefined
      if (
        md?.kind === 'blood_test_prescription' &&
        md.blood_test_prescription &&
        typeof md.blood_test_prescription === 'object'
      ) {
        bloodTestPrescriptionMeta = md.blood_test_prescription as BloodTestPrescriptionExtraction
        const pn = String(bloodTestPrescriptionMeta.resolved_patient_name ?? '').trim()
        const pid = String(bloodTestPrescriptionMeta.resolved_patient_id ?? '').trim()
        if (pn) activePatientNameHint = pn
        if (pid) activePatientIdHint = pid
      }
      let renewOrdonnanceMeta: RenewOrdonnanceExtraction | undefined
      if (md?.kind === 'renew_ordonnance' && md.renew_ordonnance && typeof md.renew_ordonnance === 'object') {
        renewOrdonnanceMeta = normalizeRenewOrdonnanceMeta(
          md.renew_ordonnance as Record<string, unknown>,
        )
      }
      let autoOpenPrescriptionDocument: boolean | undefined
      let prioritizeDocumentAutoOpen: boolean | undefined
      if (
        md?.kind === 'show_modality' &&
        (md.prioritize_document_auto_open === true ||
          md.prioritizeDocumentAutoOpen === true ||
          md.check_exam_receipt_opened === true ||
          md.checkExamReceiptOpened === true)
      ) {
        prioritizeDocumentAutoOpen = true
        if (modalityMeta?.filePath) {
          autoOpenModalityFile = true
        }
      }
      let sportsNocCertificatePreview: boolean | undefined
      let renewOrdonnanceDocumentPreview: boolean | undefined
      let prescriptionDocumentPreviewUrl: string | undefined
      const bloodDocUrl =
        bloodTestPrescriptionMeta && !bloodTestPrescriptionMeta.needs_clarification
          ? String(bloodTestPrescriptionMeta.ordonnance_file_url ?? '').trim()
          : ''
      const rxDocUrl =
        prescriptionMeta?.intent_action === 'view_prescription' &&
        !prescriptionMeta.needs_clarification
          ? String(prescriptionMeta.ordonnance_file_url ?? '').trim()
          : ''
      const docUrl = bloodDocUrl || rxDocUrl
      if (docUrl && !batchDeferredPatientOpen) {
        const pu = safeModalityFileOpenUrl(docUrl, opts?.apiBaseUrl)
        if (pu) {
          autoOpenPrescriptionDocument = true
          prescriptionDocumentPreviewUrl = pu
          if (bloodDocUrl) {
            prioritizeDocumentAutoOpen = true
          }
        }
      }
      let navigationMeta: PageNavigationExtraction | undefined
      if (navPayload && navPayload.domain === 'navigation' && navPayload.intent_action === 'page_navigation') {
        navigationMeta = navPayload
      }
      const closeModalityPreview = md?.kind === 'close_modality_preview' ? true : undefined
      const closeMessageThread = md?.kind === 'close_message_thread' ? true : undefined
      let mailFrench: boolean | undefined
      const mailResponseLang = String(md?.response_language ?? md?.responseLanguage ?? '')
        .trim()
        .toLowerCase()
      if (mailResponseLang === 'fr' || mailResponseLang === 'french') mailFrench = true
      else if (mailResponseLang === 'en' || mailResponseLang === 'english') mailFrench = false

      let mailDocument: MailDocumentPayload | undefined
      if (md?.kind === 'mail_document' && md.mail_document && typeof md.mail_document === 'object') {
        const mdw = md.mail_document as Record<string, unknown>
        const docType =
          mdw.documentType != null && String(mdw.documentType).trim() !== ''
            ? String(mdw.documentType)
            : mdw.activeDocumentName != null
              ? String(mdw.activeDocumentName)
              : ''
        const mailBodyRaw =
          mdw.mailBody != null && String(mdw.mailBody).trim() !== '' ? String(mdw.mailBody).trim() : ''
        if (mailFrench == null && /\bci-?joint/i.test(mailBodyRaw)) mailFrench = true
        const frMail = mailFrench === true
        mailDocument = {
          currentIntent: 'mail_document',
          documentType: docType,
          mailSubject:
            mdw.mailSubject != null && String(mdw.mailSubject).trim() !== ''
              ? String(mdw.mailSubject)
              : docType,
          mailBody:
            mailBodyRaw ||
            (docType ? (frMail ? `${docType} ci-joint.` : `${docType} enclosed.`) : ''),
          recipientType: mdw.recipientType != null ? String(mdw.recipientType) : '',
          recipientName: mdw.recipientName != null ? String(mdw.recipientName) : '',
          awaitingMailConfirmation: mdw.awaitingMailConfirmation === true,
          awaitingDocumentOpen: mdw.awaitingDocumentOpen === true,
          mail_sent_status: mdw.mail_sent_status != null ? String(mdw.mail_sent_status) : null,
        }
      }
      const renewUi = renewOrdonnanceFieldsFromAssistantMetadata(md)
      let renewOrdonnanceRenewing = renewUi.renewOrdonnanceRenewing
      let renewOrdonnanceRenewalCompleted = renewUi.renewOrdonnanceRenewalCompleted
      let renewOrdonnanceFrench = renewUi.renewOrdonnanceFrench
      let renewOrdonnanceYesNoPrompt = renewUi.renewOrdonnanceYesNoPrompt
      if (renewUi.mailDocument && !mailDocument) {
        mailDocument = renewUi.mailDocument
      }
      const resolvedRenewPreviewUrl = resolveRenewOrdonnancePreviewUrlFromMetadata(md, opts?.apiBaseUrl)
      if (resolvedRenewPreviewUrl) {
        const pu = safeModalityFileOpenUrl(resolvedRenewPreviewUrl, opts?.apiBaseUrl)
        if (pu) {
          renewOrdonnanceDocumentPreview = true
          modalityMeta = { filePath: pu, showExamDetails: false }
          autoOpenModalityFile = true
          autoOpenPrescriptionDocument = true
          prescriptionDocumentPreviewUrl = pu
          prioritizeDocumentAutoOpen = true
        }
      } else if (renewUi.autoOpenPrescriptionDocument && renewUi.prescriptionDocumentPreviewUrl) {
        const pu = safeModalityFileOpenUrl(renewUi.prescriptionDocumentPreviewUrl, opts?.apiBaseUrl)
        if (pu) {
          autoOpenPrescriptionDocument = true
          prescriptionDocumentPreviewUrl = pu
        }
      }
      let unreadMessagesUi: ChatMessage['unreadMessagesUi']
      if (md?.kind === 'unread_messages' && md.unread_messages && typeof md.unread_messages === 'object') {
        const um = md.unread_messages as Record<string, unknown>
        const vr = um.variant === 'urgent' ? 'urgent' : 'unread'
        const urRaw = um.urgent
        const urgent: 0 | 1 =
          urRaw === 1 || urRaw === '1' ? 1 : vr === 'urgent' ? 1 : 0
        const umLang = String(um.language ?? md.response_language ?? md.responseLanguage ?? '')
        const listFr =
          umLang.toLowerCase() === 'french' ||
          umLang.toLowerCase() === 'fr' ||
          umLang.toLowerCase().startsWith('fr-')
        unreadMessagesUi = { variant: vr, urgent, language: listFr ? 'fr' : 'en' }
      }

      let getLastMessageUi: ChatMessage['getLastMessageUi']
      if (md?.kind === 'get_last_message' && md.get_last_message && typeof md.get_last_message === 'object') {
        const glm = md.get_last_message as Record<string, unknown>
        const scope = glm.scope === 'inbox_global' ? 'inbox_global' : 'patient'
        const glmLang = String(glm.language ?? md.response_language ?? md.responseLanguage ?? '')
        const glmFr =
          glmLang.toLowerCase() === 'french' ||
          glmLang.toLowerCase() === 'fr' ||
          glmLang.toLowerCase().startsWith('fr-')
        getLastMessageUi = {
          scope,
          fbUid: String(glm.fb_uid ?? glm.fbUid ?? '').trim() || undefined,
          patientName: String(glm.patient_name ?? glm.patientName ?? '').trim() || undefined,
          language: glmFr ? 'fr' : 'en',
        }
      }

      let awaitingMessageEditField: ChatMessage['awaitingMessageEditField']
      const editFieldRaw = md?.awaiting_message_edit_field ?? md?.awaitingMessageEditField
      if (
        typeof editFieldRaw === 'string' &&
        (editFieldRaw === 'purpose' ||
          editFieldRaw === 'message' ||
          editFieldRaw === 'recipient_name' ||
          editFieldRaw === 'recipient_type')
      ) {
        awaitingMessageEditField = editFieldRaw
      }

      let secretaryFrench: boolean | undefined
      const secResponseLang = String(md?.response_language ?? md?.responseLanguage ?? '')
        .trim()
        .toLowerCase()
      if (secResponseLang === 'fr' || secResponseLang === 'french') secretaryFrench = true
      else if (secResponseLang === 'en' || secResponseLang === 'english') secretaryFrench = false

      let secretaryMessage: SecretaryMessagePayload | undefined
      let reminderPreview:
        | { purpose: string; day: string; time: string; language?: string }
        | undefined
      let notePreview: ChatMessage['notePreview']
      let patientOptionsPaging: ChatMessage['patientOptionsPaging']
      let patientOptionsRefresh: boolean | undefined
      if (
        hasCards &&
        md &&
        typeof md.patient_options_total === 'number' &&
        Number.isFinite(md.patient_options_total) &&
        typeof md.patient_options_visible_count === 'number' &&
        Number.isFinite(md.patient_options_visible_count) &&
        typeof md.patient_options_has_more === 'boolean'
      ) {
        patientOptionsPaging = {
          total: md.patient_options_total,
          visibleCount: md.patient_options_visible_count,
          hasMore: md.patient_options_has_more,
        }
      }
      if (md?.patient_options_refresh === true) {
        patientOptionsRefresh = true
      }

      const sportsNocFlowCancelled =
        md?.sports_noc_flow_cancelled === true || md?.sportsNocFlowCancelled === true ? true : undefined
      const calendarTimeNeedsDateClarification =
        md?.kind === 'calendar_time_retrieval' && md?.needs_date_clarification === true ? true : undefined
      const cancelNotice =
        sportsNocFlowCancelled ||
        md?.kind === 'intent_cancel_empty' ||
        md?.kind === 'intent_cancelled' ||
        md?.kind === 'intent_task_cancelled' ||
        md?.message_send_cancelled === true
          ? true
          : undefined
      let sportsNocSummary: ChatMessage['sportsNocSummary']
      let sportsNocYesNoPrompt: ChatMessage['sportsNocYesNoPrompt']
      let sportsNocYesNoResolved: boolean | undefined
      let sportsNocFrench: boolean | undefined
      let sportsNocAwaitingField: SportsNocAwaitingField | undefined
      let sportsNocNeedsClarification: boolean | undefined
      let sportsNocPreviewUpdateInPlace: boolean | undefined
      let sportsNocSummaryUpdateInPlace: boolean | undefined
      let sportsNocGenerateConfirm: boolean | undefined
      let sportsNocCertificateGenerated: boolean | undefined
      let sportsNocLocationPickerStyle: ChatMessage['sportsNocLocationPickerStyle']
      let sportsNocCurrentLocationId: number | undefined
      let sportsNocCommentsDraft: string | undefined
      let sportsNocEditUiOnly: boolean | undefined
      const sportsFields = sportsNocFieldsFromAssistantMetadata(md)
      if (sportsFields.sportsNocFrench === true) sportsNocFrench = true
      else if (sportsFields.sportsNocFrench === false) sportsNocFrench = false
      if (sportsFields.sportsNocAwaitingField) sportsNocAwaitingField = sportsFields.sportsNocAwaitingField
      if (sportsFields.sportsNocNeedsClarification) sportsNocNeedsClarification = true
      if (sportsFields.sportsNocPreviewUpdateInPlace) sportsNocPreviewUpdateInPlace = true
      if (sportsFields.sportsNocSummaryUpdateInPlace) sportsNocSummaryUpdateInPlace = true
      if (sportsFields.sportsNocSummary) sportsNocSummary = sportsFields.sportsNocSummary
      if (sportsFields.sportsNocYesNoPrompt) sportsNocYesNoPrompt = sportsFields.sportsNocYesNoPrompt
      if (sportsFields.sportsNocCertificateGenerated) sportsNocCertificateGenerated = true
      if (sportsFields.sportsNocLocationPickerStyle) {
        sportsNocLocationPickerStyle = sportsFields.sportsNocLocationPickerStyle
      }
      if (sportsFields.sportsNocCurrentLocationId) {
        sportsNocCurrentLocationId = sportsFields.sportsNocCurrentLocationId
      }
      if (sportsFields.sportsNocCommentsDraft != null) {
        sportsNocCommentsDraft = sportsFields.sportsNocCommentsDraft
      }
      if (sportsFields.sportsNocEditUiOnly) sportsNocEditUiOnly = true
      let resolvedSportsNocLocationOptions = sportsNocLocationOptions
      if (
        !resolvedSportsNocLocationOptions?.length &&
        sportsFields.sportsNocLocationOptions?.length
      ) {
        resolvedSportsNocLocationOptions = sportsFields.sportsNocLocationOptions
      }
      let resolvedSportsNocSportOptions = sportsNocSportOptions
      if (
        !resolvedSportsNocSportOptions?.length &&
        sportsFields.sportsNocSportOptions?.length
      ) {
        resolvedSportsNocSportOptions = sportsFields.sportsNocSportOptions
      }
      const showSportsNocSportPicker = Boolean(
        resolvedSportsNocSportOptions?.length &&
          (sportUiOk ||
            uiTypeFromRow === 'sports_noc_sports_options' ||
            sportsFields.sportsNocAwaitingField === 'sports' ||
            sportsNocAwaitingField === 'sports'),
      )
      if (
        sportsFields.sportsNocMailPreview &&
        sportsFields.sportsNocYesNoPrompt?.kind === 'mail_certificate' &&
        !mailDocument
      ) {
        const mp = sportsFields.sportsNocMailPreview
        mailDocument = {
          currentIntent: 'mail_document',
          documentType: mp.documentType,
          mailSubject: mp.mailSubject,
          mailBody: mp.mailBody,
          recipientType: mp.recipientType,
          recipientName: mp.recipientName,
          awaitingMailConfirmation: true,
        }
      }
      if (sportsNocAwaitingField === 'generate_confirm' && sportsNocSummary) {
        sportsNocGenerateConfirm = true
        sportsNocNeedsClarification = false
      }
      if (md?.kind === 'sports_noc') {
        const sn =
          md.sports_noc && typeof md.sports_noc === 'object'
            ? (md.sports_noc as Record<string, unknown>)
            : {}
        const pn = String(sn.resolved_patient_name ?? '').trim()
        const pid = String(sn.resolved_patient_id ?? '').trim()
        if (pn) activePatientNameHint = pn
        if (pid) activePatientIdHint = pid
        if (
          sportsNocSummary &&
          !(sportsNocSummary.patient.id || '').trim() &&
          (pid || activePatientIdHint)
        ) {
          sportsNocSummary = {
            ...sportsNocSummary,
            patient: {
              id: pid || activePatientIdHint || '',
              name: pn || activePatientNameHint || sportsNocSummary.patient.name || '',
            },
          }
        }
      }

      const resolvedPreviewUrl = resolveSportsNocCertificatePreviewUrlFromMetadata(md, opts?.apiBaseUrl)
      if (resolvedPreviewUrl) {
        const pu = safeModalityFileOpenUrl(resolvedPreviewUrl, opts?.apiBaseUrl)
        if (pu) {
          sportsNocCertificatePreview = true
          // Same open path as ECG (show_modality): direct HTTPS PDF URL in chat JSON → iframe.
          modalityMeta = { filePath: pu, showExamDetails: false }
          autoOpenModalityFile = true
        } else {
          // eslint-disable-next-line no-console
          console.warn('[VoiceNav] sports_noc preview URL present but not auto-openable', {
            resolvedPreviewUrl,
            apiBaseUrl: opts?.apiBaseUrl,
          })
        }
      } else if (sportsNocCertificateGenerated && md) {
        const snDbg =
          md.sports_noc && typeof md.sports_noc === 'object'
            ? (md.sports_noc as Record<string, unknown>)
            : {}
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports_noc certificate generated but preview URL missing in metadata', {
          kind: md.kind,
          hasSportsNoc: Boolean(md.sports_noc),
          hasPreviewUrl: Boolean(String(snDbg.certificate_preview_url ?? '').trim()),
          hasPreviewToken: Boolean(String(snDbg.certificate_preview_token ?? '').trim()),
        })
      }

      if (md?.kind === 'secretary_message' && md.secretary_message && typeof md.secretary_message === 'object') {
        const sm = md.secretary_message as Record<string, unknown>
        const intent = sm.intent
        const whomRaw = sm.whom
        const whomIsLegacy = whomRaw === 'secretary' || whomRaw === 'patient'
        const whomIsStructured =
          whomRaw !== null &&
          typeof whomRaw === 'object' &&
          !Array.isArray(whomRaw) &&
          ('name' in (whomRaw as object) || 'type' in (whomRaw as object) || 'fb_uid' in (whomRaw as object))
        if (
          (intent === 'message' ||
            intent === 'message_patient' ||
            intent === 'ask_secretary_book_for_patient' ||
            intent === 'ask_secretary_task' ||
            intent === 'ask_patient_contact_secretary') &&
          (whomIsLegacy || whomIsStructured)
        ) {
          const conf = sm.confidence
          let whom: SecretaryMessagePayload['whom']
          if (whomIsLegacy) {
            whom = whomRaw as 'secretary' | 'patient'
          } else {
            const w = whomRaw as Record<string, unknown>
            whom = {
              name: w.name != null ? String(w.name) : '',
              type: (w.type != null ? String(w.type) : '') as MessageWhomPayload['type'],
              fb_uid: w.fb_uid != null && String(w.fb_uid).trim() !== '' ? String(w.fb_uid) : null,
            }
          }

          secretaryMessage = {
            intent,
            whom,
            what: sm.what != null ? String(sm.what) : null,
            patient_name: sm.patient_name != null ? String(sm.patient_name) : null,
            patient_id: sm.patient_id != null ? String(sm.patient_id) : null,
            secretary_name: sm.secretary_name != null ? String(sm.secretary_name) : null,
            secretary_email: sm.secretary_email != null ? String(sm.secretary_email) : null,
            purpose: sm.purpose != null ? String(sm.purpose) : null,
            message: sm.message != null ? String(sm.message) : null,
            urgent: typeof sm.urgent === 'boolean' ? sm.urgent : null,
            subject: sm.subject != null ? String(sm.subject) : null,
            object: sm.object != null ? String(sm.object) : null,
            message_sent_status:
              sm.message_sent_status != null ? String(sm.message_sent_status) : null,
            discussion_id: sm.discussion_id != null ? String(sm.discussion_id) : null,
            discussion_initiate_error:
              sm.discussion_initiate_error != null ? String(sm.discussion_initiate_error) : null,
            autofill_note: sm.autofill_note != null ? String(sm.autofill_note) : null,
            legacy_intent: sm.legacy_intent != null ? String(sm.legacy_intent) : null,
            mentioned_contact_role:
              sm.mentioned_contact_role === 'secretary' || sm.mentioned_contact_role === 'doctor'
                ? sm.mentioned_contact_role
                : null,
            mentioned_contact_name:
              sm.mentioned_contact_name != null && String(sm.mentioned_contact_name).trim() !== ''
                ? String(sm.mentioned_contact_name)
                : null,
            confidence: typeof conf === 'number' && Number.isFinite(conf) ? conf : 0,
          }
          const resolvedPreviewFr = resolveSecretaryPreviewFrench(secretaryMessage, {
            backendFrench: secretaryFrench,
            responseLang: secResponseLang || undefined,
          })
          if (resolvedPreviewFr != null) secretaryFrench = resolvedPreviewFr
        }
      }
      if (md?.kind === 'remind_me' && md.remind_preview && typeof md.remind_preview === 'object') {
        reminderPreview = wireRemindPreviewPayload(md.remind_preview as Record<string, unknown>)
      }
      if (
        (md?.kind === 'create_note_task' || md?.note_preview) &&
        md.note_preview &&
        typeof md.note_preview === 'object'
      ) {
        notePreview = wireNotePreviewPayload(md.note_preview as Record<string, unknown>)
      }
      let awaitingRemindEditField: ChatMessage['awaitingRemindEditField']
      const remindEditFieldRaw = md?.awaiting_remind_edit_field ?? md?.awaitingRemindEditField
      if (
        remindEditFieldRaw === 'purpose' ||
        remindEditFieldRaw === 'day' ||
        remindEditFieldRaw === 'time'
      ) {
        awaitingRemindEditField = remindEditFieldRaw
      }
      const reminderConfirmed =
        md.reminder_confirmed === true || md.reminderConfirmed === true || md.reminder_api_success === true
      const reminderCancelled =
        md.remind_confirmation === 'cancelled' ||
        md.reminder_cancelled === true ||
        md.reminderCancelled === true
      const noteConfirmed =
        md.note_confirmed === true || md.noteConfirmed === true || md.note_api_success === true
      const noteCancelled =
        md.note_confirmation === 'cancelled' ||
        md.note_cancelled === true ||
        md.noteCancelled === true
      const reminderPreviewUpdateInPlace =
        md.reminder_preview_update_in_place === true || md.reminderPreviewUpdateInPlace === true
      const secretaryPreviewUpdateInPlace =
        md.secretary_preview_update_in_place === true || md.secretaryPreviewUpdateInPlace === true
      let pendingModalityExam: ChatMessage['pendingModalityExam']
      if (md?.kind === 'pending_modality_exam') {
        const status = md.status === 'done' ? 'done' : 'pending'
        pendingModalityExam = {
          modality: md.modality != null ? String(md.modality) : '',
          recency: md.recency != null ? String(md.recency) : 'latest',
          language: md.language != null ? String(md.language) : undefined,
          targetPatientName:
            md.target_patient_name != null ? String(md.target_patient_name) : undefined,
          targetPatientId: md.target_patient_id != null ? String(md.target_patient_id) : undefined,
          targetPatientUniqueId:
            md.target_patient_unique_id != null ? String(md.target_patient_unique_id) : undefined,
          status,
        }
      }
      let patientSwitchConfirm = parsePatientSwitchConfirmFromMetadata(md)
      if (!patientSwitchConfirm) {
        patientSwitchConfirm = inferPatientSwitchConfirmFromContent(r.text)
      }
      const generalConfirmationContextRaw =
        md.general_confirmation_context != null
          ? String(md.general_confirmation_context)
          : md.generalConfirmationContext != null
            ? String(md.generalConfirmationContext)
            : patientSwitchConfirm?.context
      const readModalityYesNoPrompt = parseReadModalityConfirmFromMetadata(
        md as Record<string, unknown>,
      )
      const checkExamReceiptYesNoPrompt = parseCheckExamReceiptConfirmFromMetadata(
        md as Record<string, unknown>,
      )
      const readModalityYesNoResolved =
        md.read_modality_confirmed === true ||
        md.read_modality_cancelled === true ||
        md.readModalityConfirmed === true ||
        md.readModalityCancelled === true ||
        md.read_document_confirmed === true ||
        md.read_document_cancelled === true ||
        md.readDocumentConfirmed === true ||
        md.readDocumentCancelled === true
      const rowIdRaw = (r as { id?: unknown }).id
      const stableId =
        typeof rowIdRaw === 'string' && rowIdRaw.trim() ? rowIdRaw.trim() : uid()
      const folderCardUi = Boolean(folderOptionsUi && options?.length)
      const uiLangFrHint = /[àâäéèêëïîôùûçœ]|radiographie|ordonnance/i.test(
        chatContentString(r.text),
      )
      let assistantContent = folderCardUi
        ? folderOptionsDisplayContent(
            chatContentString(r.text),
            options!.length,
            uiLangFrHint,
          )
        : chatContentString(r.text)
      if (renewOrdonnanceMeta) {
        const clarifyDraft: ChatMessage = {
          id: stableId,
          role: 'assistant',
          content: assistantContent,
          timestamp: Date.now(),
          renewOrdonnanceMeta,
          renewOrdonnanceFrench,
        }
        const clarifyText = renewOrdonnanceAssistantBubbleText(clarifyDraft)
        if (clarifyText) assistantContent = clarifyText
      }
      return {
        id: stableId,
        role: 'assistant' as const,
        content: assistantContent,
        timestamp: Date.now(),
        uiType: hasCards
          ? folderCardUi
            ? 'folder_options'
            : uiTypeFromRow === 'assignee_options'
            ? 'assignee_options'
            : uiTypeFromRow === 'recipient_type_options'
              ? 'recipient_type_options'
              : uiTypeFromRow === 'delivery_channel_options'
                ? 'delivery_channel_options'
              : uiTypeFromRow === 'prescription_type_options'
                ? 'prescription_type_options'
              : 'patient_options'
          : commentsUiOk
            ? 'sports_noc_comments_input'
          : resolvedSportsNocLocationOptions?.length
            ? 'sports_noc_location_options'
            : resolvedSportsNocSportOptions?.length
              ? 'sports_noc_sports_options'
              : undefined,
        options: hasCards ? options : undefined,
        assigneeOptionsResolved:
          uiTypeFromRow === 'assignee_options' && typeof md?.assigneeOptionsResolved === 'boolean'
            ? md.assigneeOptionsResolved
            : undefined,
        recipientTypeOptionsResolved:
          uiTypeFromRow === 'recipient_type_options' &&
          typeof md?.recipientTypeOptionsResolved === 'boolean'
            ? md.recipientTypeOptionsResolved
            : undefined,
        deliveryChannelOptionsResolved:
          uiTypeFromRow === 'delivery_channel_options' &&
          typeof md?.deliveryChannelOptionsResolved === 'boolean'
            ? md.deliveryChannelOptionsResolved
            : undefined,
        attachmentDeliveryPending:
          uiTypeFromRow === 'delivery_channel_options' &&
          md?.attachment_delivery_pending &&
          typeof md.attachment_delivery_pending === 'object'
            ? (md.attachment_delivery_pending as Record<string, unknown>)
            : undefined,
        sportsNocLocationOptions: resolvedSportsNocLocationOptions,
        sportsNocLocationPickerStyle,
        sportsNocCurrentLocationId,
        sportsNocCommentsDraft,
        sportsNocEditUiOnly,
        sportsNocSportOptions: resolvedSportsNocSportOptions,
        sportsNocSportSelectedIds: showSportsNocSportPicker
          ? sportsFields.sportsNocSportSelectedIds ?? []
          : undefined,
        modalityMeta,
        openPatientDossierUrl,
        activePatientNameHint,
        activePatientIdHint,
        noActivePatientContextHint,
        pendingModalityExam,
        openNavigationUrl,
        autoOpenModalityFile,
        autoOpenPrescriptionDocument,
        prioritizeDocumentAutoOpen,
        prescriptionDocumentPreviewUrl,
        sportsNocCertificatePreview,
        renewOrdonnanceDocumentPreview,
        prescriptionMeta,
        bloodTestPrescriptionMeta,
        renewOrdonnanceMeta,
        renewOrdonnanceRenewing,
        renewOrdonnanceRenewalCompleted,
        renewOrdonnanceFrench,
        renewOrdonnanceYesNoPrompt,
        navigationMeta,
        closeModalityPreview,
        closeMessageThread,
        secretaryMessage,
        secretaryFrench,
        reminderPreview,
        notePreview,
        mailDocument,
        mailFrench,
        unreadMessagesUi,
        getLastMessageUi,
        patientOptionsPaging,
        patientOptionsRefresh,
        sportsNocFlowCancelled,
        calendarTimeNeedsDateClarification,
        cancelNotice,
        sportsNocFrench,
        sportsNocAwaitingField,
        sportsNocNeedsClarification,
        sportsNocPreviewUpdateInPlace,
        sportsNocSummaryUpdateInPlace,
        sportsNocSummary,
        sportsNocYesNoPrompt,
        sportsNocYesNoResolved,
        sportsNocGenerateConfirm,
        sportsNocCertificateGenerated,
        awaitingMessageEditField,
        awaitingRecipientNameCorrection:
          md.awaiting_recipient_name_correction === true ||
          md.awaitingRecipientNameCorrection === true ||
          md.needs_recipient_clarification === true,
        awaitingMessageConfirmation: awaitingMessageEditField
          ? false
          : md.awaiting_message_confirmation === true ||
            md.awaitingMessageConfirmation === true ||
            secretaryMessage?.autofill_note === 'awaiting_send_confirmation',
        awaitingMailConfirmation:
          md.awaiting_mail_confirmation === true || mailDocument?.awaitingMailConfirmation === true,
        awaitingGeneralConfirmation:
          md.awaiting_general_confirmation === true ||
          md.awaitingGeneralConfirmation === true ||
          Boolean(patientSwitchConfirm),
        generalConfirmationContext: generalConfirmationContextRaw,
        patientSwitchConfirm,
        readModalityYesNoPrompt,
        readModalityYesNoResolved: readModalityYesNoResolved || undefined,
        checkExamReceiptYesNoPrompt,
        checkExamReceiptYesNoResolved:
          md.check_exam_receipt_confirmed === true ||
          md.check_exam_receipt_cancelled === true ||
          md.checkExamReceiptConfirmed === true ||
          md.checkExamReceiptCancelled === true ||
          md.checkExamReceiptYesNoResolved === true ||
          undefined,
        awaitingRemindConfirmation:
          md.awaiting_remind_confirmation === true || md.awaitingRemindConfirmation === true,
        awaitingRemindEditField,
        remindFlowActive: md.kind === 'remind_me' && !reminderConfirmed && !reminderCancelled,
        awaitingNoteConfirmation:
          md.awaiting_note_confirmation === true || md.awaitingNoteConfirmation === true,
        noteAwaitingContentClarification:
          md.awaiting_note_content_clarification === true ||
          md.awaitingNoteContentClarification === true ||
          md.note_clarification === 'missing_content',
        noteFlowActive:
          md.kind === 'create_note_task' && !noteConfirmed && !noteCancelled,
        reminderPreviewUpdateInPlace,
        secretaryPreviewUpdateInPlace,
        reminderConfirmed,
        reminderCancelled,
        noteConfirmed,
        noteCancelled,
      }
    })
}

function secretaryInlineEditDraftForField(
  sm: SecretaryMessagePayload,
  field: SecretaryMessageInlineEditField,
): string {
  const whom = sm.whom
  const recipientName =
    typeof whom === 'object' && whom !== null
      ? (whom.name || '').trim() || (sm.secretary_name || '').trim()
      : (sm.secretary_name || '').trim()
  if (field === 'purpose') return (sm.subject || sm.purpose || sm.what || '').trim()
  if (field === 'message') return (sm.message || sm.object || '').trim()
  return recipientName
}

function buildSecretaryInlineEditCommand(
  field: SecretaryMessageInlineEditField,
  value: string,
  fr: boolean,
): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (field === 'purpose') return fr ? `modifier objet pour ${trimmed}` : `edit subject to ${trimmed}`
  if (field === 'message') return fr ? `modifier message pour ${trimmed}` : `edit message to ${trimmed}`
  return fr
    ? `modifier nom destinataire pour ${trimmed}`
    : `edit recipient name to ${trimmed}`
}

function hostedRowsHaveAssigneeOptions(rows: readonly ApiAssistantRow[]): boolean {
  return rows.some((r) => {
    if (r.role !== 'assistant') return false
    if ((r.ui_type || '').trim() === 'assignee_options') return true
    const md = parseAssistantMetadata(r.metadata)
    return (typeof md?.uiType === 'string' && md.uiType === 'assignee_options') || md?.ui_type === 'assignee_options'
  })
}

function assigneeOptionsFromApiRows(
  rows: readonly ApiAssistantRow[],
  loggedUserId?: string,
): PatientOption[] {
  const mapped = mapAssistantApiRows([...rows], { loggedUserId })
  return mapped.find((m) => m.uiType === 'assignee_options' && m.options?.length)?.options ?? []
}

export function VoiceNavWidget({ config }: { config: WidgetConfig }) {
  const [open, setOpen] = useState(() => getInitialChatPanelOpen(config))
  const chatLang = useMemo(() => getSupportedLanguage(), [])
  const chatUi = useMemo(() => getChatUiStrings(chatLang), [chatLang])
  const panelTitle = useMemo(
    () => resolveChatPanelTitle(config.title, chatUi.header),
    [config.title, chatUi.header],
  )
  const composerPlaceholder = resolveChatPlaceholder(config.placeholder, chatUi.placeholder)

  useLayoutEffect(() => {
    applyDocumentLang(chatLang)
  }, [chatLang])
  /** In-widget preview for exam file or prescription PDF (HTTPS), docked next to the chat panel. */
  const [modalityPreview, setModalityPreview] = useState<{
    url: string
    frameKey: string
    previewKind: 'exam' | 'prescription'
    /** Raw exam date string from assistant metadata (server normalizes). */
    examDateRaw?: string
    /** Dossier folder slug (ecg, echo, …) for previous-relative-to-open commands. */
    folderName?: string
    /** Human-readable preview header (no URL); from assistant row for exams. */
    previewTitle?: string
    /** After sports NOC PDF iframe loads, notify backend (mail step). */
    sportsNocNotifyPreviewOpened?: boolean
    /** Preview token the backend must verify was streamed before mail step. */
    sportsNocPreviewToken?: string
    /** After renewed ordonnance PDF iframe loads, unlock mail confirmation row(s). */
    renewOrdonnanceNotifyPreviewOpened?: boolean
    renewOrdonnanceUnlockMessageIds?: string[]
  } | null>(null)
  const [modalityPreviewMaximized, setModalityPreviewMaximized] = useState(false)
  const closePreviewHandledIdsRef = useRef<Set<string>>(new Set())
  const closeMessageThreadHandledIdsRef = useRef<Set<string>>(new Set())
  /** Message/frame keys the user closed — do not auto-reopen from deferred preview logic. */
  const userDismissedDocumentPreviewMessageIdsRef = useRef<Set<string>>(new Set())

  const openPanel = useCallback(() => {
    writeChatPanelExplicitlyClosed(config, false)
    writeChatPanelEverOpened(config)
    setOpen(true)
  }, [config])

  const closePanel = useCallback(() => {
    writeChatPanelExplicitlyClosed(config, true)
    setOpen(false)
  }, [config])
  const togglePanel = useCallback(() => {
    setOpen((v) => {
      const next = !v
      if (next) {
        writeChatPanelExplicitlyClosed(config, false)
        writeChatPanelEverOpened(config)
      } else {
        writeChatPanelExplicitlyClosed(config, true)
      }
      return next
    })
  }, [config])
  const setModalityPreviewSafe = useCallback(
    (next: {
      url: string
      frameKey: string
      previewKind: 'exam' | 'prescription'
      examDateRaw?: string
      folderName?: string
      previewTitle?: string
      sportsNocNotifyPreviewOpened?: boolean
      sportsNocPreviewToken?: string
      renewOrdonnanceNotifyPreviewOpened?: boolean
      renewOrdonnanceUnlockMessageIds?: string[]
    } | null) => {
      setModalityPreview((prev) => {
        if (
          next &&
          prev &&
          prev.url === next.url &&
          prev.frameKey === next.frameKey &&
          prev.previewKind === next.previewKind
        ) {
          return prev
        }
        if (prev?.url?.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(prev.url)
          } catch {
            // ignore
          }
        }
        return next
      })
    },
    [],
  )
  const rememberDismissedDocumentPreview = useCallback(() => {
    const p = modalityPreview
    if (p?.frameKey) {
      userDismissedDocumentPreviewMessageIdsRef.current.add(p.frameKey)
    }
    mailOpenThenMailDocumentOpenedRef.current = false
  }, [modalityPreview])

  const closeModalityPreview = useCallback(() => {
    rememberDismissedDocumentPreview()
    setModalityPreviewMaximized(false)
    setModalityPreviewSafe(null)
  }, [rememberDismissedDocumentPreview, setModalityPreviewSafe])

  useEffect(() => {
    setWidgetPanelControls({ open: openPanel, close: closePanel, toggle: togglePanel })
    return () => setWidgetPanelControls(null)
  }, [openPanel, closePanel, togglePanel])
  const patientContext: PatientContext | Record<string, unknown> | undefined = config.patientContext
  const patientContextForHostAdapterRef = useRef(patientContext)
  patientContextForHostAdapterRef.current = patientContext

  const [sportsNocReplyLang, setSportsNocReplyLang] = useState<'en' | 'fr' | null>(null)
  const sportsNocFollowupFr = useCallback(
    () => sportsNocFollowupFrForLang(sportsNocReplyLang),
    [sportsNocReplyLang],
  )
  /** Per-widget-mount id for backend transcript audit correlation (not persisted). */
  const auditSessionId = useMemo(() => uid(), [])

  const conversationScopeKey = useMemo(
    () => `${config.clientId ?? ''}:${config.tenantId ?? ''}:${config.apiBaseUrl ?? ''}`,
    [config.clientId, config.tenantId, config.apiBaseUrl],
  )
  /** While > 0, skip server chat-history hydrate so it cannot overwrite an in-flight assistant reply. */
  const chatCommandInFlightRef = useRef(0)
  /** Block debounced client chat-history REPLACE right after /chat/command (server already committed). */
  const suppressDebouncedChatPersistUntilRef = useRef(0)
  /** Last /chat/command assistant rows (used by follow-up UI helpers such as live recipient lookup). */
  const lastHostedAssistantRowsRef = useRef<ApiAssistantRow[]>([])

  const [messages, setMessages] = useState<ChatMessage[]>(() => copilotInitialMessages())
  const [sessionChatHydrated, setSessionChatHydrated] = useState(false)
  /** Restored from GET /session/context (copilot Redis active chart). */
  const [sessionActivePatient, setSessionActivePatient] =
    useState<VoiceNavSessionActivePatientPayload | null>(null)
  const restoringChatFromServerRef = useRef(false)
  const messagesRef = useRef<ChatMessage[]>(messages)
  /** Voice turn: user bubble shown on ``transcript_ready`` before Nova intent completes. */
  const voiceTurnUserInChatRef = useRef(false)
  const voiceTurnDisplayTextRef = useRef('')
  const [voiceProcessingUserMessageId, setVoiceProcessingUserMessageId] = useState<string | null>(null)
  const [voiceIntentProcessing, setVoiceIntentProcessing] = useState(false)
  /** Keep in sync every render so voice onFinal always sees latest history (incl. patient cards). */
  messagesRef.current = messages

  const resolvedApiBaseUrl = useMemo(
    () => resolveVoiceNavApiBaseUrl(config.apiBaseUrl),
    [config.apiBaseUrl],
  )

  useEffect(() => {
    logMedicalLinkCredentialSourceOnce()
  }, [])

  const getMedicalLinkCredentialsForApi = useCallback(() => {
    const s = getMedicalLinkCredentialsFromSession()
    return {
      piBearerToken: (config.piBearerToken ?? '').trim() || s.piBearerToken,
      loggedUserId: (config.loggedUserId ?? '').trim() || s.loggedUserId,
      loggedUserType: s.loggedUserType,
      loggedUserName: s.loggedUserName,
      medCenterId: (config.medCenterId ?? '').trim() || s.medCenterId,
      senderFbUid: (config.senderFbUid ?? '').trim() || s.senderFbUid,
    }
  }, [config.piBearerToken, config.loggedUserId, config.medCenterId, config.senderFbUid])

  const readAloud = useReadAloudPolly(resolvedApiBaseUrl)
  const sendPatientSelectionRef = useRef<(selectionId: string) => Promise<void>>(async () => {})
  const fulfillPatientSwitchConfirmRef =
    useRef<(messageId: string, choice: 'yes' | 'no') => Promise<void>>(async () => {})
  const fulfillGlobalIntentCancelRef = useRef<
    (opts?: { displayText?: string }) => Promise<void>
  >(async () => {})
  const patientSwitchConfirmInFlightRef = useRef(false)
  const lastHandledPatientSwitchConfirmIdRef = useRef<string | null>(null)
  const pendingUiReplayInFlightRef = useRef(false)
  const pendingUiReplayDoneRef = useRef(false)
  const reloadModalityReplayStagedRef = useRef(false)
  const llmTokenSessionLogRef = useRef(createLlmTokenSessionLogger())
  const openedModalityFileMessageIdsRef = useRef<Set<string>>(new Set())
  const openedPrescriptionFileMessageIdsRef = useRef<Set<string>>(new Set())
  /** Open-then-mail: document iframe opened once for this cycle; blocks duplicate opens from the messages effect. */
  const mailOpenThenMailDocumentOpenedRef = useRef(false)
  /** When set, identical mail-preview anchor updates skip React ``setMessages`` (avoids remount/refresh). */
  const mailPreviewAnchorRef = useRef<{ messageId: string; fingerprint: string } | null>(null)
  /** Full GET chat-history hydrate runs once per ``conversationScopeKey`` (avoids mid-flow transcript replace). */
  const chatHydrateCompletedForScopeRef = useRef<string | null>(null)
  const runDeferredPendingUiPipelineAfterHydrateRef = useRef<
    (restored: ChatMessage[], ctx: VoiceNavSessionContextResponse | null) => Promise<void>
  >(async () => {})

  const commitChatMessages = useCallback((next: ChatMessage[]) => {
    const anchor = pendingMailPreviewAnchor(next)
    if (mailPreviewAnchorsEqual(mailPreviewAnchorRef.current, anchor)) {
      messagesRef.current = next
      return
    }
    messagesRef.current = next
    setMessages(next)
    mailPreviewAnchorRef.current = anchor
  }, [])
  const openedSportsNocPreviewPostedIdsRef = useRef<Set<string>>(new Set())
  const openedRenewOrdonnanceMessageIdsRef = useRef<Set<string>>(new Set())
  const postedCreatedDiscussionMessageIdsRef = useRef<Set<string>>(new Set())
  const lastOpenedChatThreadRef = useRef<{ discussionId: string; messageText: string } | null>(null)
  const renewOrdonnanceMailConfirmUnlockedIdsRef = useRef<Set<string>>(new Set())
  const renewOrdonnanceSuccessUnlockedIdsRef = useRef<Set<string>>(new Set())
  const renewStagedRevealDoneRef = useRef<Set<string>>(new Set())
  const renewStagedRevealTimerRef = useRef<number | null>(null)
  const renewMailAfterSuccessTimerRef = useRef<number | null>(null)
  const applyHostedChatAssistantRowsRef = useRef<
    | ((
        rows: ApiAssistantRow[],
        historyForMock: ChatMessage[],
        opts?: {
          skipIntentNavigation?: boolean
          llmTokenUsage?: unknown
          turnKind?: 'typed' | 'voice'
          transcribeProcessingMs?: number
        },
      ) => Promise<boolean>)
    | null
  >(null)
  /** Tab opened on user gesture (Send, patient pick); navigated when assistant URLs arrive. Voice uses fallback open when no tab was pre-opened. */
  const intentNavWindowRef = useRef<Window | null>(null)
  const completeIntentNavigationRef = useRef<(msgs: ChatMessage[]) => void>(() => {})

  const prevConversationScopeRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevConversationScopeRef.current === null) {
      prevConversationScopeRef.current = conversationScopeKey
      return
    }
    if (prevConversationScopeRef.current === conversationScopeKey) return
    prevConversationScopeRef.current = conversationScopeKey
    lastHandledPatientSwitchConfirmIdRef.current = null
    setSessionChatHydrated(false)
    setMessages(copilotInitialMessages())
    openedModalityFileMessageIdsRef.current.clear()
    postedCreatedDiscussionMessageIdsRef.current.clear()
    closeMessageThreadHandledIdsRef.current.clear()
    openedPrescriptionFileMessageIdsRef.current.clear()
    mailOpenThenMailDocumentOpenedRef.current = false
    mailPreviewAnchorRef.current = null
    chatHydrateCompletedForScopeRef.current = null
    lastOpenedChatThreadRef.current = null
    setModalityPreviewSafe(null)
    setModalityPreviewMaximized(false)
    readAloud.stop()
  }, [conversationScopeKey, readAloud.stop, setModalityPreviewSafe])

  const appendAssistantLine = useCallback((content: string) => {
    setMessages((prev) => [
      ...prev,
      { id: uid(), role: 'assistant' as const, content, timestamp: Date.now() },
    ])
  }, [])

  const unreadRowsByMessageIdRef = useRef<Map<string, NormalizedUnreadMessage[]>>(new Map())

  const unreadListLanguageIsFrench = useCallback((messageId: string): boolean => {
    const host = messagesRef.current.find((m) => m.id === messageId)
    return host?.unreadMessagesUi?.language === 'fr' || host?.getLastMessageUi?.language === 'fr'
  }, [])

  const handleUnreadRowsLoaded = useCallback((messageId: string, rows: NormalizedUnreadMessage[]) => {
    unreadRowsByMessageIdRef.current.set(messageId, rows.slice())
  }, [])

  const handleUnreadReadAloudRequest = useCallback(
    (messageId: string, rows: NormalizedUnreadMessage[]) => {
      const listFr = unreadListLanguageIsFrench(messageId)
      void readAloud.synthesizeAndPlay(segmentsFromUnreadRows(rows, listFr ? 'fr' : 'en'))
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, unreadMessagesReadAloudPlayed: true } : m,
        ),
      )
    },
    [readAloud.synthesizeAndPlay, unreadListLanguageIsFrench],
  )

  const handleUnreadReadAloudDecline = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, unreadMessagesReadAloudDismissed: true } : m)),
    )
  }, [])

  const emitOpenChatCreatedWithId = useCallback(
    (discussionId: string, messageText: string, meta?: { messageId?: string; source?: string }) => {
      const trimmedDiscussionId = discussionId.trim()
      if (!trimmedDiscussionId) return
      const payload = {
        type: 'OPEN_CHAT_CREATED_WITH_ID',
        data: {
          discussionId: trimmedDiscussionId,
          messageText: messageText.trim(),
        },
      } as const
      const embeddedInParent = typeof window !== 'undefined' && window.parent && window.parent !== window
      try {
        console.debug('[VoiceNav] OPEN_CHAT_CREATED_WITH_ID emit', {
          source: meta?.source ?? 'unknown',
          messageId: meta?.messageId,
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
          embeddedInParent,
        })
        window.postMessage(payload, '*')
        console.debug('[VoiceNav] OPEN_CHAT_CREATED_WITH_ID posted to current window', {
          source: meta?.source ?? 'unknown',
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
        })
        if (embeddedInParent) {
          window.parent.postMessage(payload, '*')
          console.debug('[VoiceNav] OPEN_CHAT_CREATED_WITH_ID posted to parent window', {
            source: meta?.source ?? 'unknown',
            discussionId: payload.data.discussionId,
            messageText: payload.data.messageText,
          })
        } else {
          console.warn(
            '[VoiceNav] No parent window detected for OPEN_CHAT_CREATED_WITH_ID; host must listen on the same window',
            {
              source: meta?.source ?? 'unknown',
              discussionId: payload.data.discussionId,
              messageText: payload.data.messageText,
            },
          )
        }
        lastOpenedChatThreadRef.current = {
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
        }
      } catch (error) {
        console.error('[VoiceNav] Failed to emit OPEN_CHAT_CREATED_WITH_ID', {
          source: meta?.source ?? 'unknown',
          messageId: meta?.messageId,
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
          error,
        })
      }
    },
    [],
  )

  const emitCloseChatThread = useCallback(
    (meta?: { messageId?: string; source?: string }) => {
      const current = lastOpenedChatThreadRef.current
      const payload = {
        type: 'CLOSE_CHAT_THREAD',
        data: {
          discussionId: current?.discussionId ?? '',
          messageText: current?.messageText ?? '',
        },
      } as const
      const embeddedInParent = typeof window !== 'undefined' && window.parent && window.parent !== window
      try {
        console.debug('[VoiceNav] CLOSE_CHAT_THREAD emit', {
          source: meta?.source ?? 'unknown',
          messageId: meta?.messageId,
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
          embeddedInParent,
        })
        window.postMessage(payload, '*')
        console.debug('[VoiceNav] CLOSE_CHAT_THREAD posted to current window', {
          source: meta?.source ?? 'unknown',
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
        })
        if (embeddedInParent) {
          window.parent.postMessage(payload, '*')
          console.debug('[VoiceNav] CLOSE_CHAT_THREAD posted to parent window', {
            source: meta?.source ?? 'unknown',
            discussionId: payload.data.discussionId,
            messageText: payload.data.messageText,
          })
        } else {
          console.warn('[VoiceNav] No parent window detected for CLOSE_CHAT_THREAD; host must listen on the same window', {
            source: meta?.source ?? 'unknown',
            discussionId: payload.data.discussionId,
            messageText: payload.data.messageText,
          })
        }
        lastOpenedChatThreadRef.current = null
      } catch (error) {
        console.error('[VoiceNav] Failed to emit CLOSE_CHAT_THREAD', {
          source: meta?.source ?? 'unknown',
          messageId: meta?.messageId,
          discussionId: payload.data.discussionId,
          messageText: payload.data.messageText,
          error,
        })
      }
    },
    [],
  )

  const openUnreadThreadRow = useCallback(
    (row: NormalizedUnreadMessage, index: number, source: string): boolean => {
      const discussionId = row.threadContext.discussionId?.trim() || ''
      if (!discussionId) {
        console.warn('[VoiceNav] Cannot open unread thread without discussionId', {
          source,
          rowId: row.id,
          index,
        })
        appendAssistantLine(
          sportsNocFollowupFr()
            ? 'Je ne peux pas ouvrir ce message car son identifiant de discussion est manquant.'
            : 'I cannot open that message because its discussion id is missing.',
        )
        return false
      }
      emitOpenChatCreatedWithId(discussionId, row.lastMessageText || row.preview || row.content || '', {
        source,
        messageId: row.id,
      })
      return true
    },
    [appendAssistantLine, emitOpenChatCreatedWithId, sportsNocFollowupFr],
  )

  const tryOpenUnreadSelection = useCallback(
    (messageId: string, index: number, source: string): boolean => {
      const rows = unreadRowsByMessageIdRef.current.get(messageId)
      const frUi = sportsNocFollowupFr()
      if (rows === undefined) {
        appendAssistantLine(
          frUi
            ? 'La liste se charge encore — réessayez dans un instant.'
            : 'That list is still loading — please try again in a moment.',
        )
        return false
      }
      if (rows.length === 0) {
        appendAssistantLine(
          frUi
            ? 'Il n’y a aucun message à ouvrir dans cette liste.'
            : 'There are no messages in that list to open.',
        )
        return false
      }
      if (index < 0 || index >= rows.length) {
        appendAssistantLine(
          frUi
            ? 'Je n’ai pas trouvé ce message dans la liste. Choisissez une carte affichée.'
            : 'I could not find that message in the list. Please choose one of the visible cards.',
        )
        return false
      }
      return openUnreadThreadRow(rows[index], index, source)
    },
    [appendAssistantLine, openUnreadThreadRow, sportsNocFollowupFr],
  )

  const handleUnreadMessageOpen = useCallback(
    (_messageId: string, row: NormalizedUnreadMessage, index: number) => {
      void openUnreadThreadRow(row, index, 'unread-card')
    },
    [openUnreadThreadRow],
  )

  useEffect(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role !== 'assistant') continue
      if (m.sportsNocFrench === true) {
        setSportsNocReplyLang('fr')
        return
      }
      if (m.sportsNocFrench === false) {
        setSportsNocReplyLang('en')
        return
      }
    }
  }, [messages])

  const clearRenewStagedRevealTimers = useCallback(() => {
    if (renewStagedRevealTimerRef.current != null) {
      window.clearTimeout(renewStagedRevealTimerRef.current)
      renewStagedRevealTimerRef.current = null
    }
    if (renewMailAfterSuccessTimerRef.current != null) {
      window.clearTimeout(renewMailAfterSuccessTimerRef.current)
      renewMailAfterSuccessTimerRef.current = null
    }
  }, [])

  const unlockRenewOrdonnanceMailConfirm = useCallback((mailMessageIds: readonly string[]) => {
    let changed = false
    for (const mid of mailMessageIds) {
      if (!renewOrdonnanceMailConfirmUnlockedIdsRef.current.has(mid)) {
        renewOrdonnanceMailConfirmUnlockedIdsRef.current.add(mid)
        changed = true
      }
    }
    if (changed) {
      setRenewOrdonnanceMailConfirmUnlockedIds(new Set(renewOrdonnanceMailConfirmUnlockedIdsRef.current))
    }
  }, [])

  const unlockRenewOrdonnanceSuccess = useCallback((successMessageId: string) => {
    const sid = (successMessageId || '').trim()
    if (!sid) return
    if (renewOrdonnanceSuccessUnlockedIdsRef.current.has(sid)) return
    renewOrdonnanceSuccessUnlockedIdsRef.current.add(sid)
    setRenewOrdonnanceSuccessUnlockedIds(new Set(renewOrdonnanceSuccessUnlockedIdsRef.current))
  }, [])

  const runRenewOrdonnanceStagedReveal = useCallback(
    (successMessageId: string, mailMessageIds: readonly string[]) => {
      const sid = (successMessageId || '').trim()
      if (!sid || renewStagedRevealDoneRef.current.has(sid)) return
      renewStagedRevealDoneRef.current.add(sid)
      clearRenewStagedRevealTimers()
      unlockRenewOrdonnanceSuccess(sid)
      renewMailAfterSuccessTimerRef.current = window.setTimeout(() => {
        renewMailAfterSuccessTimerRef.current = null
        unlockRenewOrdonnanceMailConfirm(mailMessageIds)
      }, RENEW_ORDONNANCE_MAIL_AFTER_SUCCESS_MS)
    },
    [clearRenewStagedRevealTimers, unlockRenewOrdonnanceMailConfirm, unlockRenewOrdonnanceSuccess],
  )

  const beginRenewOrdonnanceStagedReveal = useCallback(
    (successMessageId: string, mailMessageIds: readonly string[]) => {
      const sid = (successMessageId || '').trim()
      if (!sid || renewStagedRevealDoneRef.current.has(sid)) return
      clearRenewStagedRevealTimers()
      renewStagedRevealTimerRef.current = window.setTimeout(() => {
        renewStagedRevealTimerRef.current = null
        runRenewOrdonnanceStagedReveal(sid, mailMessageIds)
      }, RENEW_ORDONNANCE_PREVIEW_SUCCESS_FALLBACK_MS)
    },
    [clearRenewStagedRevealTimers, runRenewOrdonnanceStagedReveal],
  )

  const openLatestModalityPreviewIfReady = useCallback(
    (msgs: readonly ChatMessage[]) => {
      const deferOtherDocs = shouldDeferDocumentAutoOpen(msgs)
      if (deferOtherDocs) {
        const hasPriorityPdf = [...msgs].reverse().some(
          (m) =>
            m.role === 'assistant' &&
            !m.historySnapshot &&
            (m.sportsNocCertificatePreview === true ||
              m.renewOrdonnanceDocumentPreview === true ||
              m.prioritizeDocumentAutoOpen === true) &&
            m.autoOpenModalityFile &&
            (m.modalityMeta?.filePath || '').trim(),
        )
        if (!hasPriorityPdf) return
      }
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i]
        if (m.historySnapshot || m.role !== 'assistant' || !m.autoOpenModalityFile) continue
        if (
          deferOtherDocs &&
          m.sportsNocCertificatePreview !== true &&
          m.renewOrdonnanceDocumentPreview !== true &&
          m.prioritizeDocumentAutoOpen !== true
        ) {
          continue
        }
        const url = safeModalityFileOpenUrl(m.modalityMeta?.filePath, resolvedApiBaseUrl)
        if (!url) continue
        if (userDismissedDocumentPreviewMessageIdsRef.current.has(m.id)) continue
        if (openedModalityFileMessageIdsRef.current.has(m.id)) continue
        openedModalityFileMessageIdsRef.current.add(m.id)
        const isSportsCert = m.sportsNocCertificatePreview === true
        const isRenewOrdonnance = m.renewOrdonnanceDocumentPreview === true
        const fr = m.sportsNocFrench === true || m.renewOrdonnanceFrench === true
        setModalityPreviewMaximized(false)
        if (isSportsCert) {
          // eslint-disable-next-line no-console
          console.info('[VoiceNav] sports_noc opening PDF via file_path (ECG path)', {
            messageId: m.id,
            url,
          })
        }
        const renewUnlockIds = isRenewOrdonnance ? renewOrdonnanceMailMessageIds(msgs) : []
        setModalityPreviewSafe({
          url,
          frameKey: m.id,
          previewKind: isSportsCert || isRenewOrdonnance ? 'prescription' : 'exam',
          examDateRaw: m.modalityMeta?.dateCreated,
          folderName: m.modalityMeta?.folderName,
          previewTitle: isSportsCert
            ? fr
              ? 'Certificat sport'
              : 'Sports certificate'
            : isRenewOrdonnance
              ? fr
                ? 'Ordonnance renouvelée'
                : 'Renewed ordonnance'
              : (m.content || '').trim() || undefined,
          sportsNocNotifyPreviewOpened: isSportsCert,
          sportsNocPreviewToken: isSportsCert
            ? previewTokenFromSportsNocPreviewUrl(url)
            : undefined,
          renewOrdonnanceNotifyPreviewOpened: isRenewOrdonnance,
          renewOrdonnanceUnlockMessageIds: renewUnlockIds,
        })
        if (isRenewOrdonnance) {
          beginRenewOrdonnanceStagedReveal(m.id, renewUnlockIds)
        }
        openPanel()
        return
      }
    },
    [beginRenewOrdonnanceStagedReveal, openPanel, setModalityPreviewSafe, resolvedApiBaseUrl],
  )

  const [renewOrdonnanceMailConfirmUnlockedIds, setRenewOrdonnanceMailConfirmUnlockedIds] =
    useState<ReadonlySet<string>>(() => new Set())
  const [renewOrdonnanceSuccessUnlockedIds, setRenewOrdonnanceSuccessUnlockedIds] =
    useState<ReadonlySet<string>>(() => new Set())
  const [reminderInlineEdit, setReminderInlineEdit] = useState<{
    messageId: string
    field: ReminderPreviewEditField
    draft: string
    originalDraft: string
    source: 'local' | 'server'
    saving: boolean
    error: string | null
  } | null>(null)
  const [reminderInlineVoiceBusy, setReminderInlineVoiceBusy] = useState(false)
  const [noteInlineEdit, setNoteInlineEdit] = useState<{
    messageId: string
    draft: string
    originalDraft: string
    saving: boolean
    error: string | null
  } | null>(null)
  const [noteInlineVoiceBusy, setNoteInlineVoiceBusy] = useState(false)
  useEffect(() => {
    if (!noteInlineEdit) setNoteInlineVoiceBusy(false)
  }, [noteInlineEdit])
  const [secretaryInlineEdit, setSecretaryInlineEdit] = useState<{
    messageId: string
    field: SecretaryMessageInlineEditField
    draft: string
    originalDraft: string
    assigneeOptions: PatientOption[]
    saving: boolean
    error: string | null
  } | null>(null)
  const [secretaryInlineVoiceBusy, setSecretaryInlineVoiceBusy] = useState(false)
  const [mailDocumentInlineEdit, setMailDocumentInlineEdit] = useState<{
    messageId: string
    field: MailDocumentInlineEditField
    draft: string
    originalDraft: string
    assigneeOptions: PatientOption[]
    saving: boolean
    error: string | null
  } | null>(null)
  const [mailDocumentInlineVoiceBusy, setMailDocumentInlineVoiceBusy] = useState(false)
  const [secretaryUrgentToggleBusyMessageId, setSecretaryUrgentToggleBusyMessageId] = useState<string | null>(null)
  const [sportsNocSummaryInlineEdit, setSportsNocSummaryInlineEdit] =
    useState<Omit<SportsNocSummaryInlineEditProps, 'isSending' | 'onLocationDraftChange' | 'onSportToggle' | 'onCommentsDraftChange' | 'onSave' | 'onCancel'> | null>(
      null,
    )
  const [sportsNocInlineVoiceBusy, setSportsNocInlineVoiceBusy] = useState(false)
  const openSportsNocSummaryInlineEditRef = useRef<
    (
      messageId: string,
      action: 'edit_location' | 'edit_sports' | 'edit_comments',
      opts?: { force?: boolean },
    ) => Promise<boolean>
  >(() => Promise.resolve(false))
  const tryOpenSportsNocReviewEditFromTextRef = useRef<(text: string) => Promise<boolean>>(
    () => Promise.resolve(false),
  )
  const sportsNocSummaryInlineEditRef = useRef(sportsNocSummaryInlineEdit)
  useEffect(() => {
    sportsNocSummaryInlineEditRef.current = sportsNocSummaryInlineEdit
  }, [sportsNocSummaryInlineEdit])
  useEffect(() => {
    if (!sportsNocSummaryInlineEdit) return
    const stillPresent = messages.some(
      (m) =>
        m.id === sportsNocSummaryInlineEdit.messageId &&
        m.role === 'assistant' &&
        !m.historySnapshot &&
        !m.sportsNocFlowCancelled &&
        Boolean(m.sportsNocSummary),
    )
    if (!stillPresent && !sportsNocSummaryInlineEdit.saving) {
      setSportsNocInlineVoiceBusy(false)
      setSportsNocSummaryInlineEdit(null)
    }
  }, [messages, sportsNocSummaryInlineEdit])

  useEffect(() => {
    let target:
      | { id: string; field: ReminderPreviewEditField; draft: string }
      | null = null
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (
        m.role !== 'assistant' ||
        m.historySnapshot ||
        !m.reminderPreview ||
        !m.awaitingRemindEditField ||
        m.reminderPreviewResolved ||
        m.reminderYesNoSuperseded ||
        !m.awaitingRemindConfirmation ||
        m.reminderConfirmed ||
        m.reminderCancelled
      ) {
        continue
      }
      target = {
        id: m.id,
        field: m.awaitingRemindEditField,
        draft: reminderInlineEditDraftForField(m.reminderPreview, m.awaitingRemindEditField),
      }
      break
    }
    if (!target) {
      setReminderInlineEdit((prev) => (prev?.saving ? prev : null))
      return
    }
    setReminderInlineEdit((prev) => {
      if (prev && prev.messageId === target!.id && prev.field === target!.field) return prev
      return {
        messageId: target.id,
        field: target.field,
        draft: target.draft,
        originalDraft: target.draft,
        source: 'server',
        saving: false,
        error: null,
      }
    })
  }, [messages])

  useEffect(() => {
    let target:
      | { id: string; field: SecretaryMessageInlineEditField; draft: string }
      | null = null
    let promptIndex = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role === 'user') continue
      if (m.role !== 'assistant' || m.historySnapshot || !m.awaitingMessageEditField) break
      promptIndex = i
      break
    }
    if (promptIndex >= 0) {
      const field = messages[promptIndex].awaitingMessageEditField!
      for (let i = promptIndex - 1; i >= 0; i--) {
        const m = messages[i]
        if (
          m.role !== 'assistant' ||
          m.historySnapshot ||
          !m.secretaryMessage ||
          !isSecretaryPreviewActionable(m)
        ) {
          continue
        }
        target = {
          id: m.id,
          field,
          draft: secretaryInlineEditDraftForField(m.secretaryMessage, field),
        }
        break
      }
    }
    if (!target) {
      setSecretaryInlineEdit((prev) => (prev?.saving ? prev : null))
      return
    }
    setSecretaryInlineVoiceBusy(false)
    setSecretaryInlineEdit((prev) => {
      if (prev && prev.messageId === target!.id && prev.field === target!.field) return prev
      return {
        messageId: target.id,
        field: target.field,
        draft: target.draft,
        originalDraft: target.draft,
        assigneeOptions: [],
        saving: false,
        error: null,
      }
    })
  }, [messages])

  useEffect(() => {
    setSecretaryInlineEdit((prev) => {
      if (!prev || prev.saving) return prev
      const src = messages.find((m) => m.id === prev.messageId)
      if (!src?.secretaryMessage || !isSecretaryPreviewActionable(src)) return null
      return prev
    })
  }, [messages])

  useEffect(() => {
    if (!secretaryInlineEdit || secretaryInlineEdit.field !== 'recipient_name' || secretaryInlineEdit.saving) return
    const draft = secretaryInlineEdit.draft.trim()
    if (!draft || draft === secretaryInlineEdit.originalDraft.trim()) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'assignee_lookup',
              assignee_lookup_text: draft,
              session_id: auditSessionId,
              ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
              ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
            }),
          })
          if (!res.ok) {
            throw new Error(`chat/command HTTP ${res.status}`)
          }
          const data = (await res.json()) as {
            assignee_options?: PatientOption[]
            error?: string
          }
          if (cancelled) return
          setSecretaryInlineEdit((prev) => {
            if (!prev || prev.field !== 'recipient_name' || prev.draft.trim() !== draft) return prev
            return {
              ...prev,
              originalDraft: draft,
              assigneeOptions: Array.isArray(data.assignee_options) ? data.assignee_options : [],
              error: (data.error || '').trim() || null,
            }
          })
        } catch (err) {
          if (cancelled) return
          const detail = err instanceof Error ? err.message : String(err)
          setSecretaryInlineEdit((prev) => {
            if (!prev || prev.field !== 'recipient_name' || prev.draft.trim() !== draft) return prev
            return { ...prev, assigneeOptions: [], error: detail || 'Could not search recipients.' }
          })
        }
      })()
    }, 260)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    secretaryInlineEdit,
    resolvedApiBaseUrl,
    auditSessionId,
    config.tenantId,
    getMedicalLinkCredentialsForApi,
  ])

  useEffect(() => {
    setMailDocumentInlineEdit((prev) => {
      if (!prev || prev.saving) return prev
      const src = messages.find((m) => m.id === prev.messageId)
      if (!src?.mailDocument || !isMailDocumentPreviewEditable(src, Boolean(src.historySnapshot))) return null
      return prev
    })
  }, [messages])

  useEffect(() => {
    if (
      !mailDocumentInlineEdit ||
      mailDocumentInlineEdit.field !== 'recipient_name' ||
      mailDocumentInlineEdit.saving
    ) {
      return
    }
    const draft = mailDocumentInlineEdit.draft.trim()
    if (!draft || draft === mailDocumentInlineEdit.originalDraft.trim()) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'assignee_lookup',
              assignee_lookup_text: draft,
              session_id: auditSessionId,
              ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
              ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
            }),
          })
          if (!res.ok) {
            throw new Error(`chat/command HTTP ${res.status}`)
          }
          const data = (await res.json()) as {
            assignee_options?: PatientOption[]
            error?: string
          }
          if (cancelled) return
          setMailDocumentInlineEdit((prev) => {
            if (!prev || prev.field !== 'recipient_name' || prev.draft.trim() !== draft) return prev
            return {
              ...prev,
              originalDraft: draft,
              assigneeOptions: Array.isArray(data.assignee_options) ? data.assignee_options : [],
              error: (data.error || '').trim() || null,
            }
          })
        } catch (err) {
          if (cancelled) return
          const detail = err instanceof Error ? err.message : String(err)
          setMailDocumentInlineEdit((prev) => {
            if (!prev || prev.field !== 'recipient_name' || prev.draft.trim() !== draft) return prev
            return { ...prev, assigneeOptions: [], error: detail || 'Could not search recipients.' }
          })
        }
      })()
    }, 500)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    mailDocumentInlineEdit,
    mailDocumentInlineVoiceBusy,
    resolvedApiBaseUrl,
    auditSessionId,
    config.tenantId,
    getMedicalLinkCredentialsForApi,
  ])

  const openReminderInlineEdit = useCallback((messageId: string, field: ReminderPreviewEditField) => {
    const src = messagesRef.current.find((m) => m.id === messageId)
    if (
      !src?.reminderPreview ||
      src.reminderYesNoSuperseded ||
      src.reminderPreviewResolved ||
      !src.awaitingRemindConfirmation ||
      src.reminderConfirmed ||
      src.reminderCancelled
    ) {
      return false
    }
    const draft = reminderInlineEditDraftForField(src.reminderPreview, field)
    setReminderInlineEdit({
      messageId,
      field,
      draft,
      originalDraft: draft,
      source: 'local',
      saving: false,
      error: null,
    })
    return true
  }, [])

  const postSportsNocPreviewOpened = useCallback(
    async (messageId: string, previewToken: string) => {
      if (openedSportsNocPreviewPostedIdsRef.current.has(messageId)) return
      openedSportsNocPreviewPostedIdsRef.current.add(messageId)
      try {
        if (voicnavSessionClientDebug()) {
          // eslint-disable-next-line no-console
          console.info('[voicnav_debug_session] sports_noc preview_opened', { messageId, previewToken })
        }
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action: 'preview_opened',
            preview_token: previewToken,
          },
        )
        const rows = (result?.assistant_messages ?? []) as ApiAssistantRow[]
        if (rows.length > 0) {
          const applyRows = applyHostedChatAssistantRowsRef.current
          if (applyRows) {
            await applyRows(rows, messagesRef.current, { skipIntentNavigation: true })
          }
        }
        if (result?.workflow?.active === true) {
          sportsNocBackendActiveRef.current = result.workflow.use_hosted_chat !== false
        }
        if (voicnavSessionClientDebug()) {
          // eslint-disable-next-line no-console
          console.info('[voicnav_debug_session] sports_noc preview_opened response', {
            messageId,
            assistant_messages_len: rows.length,
          })
        }
      } catch (error) {
        openedSportsNocPreviewPostedIdsRef.current.delete(messageId)
        throw error
      }
    },
    [resolvedApiBaseUrl, getMedicalLinkCredentialsForApi],
  )

  const openLatestPrescriptionPreviewIfReady = useCallback(
    (msgs: readonly ChatMessage[]) => {
      const pick = findLatestPrescriptionDocumentToAutoOpen(msgs)
      if (!pick) return
      const srcMsg = msgs.find((m) => m.id === pick.messageId)
      const fr = srcMsg?.sportsNocFrench === true
      const isSportsCert = srcMsg?.sportsNocCertificatePreview === true
      const url = safeModalityFileOpenUrl(pick.url, resolvedApiBaseUrl)
      if (!url) {
        if (isSportsCert) {
          // eslint-disable-next-line no-console
          console.warn('[VoiceNav] sports_noc preview URL rejected by safety filter', {
            messageId: pick.messageId,
            rawUrl: pick.url,
          })
        } else if (isRenewOrdonnance) {
          beginRenewOrdonnanceStagedReveal(pick.messageId, renewUnlockIds)
        }
        return
      }
      const previewUrlNow = safeModalityFileOpenUrl(modalityPreview?.url, resolvedApiBaseUrl)
      const previewMatchesPick = Boolean(previewUrlNow && previewUrlNow === url)
      const mailFlowActive = mailOpenThenMailFlowActive(msgs)
      if (userDismissedDocumentPreviewMessageIdsRef.current.has(pick.messageId)) return
      if (openedPrescriptionFileMessageIdsRef.current.has(pick.messageId) && previewMatchesPick) return
      if (mailFlowActive && previewMatchesPick) return
      if (shouldSkipDeferredDocumentAutoOpen(msgs, previewUrlNow, url)) return
      openedPrescriptionFileMessageIdsRef.current.add(pick.messageId)
      const previewTitle = isSportsCert
        ? fr
          ? 'Certificat sport'
          : 'Sports certificate'
        : (srcMsg?.content || '').trim() || undefined
      const isRenewOrdonnance = messageLooksLikeRenewOrdonnancePreview(srcMsg)
      const renewUnlockIds = isRenewOrdonnance ? renewOrdonnanceMailMessageIds(msgs) : []
      setModalityPreviewMaximized(false)
      if (isSportsCert) {
        // Same pattern as ECG: iframe loads the PDF URL directly (S3 for exams, VoiceNav proxy for sports NOC).
        // eslint-disable-next-line no-console
        console.info('[VoiceNav] sports_noc opening PDF preview (iframe)', {
          messageId: pick.messageId,
          url,
        })
      }
      if (mailFlowActive) {
        mailOpenThenMailDocumentOpenedRef.current = true
      }
      setModalityPreviewSafe({
        url,
        frameKey: mailFlowActive ? mailDocumentPreviewFrameKey(url) : pick.messageId,
        previewKind: 'prescription',
        previewTitle,
        sportsNocNotifyPreviewOpened: isSportsCert,
        sportsNocPreviewToken: isSportsCert
          ? previewTokenFromSportsNocPreviewUrl(url)
          : undefined,
        renewOrdonnanceNotifyPreviewOpened: isRenewOrdonnance,
        renewOrdonnanceUnlockMessageIds: renewUnlockIds,
      })
      if (isRenewOrdonnance) {
        beginRenewOrdonnanceStagedReveal(pick.messageId, renewUnlockIds)
      }
      openPanel()
    },
    [
      beginRenewOrdonnanceStagedReveal,
      openPanel,
      setModalityPreviewSafe,
      resolvedApiBaseUrl,
      modalityPreview?.url,
    ],
  )

  const onModalityPreviewIframeLoad = useCallback(() => {
    const p = modalityPreview
    if (!p) return
    if (p.renewOrdonnanceNotifyPreviewOpened) {
      clearRenewStagedRevealTimers()
      runRenewOrdonnanceStagedReveal(
        p.frameKey,
        p.renewOrdonnanceUnlockMessageIds || renewOrdonnanceMailMessageIds(messagesRef.current),
      )
    }
    if (!p.sportsNocNotifyPreviewOpened) return
    const previewToken = (p.sportsNocPreviewToken || '').trim()
    if (!previewToken) {
      // eslint-disable-next-line no-console
      console.warn('[VoiceNav] sports_noc preview_opened skipped: missing preview_token', {
        messageId: p.frameKey,
      })
      return
    }
    const fr =
      messagesRef.current.find((m) => m.id === p.frameKey)?.sportsNocFrench === true
    void postSportsNocPreviewOpened(p.frameKey, previewToken).catch((err) => {
      openedPrescriptionFileMessageIdsRef.current.delete(p.frameKey)
      const detail = err instanceof Error ? err.message : String(err)
      // eslint-disable-next-line no-console
      console.warn('[VoiceNav] sports_noc preview_opened callback failed', {
        messageId: p.frameKey,
        detail,
      })
      appendAssistantLine(
        fr
          ? `Aperçu ouvert, mais impossible de passer à l'étape e-mail : ${detail}`
          : `Preview opened, but I could not continue to the email step: ${detail}`,
      )
    })
  }, [
    modalityPreview,
    postSportsNocPreviewOpened,
    appendAssistantLine,
    clearRenewStagedRevealTimers,
    runRenewOrdonnanceStagedReveal,
  ])

  const openDeferredDocumentPreviewsIfReady = useCallback(
    (msgs: readonly ChatMessage[]) => {
      const last = msgs[msgs.length - 1]
      if (last?.role === 'assistant' && last.closeModalityPreview) return
      const pick = findLatestPrescriptionDocumentToAutoOpen(msgs)
      const pickUrl = pick?.url
        ? safeModalityFileOpenUrl(pick.url, resolvedApiBaseUrl)
        : null
      const previewUrlNow = safeModalityFileOpenUrl(modalityPreview?.url, resolvedApiBaseUrl)
      if (shouldSkipDeferredDocumentAutoOpen(msgs, previewUrlNow, pickUrl)) {
        return
      }
      openLatestModalityPreviewIfReady(msgs)
      openLatestPrescriptionPreviewIfReady(msgs)
    },
    [
      openLatestModalityPreviewIfReady,
      openLatestPrescriptionPreviewIfReady,
      resolvedApiBaseUrl,
      modalityPreview?.url,
    ],
  )

  useEffect(() => {
    if (findPendingMailSendConfirm(messages) && mailOpenThenMailDocumentOpenedRef.current) {
      return
    }
    if (mailOpenThenMailFlowActive(messages) && mailOpenThenMailDocumentOpenedRef.current) {
      return
    }
    openDeferredDocumentPreviewsIfReady(messages)
  }, [messages, openDeferredDocumentPreviewsIfReady])

  const [draft, setDraft] = useState('')
  const [isSending, setIsSending] = useState(false)

  const messageListRef = useRef<HTMLDivElement | null>(null)
  const messageEndRef = useRef<HTMLDivElement | null>(null)

  const [voiceError, setVoiceError] = useState<string | null>(null)
  /** Restored from GET /session/context (server-side active patient); not persisted in browser storage. */
  const [sessionServerActivePatientName, setSessionServerActivePatientName] = useState<string | null>(null)
  const lastPersistedOpenPatientKeyRef = useRef<string | null>(null)
  const lastApiSessionCredsRef = useRef<{ token: string; uid: string } | null>(null)
  /** Backend ``active_workflows.sports_noc`` — source of truth for hosted-chat routing after reload. */
  const sportsNocBackendActiveRef = useRef(false)
  /** Set when automatic new-tab navigation failed (no user gesture or popup blocked). */
  const [navigationOpenFailedUrl, setNavigationOpenFailedUrl] = useState<string | null>(null)
  const [backendVersionInfo, setBackendVersionInfo] = useState<VoiceNavVersionInfo | null>(null)

  const websocketUrl = useMemo(() => {
    return resolveVoiceNavWebSocketTranscribeUrl(config.websocketUrl, resolvedApiBaseUrl)
  }, [config.websocketUrl, resolvedApiBaseUrl])

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const info = await fetchVoiceNavVersionInfo(resolvedApiBaseUrl, ac.signal)
        if (!ac.signal.aborted) setBackendVersionInfo(info)
      } catch {
        if (!ac.signal.aborted) setBackendVersionInfo(null)
      }
    })()
    return () => ac.abort()
  }, [resolvedApiBaseUrl])

  const transcribeMode = useMemo<1 | 2>(() => {
    return backendVersionInfo?.transcribe_mode === 2 ? 2 : 1
  }, [backendVersionInfo])

  useEffect(() => {
    if (!backendVersionInfo) return
    logAwsTranscribeMode(transcribeMode, {
      apiBaseUrl: resolvedApiBaseUrl,
      rawFromVersion: backendVersionInfo.transcribe_mode,
    })
  }, [backendVersionInfo, transcribeMode, resolvedApiBaseUrl])

  const inlineVoiceTranscribe = useMemo(
    () => ({
      websocketUrl,
      apiBaseUrl: resolvedApiBaseUrl,
      transcribeMode,
      getMedicalLinkCredentials: getMedicalLinkCredentialsForApi,
      clientId: config.clientId,
      tenantId: config.tenantId,
      sessionId: auditSessionId,
    }),
    [
      websocketUrl,
      resolvedApiBaseUrl,
      transcribeMode,
      getMedicalLinkCredentialsForApi,
      config.clientId,
      config.tenantId,
      auditSessionId,
    ],
  )

  const composerBottomLabel = useMemo(
    () => buildComposerVersionLabel(backendVersionInfo),
    [backendVersionInfo, chatLang],
  )

  /** HTTP host adapter: Redis session + replay routes (works for Laravel embed and standalone VoicNav). */
  const patientSwitchHostAdapter = useMemo(
    () =>
      createHttpVoiceNavHostAdapter({
        apiBaseUrl: resolvedApiBaseUrl,
        getCredentials: getMedicalLinkCredentialsForApi,
        getPatientContextFromHost: () => patientContextForHostAdapterRef.current,
      }),
    [resolvedApiBaseUrl, getMedicalLinkCredentialsForApi],
  )

  const applyPendingUiReplayIfAny = useCallback(async () => {
    if (findPendingMailSendConfirm(messagesRef.current) || mailOpenThenMailFlowActive(messagesRef.current)) {
      return
    }
    if (pendingUiReplayInFlightRef.current || pendingUiReplayDoneRef.current) return
    pendingUiReplayInFlightRef.current = true
    try {
      const creds = getMedicalLinkCredentialsForApi()
      const tok = creds.piBearerToken.trim()
      const uid = creds.loggedUserId.trim()
      if (!tok || !uid) return
      const replay = await postVoiceNavSessionPendingUiReplay(resolvedApiBaseUrl, {
        piBearerToken: creds.piBearerToken,
        loggedUserId: creds.loggedUserId,
        medCenterId: creds.medCenterId,
      })
      if (!findPendingMailSendConfirm(messagesRef.current)) {
        setMessages((prev) =>
          appendPendingUiReplayDebugMessage(prev, summarizePendingUiReplayResponse(replay, 1)),
        )
      }
      if (!replay?.replayed || !Array.isArray(replay.assistant_messages)) return
      pendingUiReplayDoneRef.current = true
      const replayRows = replay.assistant_messages as ApiAssistantRow[]
      if (!replayRows.length) return
      const assistantMsgs = mapAssistantApiRows(replayRows, {
        loggedUserId: uid,
        apiBaseUrl: resolvedApiBaseUrl,
      })
      setMessages((prev) => {
        const merged = mergeMessagesAfterDeferredUiReplay(prev, assistantMsgs)
        messagesRef.current = merged
        queueMicrotask(() => openDeferredDocumentPreviewsIfReady(merged))
        return merged
      })
      completeIntentNavigationRef.current(assistantMsgs)
    } finally {
      pendingUiReplayInFlightRef.current = false
    }
  }, [resolvedApiBaseUrl, getMedicalLinkCredentialsForApi, openDeferredDocumentPreviewsIfReady])

  const maybeAutoFulfillOpenModalitySwitchThenReplay = useCallback(
    async (
      loadedSessionContext: VoiceNavSessionContextResponse | null,
      restoredMessages: ChatMessage[],
    ): Promise<boolean> => {
      const creds = getMedicalLinkCredentialsForApi()
      const tok = creds.piBearerToken.trim()
      const uid = creds.loggedUserId.trim()
      if (!tok || !uid) return false

      const messagesForReplay = withoutPostRefreshUiDebugMessages(restoredMessages).filter(
        (m) => m.id !== SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID,
      )

      const workflow = loadedSessionContext?.pending_open_modality_workflow ?? null
  if (
    !workflow ||
    (workflow.kind !== 'open_modality_for_patient' &&
      workflow.kind !== 'view_prescription_for_patient' &&
      workflow.kind !== 'sports_noc_for_patient' &&
      workflow.kind !== 'renew_ordonnance_for_patient') ||
    !workflow.awaiting_switch_confirm
  ) {
    return false
  }

      const pendingSwitch = findPendingPatientSwitchConfirm(messagesForReplay)
      if (!pendingSwitch) return false

      const n = normalizePatientContext(patientContext)
      const hostPayload =
        n && ((n.name || '').trim() || (n.patientId || '').trim())
          ? { patient_id: n.patientId, patient_name: n.name }
          : null

      const switchReq = pendingSwitchRequestedNameFromMessages(
        messagesForReplay,
        pendingSwitch.messageId,
      )

      if (
        !modalitiesQueuedSwitchChartMatches({
          workflow,
          hostPatient: hostPayload,
          sessionActivePatient: loadedSessionContext?.active_patient,
          switchRequestedPatientName: switchReq,
        })
      ) {
        return false
      }

      await fulfillPatientSwitchConfirmRef.current(pendingSwitch.messageId, 'yes')
      const refreshedCtx =
        (await fetchVoiceNavSessionContext(resolvedApiBaseUrl, creds)) ?? loadedSessionContext
      const afterFulfill = withoutPostRefreshUiDebugMessages(messagesRef.current).filter(
        (m) => m.id !== SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID,
      )
      await tryReplayDeferredPatientSwitchEcg({
        adapter: patientSwitchHostAdapter,
        restoredMessages: afterFulfill,
        initialSessionContext: refreshedCtx,
        pendingUiReplayDoneRef,
        lastPersistedOpenPatientKeyRef,
        replayInFlightRef: pendingUiReplayInFlightRef,
        clearPatientSwitchUserCommitted,
        onReplayAttempt: (outcome) => {
          setMessages((prev) => appendPendingUiReplayDebugMessage(prev, outcome))
        },
        onActivePatientSyncedAfterPersist: (merged) => {
          const nm = (merged.patient_name || '').trim()
          if (nm) setSessionServerActivePatientName(nm)
          setSessionActivePatient(merged as VoiceNavSessionActivePatientPayload)
        },
        onReplaySuccess: async (assistant_messages) => {
          const replayRows = assistant_messages as ApiAssistantRow[]
          if (!replayRows.length) return
          const assistantMsgs = mapAssistantApiRows(replayRows, {
            loggedUserId: uid,
            apiBaseUrl: resolvedApiBaseUrl,
          })
          setMessages((prev) => {
            const next = mergeMessagesAfterDeferredUiReplay(prev, assistantMsgs)
            messagesRef.current = next
            queueMicrotask(() => openDeferredDocumentPreviewsIfReady(next))
            return next
          })
          completeIntentNavigationRef.current(assistantMsgs)
        },
        onFinishedPipeline: () => {
          reloadModalityReplayStagedRef.current = false
          finishDeferredReplayPipeline()
        },
      })
      return true
    },
    [
      getMedicalLinkCredentialsForApi,
      patientSwitchHostAdapter,
      patientContext,
      resolvedApiBaseUrl,
      setSessionServerActivePatientName,
      setSessionActivePatient,
      openDeferredDocumentPreviewsIfReady,
    ],
  )

  const runDeferredPendingUiPipelineAfterHydrate = useCallback(
    async (
      restoredMessages: ChatMessage[],
      /** From ``GET /session/context`` in the same hydrate pass; pending replay runs only after this snapshot exists. */
      loadedSessionContext: VoiceNavSessionContextResponse | null,
    ) => {
      const creds = getMedicalLinkCredentialsForApi()
      const tok = creds.piBearerToken.trim()
      const uid = creds.loggedUserId.trim()
      if (!tok || !uid) {
        reloadModalityReplayStagedRef.current = false
        void applyPendingUiReplayIfAny()
        return
      }

      const messagesForReplay = withoutPostRefreshUiDebugMessages(restoredMessages).filter(
        (m) => m.id !== SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID,
      )

      if (!findPendingPatientSwitchConfirm(messagesForReplay)) {
        clearPatientSwitchUserCommitted()
      }

      if (await maybeAutoFulfillOpenModalitySwitchThenReplay(loadedSessionContext, restoredMessages)) {
        return
      }

      await tryReplayDeferredPatientSwitchEcg({
        adapter: patientSwitchHostAdapter,
        restoredMessages: messagesForReplay,
        initialSessionContext: loadedSessionContext,
        pendingUiReplayDoneRef,
        lastPersistedOpenPatientKeyRef,
        replayInFlightRef: pendingUiReplayInFlightRef,
        clearPatientSwitchUserCommitted,
        onReplayAttempt: (outcome) => {
          setMessages((prev) => appendPendingUiReplayDebugMessage(prev, outcome))
        },
        onActivePatientSyncedAfterPersist: (merged) => {
          const nm = (merged.patient_name || '').trim()
          if (nm) setSessionServerActivePatientName(nm)
          setSessionActivePatient(merged as VoiceNavSessionActivePatientPayload)
        },
        onReplaySuccess: async (assistant_messages) => {
          const replayRows = assistant_messages as ApiAssistantRow[]
          if (!replayRows.length) return
          const assistantMsgs = mapAssistantApiRows(replayRows, {
            loggedUserId: uid,
            apiBaseUrl: resolvedApiBaseUrl,
          })
          setMessages((prev) => {
            const next = mergeMessagesAfterDeferredUiReplay(prev, assistantMsgs)
            messagesRef.current = next
            queueMicrotask(() => openDeferredDocumentPreviewsIfReady(next))
            return next
          })
          completeIntentNavigationRef.current(assistantMsgs)
        },
        onFinishedPipeline: () => {
          reloadModalityReplayStagedRef.current = false
          finishDeferredReplayPipeline()
        },
      })
    },
    [
      getMedicalLinkCredentialsForApi,
      patientSwitchHostAdapter,
      applyPendingUiReplayIfAny,
      setSessionServerActivePatientName,
      setSessionActivePatient,
      patientContext,
      resolvedApiBaseUrl,
      maybeAutoFulfillOpenModalitySwitchThenReplay,
      openDeferredDocumentPreviewsIfReady,
    ],
  )

  useLayoutEffect(() => {
    runDeferredPendingUiPipelineAfterHydrateRef.current = runDeferredPendingUiPipelineAfterHydrate
  }, [runDeferredPendingUiPipelineAfterHydrate])

  useEffect(() => {
    if (!sessionChatHydrated) return
    const creds = getMedicalLinkCredentialsForApi()
    const tok = creds.piBearerToken.trim()
    const uid = creds.loggedUserId.trim()
    if (!tok || !uid) return
    if (restoringChatFromServerRef.current) return
    if (patientSwitchConfirmInFlightRef.current) return

    let cancelled = false
    const handle = window.setTimeout(() => {
      void (async () => {
        if (cancelled) return
        const ctx = await fetchVoiceNavSessionContext(resolvedApiBaseUrl, creds)
        if (cancelled) return
        await maybeAutoFulfillOpenModalitySwitchThenReplay(ctx, messagesRef.current)
      })()
    }, 50)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [
    sessionChatHydrated,
    patientContext,
    sessionActivePatient,
    maybeAutoFulfillOpenModalitySwitchThenReplay,
    resolvedApiBaseUrl,
    getMedicalLinkCredentialsForApi,
  ])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const creds = getMedicalLinkCredentialsForApi()
      const tok = creds.piBearerToken.trim()
      const uid = creds.loggedUserId.trim()
      if (!tok || !uid) {
        if (!cancelled) {
          setSessionServerActivePatientName(null)
          setSessionActivePatient(null)
          setSessionChatHydrated(true)
        }
        return
      }
      const credKey = `${uid}|${tok}`
      const scope = conversationScopeKey
      const alreadyHydratedThisScope = chatHydrateCompletedForScopeRef.current === scope
      const liveMsgs = messagesRef.current
      const mailFlowBlocksHydrateReplace =
        findPendingMailSendConfirm(liveMsgs) ||
        mailOpenThenMailFlowActive(liveMsgs) ||
        chatCommandInFlightRef.current > 0

      const [data, sessionCtx] = await Promise.all([
        fetchVoiceNavChatHistory(resolvedApiBaseUrl, creds),
        fetchVoiceNavSessionContext(resolvedApiBaseUrl, creds),
      ])
      if (cancelled) return
      const credsAfter = getMedicalLinkCredentialsForApi()
      const afterKey = `${credsAfter.loggedUserId.trim()}|${credsAfter.piBearerToken.trim()}`
      if (afterKey !== credKey) return
      const sportsSnap = sessionCtx?.active_workflows?.sports_noc
      sportsNocBackendActiveRef.current =
        sportsSnap?.active === true && sportsSnap.use_hosted_chat !== false
      const ap = sessionCtx?.active_patient ?? null
      const name = (ap?.patient_name || '').trim()
      if (!cancelled) {
        setSessionServerActivePatientName(name || null)
        setSessionActivePatient(
          ap && ((ap.patient_id || '').trim() || (ap.patient_name || '').trim()) ? ap : null,
        )
      }

      if (alreadyHydratedThisScope || mailFlowBlocksHydrateReplace) {
        if (!alreadyHydratedThisScope) {
          chatHydrateCompletedForScopeRef.current = scope
        }
        queueMicrotask(() => {
          restoringChatFromServerRef.current = false
          setSessionChatHydrated(true)
        })
        return
      }

      restoringChatFromServerRef.current = true
      let restored: ChatMessage[]
      if (data == null) {
        restored = copilotInitialMessages()
      } else {
        const rows = data.messages ?? []
        const sportsSnapHydrate = sessionCtx?.active_workflows?.sports_noc
        const sportsFlowLive =
          sportsSnapHydrate?.active === true &&
          Boolean((sportsSnapHydrate.awaiting_field || '').trim())
        if (rows.length > 0) {
          restored = dedupePatientSwitchYesUserMessages(
            sportsFlowLive ? wireRowsToLiveChatMessages(rows) : wireMessagesToChatMessages(rows),
          )
          if (sportsFlowLive && sportsSnapHydrate) {
            restored = applySportsNocWorkflowSnapshotToMessages(restored, sportsSnapHydrate)
          }
        } else {
          restored = copilotInitialMessages()
        }
      }
      restored = withoutPostRefreshUiDebugMessages(restored)
      if (chatCommandInFlightRef.current > 0) {
        queueMicrotask(() => {
          restoringChatFromServerRef.current = false
          setSessionChatHydrated(true)
        })
        return
      }
      chatHydrateCompletedForScopeRef.current = scope
      setMessages(normalizeChatMessagesForDisplay(restored))
      queueMicrotask(() => {
        restoringChatFromServerRef.current = false
        setSessionChatHydrated(true)
        void runDeferredPendingUiPipelineAfterHydrateRef.current(restored, sessionCtx)
      })
    })()
    return () => {
      cancelled = true
    }
  }, [resolvedApiBaseUrl, getMedicalLinkCredentialsForApi, conversationScopeKey])

  const persistSessionChatHistoryNow = useCallback(
    (msgs: ChatMessage[]) => {
      if (!sessionChatHydrated || restoringChatFromServerRef.current) return
      const creds = getMedicalLinkCredentialsForApi()
      if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return
      void replaceVoiceNavSessionChatHistory(resolvedApiBaseUrl, creds, chatMessagesToWire(msgs))
    },
    [sessionChatHydrated, resolvedApiBaseUrl, getMedicalLinkCredentialsForApi],
  )

  useEffect(() => {
    const onPageHide = () => {
      if (!sessionChatHydrated || restoringChatFromServerRef.current) return
      const creds = getMedicalLinkCredentialsForApi()
      if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return
      flushVoiceNavSessionChatHistory(
        resolvedApiBaseUrl,
        creds,
        chatMessagesToWire(messagesRef.current),
      )
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [sessionChatHydrated, resolvedApiBaseUrl, getMedicalLinkCredentialsForApi])

  useEffect(() => {
    if (!sessionChatHydrated) return
    const creds = getMedicalLinkCredentialsForApi()
    const tok = creds.piBearerToken.trim()
    const uid = creds.loggedUserId.trim()
    if (!tok || !uid) return
    if (restoringChatFromServerRef.current) return
    if (patientSwitchConfirmInFlightRef.current || reloadModalityReplayStagedRef.current) return
    if (chatCommandInFlightRef.current > 0) return
    if (Date.now() < suppressDebouncedChatPersistUntilRef.current) return
    const handle = window.setTimeout(() => {
      if (chatCommandInFlightRef.current > 0) return
      if (Date.now() < suppressDebouncedChatPersistUntilRef.current) return
      void replaceVoiceNavSessionChatHistory(resolvedApiBaseUrl, creds, chatMessagesToWire(messages))
    }, 750)
    return () => window.clearTimeout(handle)
  }, [messages, sessionChatHydrated, resolvedApiBaseUrl, getMedicalLinkCredentialsForApi])

  useEffect(() => {
    const pending = findPendingPatientSwitchConfirm(messages)
    if (pending) {
      const idx = messages.findIndex((m) => m.id === pending.messageId)
      if (idx >= 0 && !hasPatientSwitchUserAnswerAfter(messages, idx)) {
        clearPatientSwitchUserCommitted()
      }
    }
    if (countPatientSwitchYesUserMessages(messages) <= 1) return
    const deduped = dedupePatientSwitchYesUserMessages(messages)
    if (deduped.length === messages.length) return
    messagesRef.current = deduped
    setMessages(deduped)
  }, [messages])

  useEffect(() => {
    const creds = getMedicalLinkCredentialsForApi()
    const tok = creds.piBearerToken.trim()
    const uid = creds.loggedUserId.trim()
    const prev = lastApiSessionCredsRef.current
    if (prev?.token) {
      const loggedOut = !tok
      const uidChanged = Boolean(tok) && prev.uid !== uid
      const tokenChanged = Boolean(tok) && tok !== prev.token
      if (loggedOut || uidChanged || tokenChanged) {
        void postVoiceNavLogout(resolvedApiBaseUrl, prev)
      }
      if (loggedOut || uidChanged) {
        lastPersistedOpenPatientKeyRef.current = null
        setSessionServerActivePatientName(null)
        setSessionActivePatient(null)
        setMessages([])
        setSessionChatHydrated(true)
      } else if (tokenChanged) {
        lastPersistedOpenPatientKeyRef.current = null
      }
    }
    if (tok && uid) lastApiSessionCredsRef.current = { token: tok, uid }
    else lastApiSessionCredsRef.current = null
  }, [getMedicalLinkCredentialsForApi, resolvedApiBaseUrl])

  const persistServerActivePatientFromRows = useCallback(
    async (rows: ApiAssistantRow[], opts?: { skipPendingReplay?: boolean }) => {
      const payload = coalescePatientPayloadsForSessionSync(
        latestOpenPatientPayloadFromRows(rows),
        latestPrescriptionPatientPayloadFromRows(rows),
        latestBloodTestPatientPayloadFromRows(rows),
        latestPendingModalityPatientPayloadFromAssistantRows(rows),
      )
      if (!payload) return
      const key = [payload.patient_id ?? '', payload.patient_name ?? '', payload.dob ?? ''].join('|')
      const skipReplay =
        opts?.skipPendingReplay === true || reloadModalityReplayStagedRef.current
      const shouldReplayAfterPersist = apiRowsNeedPendingUiReplayAfterActivePatientPersist(rows)
      const mailFlowBlocksReplay =
        findPendingMailSendConfirm(messagesRef.current) ||
        mailOpenThenMailFlowActive(messagesRef.current)
      if (lastPersistedOpenPatientKeyRef.current === key) {
        if (!skipReplay && shouldReplayAfterPersist && !mailFlowBlocksReplay) void applyPendingUiReplayIfAny()
        return
      }
      lastPersistedOpenPatientKeyRef.current = key
      const creds = getMedicalLinkCredentialsForApi()
      const synced = await postVoiceNavSessionActivePatient(resolvedApiBaseUrl, creds, payload)
      if (!synced) lastPersistedOpenPatientKeyRef.current = null
      else {
        const nm = (synced.patient_name || payload.patient_name || '').trim()
        if (nm) setSessionServerActivePatientName(nm)
        setSessionActivePatient(synced)
        if (!skipReplay && shouldReplayAfterPersist && !mailFlowBlocksReplay) {
          if (apiRowsHasDeferredOpenEcgOpenPatient(rows)) {
            reloadModalityReplayStagedRef.current = true
            stagePendingModalityReplayAfterReload()
          } else {
            void applyPendingUiReplayIfAny()
          }
        }
      }
    },
    [resolvedApiBaseUrl, getMedicalLinkCredentialsForApi, applyPendingUiReplayIfAny],
  )

  const handleRenewOrdonnanceMailYesNo = useCallback(
    (messageId: string, choice: 'yes' | 'no') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      const fr = src?.renewOrdonnanceFrench ?? sportsNocFollowupFr()
      const label =
        choice === 'yes' ? (fr ? 'Oui, envoyer' : 'Yes, mail it') : fr ? 'Non, annuler' : 'No, cancel'
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: 'user' as const, content: label, timestamp: Date.now() },
      ])
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                renewOrdonnanceMailResolved: true,
                renewOrdonnanceMailPrompt: undefined,
                renewOrdonnanceMeta: undefined,
                renewOrdonnanceYesNoPrompt: undefined,
                mailDocument: m.mailDocument
                  ? { ...m.mailDocument, awaitingMailConfirmation: false }
                  : undefined,
              }
            : m,
        ),
      )
      if (choice === 'no') {
        appendAssistantLine(
          fr
            ? "Compris : je n'ai pas envoyé l'ordonnance renouvelée."
            : 'Understood — I have not sent the renewed ordonnance.',
        )
        return
      }
      const mail = src?.mailDocument
      const receiverId = (mail?.mailReceiverId || src?.renewOrdonnanceMailPrompt?.receiverId || '').trim()
      const fileUrl = (mail?.mailAttachmentUrl || src?.renewOrdonnanceMailPrompt?.fileUrl || '').trim()
      const subject = (mail?.mailSubject || src?.renewOrdonnanceMailPrompt?.subject || 'renewed vidal ordonance').trim()
      const message = (mail?.mailBody || src?.renewOrdonnanceMailPrompt?.message || 'renewed vidal ordonance enclosed').trim()
      const recipientName = (mail?.recipientName || '').trim() || 'Patient'
      if (!receiverId || !fileUrl) {
        appendAssistantLine(
          fr
            ? "Impossible d'envoyer : détails patient/document manquants."
            : 'Unable to mail: missing patient/document details.',
        )
        return
      }
      void (async () => {
        try {
          const s = getMedicalLinkCredentialsFromSession()
          const token = ((config.piBearerToken ?? '').trim() || s.piBearerToken).trim()
          if (!token) throw new Error('missing token')
          await postSportsCertificateMail(
            { bearerToken: token },
            resolvedApiBaseUrl,
            {
              receiver_id: receiverId,
              subject,
              message,
              file_url: fileUrl,
            },
          )
          setMessages((prev) => [
            ...prev,
            {
              id: uid(),
              role: 'assistant' as const,
              content: '',
              timestamp: Date.now(),
              mailDocument: localMailDocumentSentSuccessPayload({
                documentLabel: subject,
                recipientName,
              }),
            },
          ])
        } catch {
          appendAssistantLine(
            fr
              ? "Impossible d'envoyer l'ordonnance renouvelée par e-mail pour le moment."
              : 'Unable to send the renewed Vidal ordonnance by e-mail right now.',
          )
        }
      })()
    },
    [appendAssistantLine, config.piBearerToken, resolvedApiBaseUrl, sportsNocFollowupFr],
  )

  const getActiveDocumentContext = useCallback(() => {
    const p = modalityPreview
    if (!p) {
      return { id: '', name: '', url: '', mime: '', examDate: '', folderName: '' }
    }
    const safeUrl = safeModalityFileOpenUrl(p.url, resolvedApiBaseUrl) ?? ''
    const folderSlug = (p.folderName ?? '').trim().toLowerCase()
    const folderLabels: Record<string, string> = {
      ecg: 'ECG',
      ekg: 'ECG',
      echo: 'Echocardiogram',
      mri: 'MRI',
      irm: 'MRI',
      ct: 'CT scan',
      sport: 'Sports medical certificate',
      sports: 'Sports medical certificate',
      certificat_sport: 'Sports medical certificate',
    }
    const name =
      p.previewKind === 'prescription'
        ? p.previewTitle?.trim() || 'Prescription document'
        : p.previewTitle?.trim() || folderLabels[folderSlug] || 'Exam document'
    return {
      id: p.frameKey,
      name,
      url: safeUrl,
      mime: safeUrl ? guessMimeFromDocumentUrl(safeUrl, p.previewKind) : '',
      examDate: (p.examDateRaw ?? '').trim(),
      folderName: (p.folderName ?? '').trim(),
    }
  }, [modalityPreview])

  const completeIntentNavigation = useCallback((msgs: ChatMessage[]) => {
    const w = intentNavWindowRef.current
    intentNavWindowRef.current = null

    const latest = pickLatestIntentNavigation(msgs)
    const url = latest?.url

    if (latest?.kind === 'page_navigation' && url) {
      readAloud.stop()
      navigateOrClosePendingWindow(w, undefined)
      openPanel()
      const ok = tryNavigateMedilinkMenuSameTab(url)
      if (!ok) {
        const recovered = navigateOrClosePendingWindow(null, url)
        if (!recovered) {
          setNavigationOpenFailedUrl(url)
        } else {
          setNavigationOpenFailedUrl(null)
        }
      } else {
        setNavigationOpenFailedUrl(null)
      }
      return
    }

    if (latest?.kind === 'patient_dossier' && url) {
      readAloud.stop()
      navigateOrClosePendingWindow(w, undefined)
      openPanel()
      const ok = tryNavigateMedilinkMenuSameTab(url)
      if (!ok) {
        const recovered = navigateOrClosePendingWindow(null, url)
        if (!recovered) {
          setNavigationOpenFailedUrl(url)
        } else {
          setNavigationOpenFailedUrl(null)
        }
      } else {
        setNavigationOpenFailedUrl(null)
      }
      return
    }

    readAloud.stop()
    const fallbackUrl = url ?? pickFirstIntentUrl(msgs)
    const ok = navigateOrClosePendingWindow(w, fallbackUrl)
    if (fallbackUrl && !ok) {
      setNavigationOpenFailedUrl(fallbackUrl)
    } else {
      setNavigationOpenFailedUrl(null)
    }
  }, [readAloud.stop, openPanel])

  useLayoutEffect(() => {
    completeIntentNavigationRef.current = completeIntentNavigation
  }, [completeIntentNavigation])

  const cancelIntentNavigation = useCallback(() => {
    setNavigationOpenFailedUrl(null)
    const w = intentNavWindowRef.current
    intentNavWindowRef.current = null
    navigateOrClosePendingWindow(w, undefined)
  }, [])

  /** Merge assistant rows from /chat/command into the live transcript (no server re-fetch). */
  const applyHostedChatAssistantRows = useCallback(
    async (
      rows: ApiAssistantRow[],
      historyForMock: ChatMessage[],
      opts?: {
        skipIntentNavigation?: boolean
        llmTokenUsage?: unknown
        turnKind?: 'typed' | 'voice'
        transcribeProcessingMs?: number
        intentProcessingMs?: number
        commandStartedAt?: number
        typedCommandUserMessageId?: string
      },
    ) => {
      if (rows.length === 0) return false
      let assistantMsgs = stampVoiceTranscribeProcessing(
        mapAssistantApiRows(rows, {
          loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
          apiBaseUrl: resolvedApiBaseUrl,
        }),
        opts?.turnKind === 'voice' && opts?.intentProcessingMs == null
          ? opts?.transcribeProcessingMs
          : undefined,
      )
      if (opts?.turnKind === 'voice' && opts?.intentProcessingMs != null) {
        assistantMsgs = stampIntentProcessing(assistantMsgs, opts.intentProcessingMs)
      }
      const typedCommandProcessingMs =
        opts?.turnKind === 'typed' ? elapsedCommandProcessingMs(opts?.commandStartedAt) : undefined
      const applyTypedTiming = (snapshot: ChatMessage[]) =>
        typedCommandProcessingMs != null
          ? stampTypedCommandProcessingOnTurn(
              snapshot,
              assistantMsgs,
              typedCommandProcessingMs,
              opts?.typedCommandUserMessageId,
            )
          : snapshot
      const mergeAssistantInto = (base: ChatMessage[]) => {
        let merged = dedupePatientSwitchYesUserMessages(
          mergePatientOptionsRefresh(base, assistantMsgs).map(enrichPatientSwitchConfirmOnMessage),
        )
        merged = applySportsNocClarificationSupersedeToEarlierMessages(merged, assistantMsgs)
        merged = applySportsNocFlowCancelledToEarlierMessages(merged, assistantMsgs)
        merged = applyMailDocumentOpenAwaitingSupersedeToEarlierPreviews(merged, assistantMsgs)
        return stripStalePrescriptionAutoOpenAfterCrossPatientSwitch(merged)
      }
      let merged = mergeAssistantInto(historyForMock)
      const reloadBeforeModality = shouldReloadHostBeforeDeferredCrossPatientEcg(rows, merged)
      const crossPatientOpenDeferred = mergedChatCrossPatientOpenBeforeDeferredEcg(merged)
      const persistChatSnapshot = (snapshot: ChatMessage[]) => {
        const creds = getMedicalLinkCredentialsForApi()
        if (!creds.piBearerToken.trim() || !creds.loggedUserId.trim()) return
        void replaceVoiceNavSessionChatHistory(
          resolvedApiBaseUrl,
          creds,
          chatMessagesToWire(snapshot),
        )
      }

      if (reloadBeforeModality) {
        reloadModalityReplayStagedRef.current = true
        const mergedForReload = mergeAssistantInto(historyForMock)
        await persistServerActivePatientFromRows(rows, { skipPendingReplay: true })
        const reloadMerged = applyTypedTiming(
          mergeAssistantInto(
            messagesRef.current.length >= historyForMock.length ? messagesRef.current : historyForMock,
          ),
        )
        messagesRef.current = reloadMerged
        setMessages(reloadMerged)
        suppressDebouncedChatPersistUntilRef.current = Date.now() + 2500
        llmTokenSessionLogRef.current.logTurn(opts?.turnKind ?? 'typed', opts?.llmTokenUsage)
        stagePendingModalityReplayAfterReload()
        if (!opts?.skipIntentNavigation) {
          readAloud.stop()
          openPanel()
          setNavigationOpenFailedUrl(null)
          const navMerged =
            messagesRef.current.length >= mergedForReload.length
              ? messagesRef.current
              : mergedForReload
          const dossierOk = navigateLatestPatientDossierInHost(navMerged)
          if (!dossierOk) {
            completeIntentNavigation(navMerged)
          }
        } else if (crossPatientOpenDeferred) {
          readAloud.stop()
          openPanel()
        }
        window.setTimeout(() => {
          if (pendingUiReplayDoneRef.current) return
          void (async () => {
            const creds = getMedicalLinkCredentialsForApi()
            const ctx = await fetchVoiceNavSessionContext(resolvedApiBaseUrl, creds)
            await tryReplayDeferredPatientSwitchEcg({
              adapter: patientSwitchHostAdapter,
              restoredMessages: messagesRef.current,
              initialSessionContext: ctx,
              pendingUiReplayDoneRef,
              lastPersistedOpenPatientKeyRef,
              replayInFlightRef: pendingUiReplayInFlightRef,
              clearPatientSwitchUserCommitted,
              onReplayAttempt: (outcome) => {
                setMessages((prev) => appendPendingUiReplayDebugMessage(prev, outcome))
              },
              onActivePatientSyncedAfterPersist: (mergedPayload) => {
                const nm = (mergedPayload.patient_name || '').trim()
                if (nm) setSessionServerActivePatientName(nm)
                setSessionActivePatient(mergedPayload as VoiceNavSessionActivePatientPayload)
              },
              onReplaySuccess: async (assistant_messages) => {
                const replayRows = assistant_messages as ApiAssistantRow[]
                if (!replayRows.length) return
                const uid = creds.loggedUserId.trim()
                const replayMsgs = mapAssistantApiRows(replayRows, {
                  loggedUserId: uid,
                  apiBaseUrl: resolvedApiBaseUrl,
                })
                setMessages((prev) => {
                  const next = mergeMessagesAfterDeferredUiReplay(prev, replayMsgs)
                  messagesRef.current = next
                  queueMicrotask(() => {
                    openDeferredDocumentPreviewsIfReady(next)
                    completeIntentNavigationRef.current(next)
                  })
                  return next
                })
              },
              onFinishedPipeline: () => {
                reloadModalityReplayStagedRef.current = false
                finishDeferredReplayPipeline()
              },
            })
          })()
        }, 1500)
        return true
      }

      void persistServerActivePatientFromRows(rows, {
        skipPendingReplay: reloadModalityReplayStagedRef.current,
      })
      const mergeBase =
        messagesRef.current.length >= historyForMock.length ? messagesRef.current : historyForMock
      const mergedNext = applyTypedTiming(mergeAssistantInto(mergeBase))
      const normalizedDisplay = normalizeChatMessagesForDisplay(mergedNext)
      const mailFlowTurn = mailOpenThenMailFlowActive(normalizedDisplay)
      const incomingAwaitingDocumentOpen = assistantMsgs.some(
        (m) => m.mailDocument?.awaitingDocumentOpen === true,
      )
      const incomingMailPreviewReady = assistantMsgs.some(
        (m) =>
          m.awaitingMailConfirmation === true || m.mailDocument?.awaitingMailConfirmation === true,
      )
      if (incomingAwaitingDocumentOpen && !incomingMailPreviewReady) {
        mailOpenThenMailDocumentOpenedRef.current = false
      }
      commitChatMessages(normalizedDisplay)
      suppressDebouncedChatPersistUntilRef.current = Date.now() + 2500
      const renewReveal = findRenewOrdonnanceStagedRevealTargets(normalizedDisplay)
      if (renewReveal) {
        beginRenewOrdonnanceStagedReveal(renewReveal.successMessageId, renewReveal.mailMessageIds)
      }
      if (!incomingMailPreviewReady) {
        queueMicrotask(() => {
          openDeferredDocumentPreviewsIfReady(mergedNext)
          const end = messageEndRef.current
          if (end) end.scrollIntoView({ behavior: 'smooth', block: 'end' })
        })
      } else {
        queueMicrotask(() => openDeferredDocumentPreviewsIfReady(mergedNext))
      }
      llmTokenSessionLogRef.current.logTurn(opts?.turnKind ?? 'typed', opts?.llmTokenUsage)
      if (opts?.skipIntentNavigation || mailFlowTurn) {
        cancelIntentNavigation()
      } else if (!readAloudRequestFromAssistantRows(rows)) {
        completeIntentNavigation(mergedNext)
      } else {
        cancelIntentNavigation()
      }
      const readAloudReq = readAloudRequestFromAssistantRows(rows)
      if (readAloudReq) {
        queueMicrotask(() => {
          if ('text' in readAloudReq) {
            void readAloud.synthesizeAndPlay([{ text: readAloudReq.text, language: readAloudReq.language }])
          } else {
            void readAloud.playMp3Base64Parts(readAloudReq.parts)
          }
        })
      }
      return true
    },
    [
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      completeIntentNavigation,
      navigateLatestPatientDossierInHost,
      cancelIntentNavigation,
      persistServerActivePatientFromRows,
      openDeferredDocumentPreviewsIfReady,
      commitChatMessages,
      beginRenewOrdonnanceStagedReveal,
      patientSwitchHostAdapter,
      setSessionServerActivePatientName,
      setSessionActivePatient,
      readAloud.synthesizeAndPlay,
      readAloud.playMp3Base64Parts,
    ],
  )

  useLayoutEffect(() => {
    applyHostedChatAssistantRowsRef.current = applyHostedChatAssistantRows
  }, [applyHostedChatAssistantRows])

  const runHostedChatCommand = useCallback(
    async (
      text: string,
      historyForMock: ChatMessage[],
      opts?: {
        skipIntentNavigation?: boolean
        commandStartedAt?: number
        typedCommandUserMessageId?: string
        skipCommandNormalize?: boolean
      },
    ) => {
      chatCommandInFlightRef.current += 1
      try {
      const resolved = await resolveTypedCommandForHostedChat(text, resolvedApiBaseUrl, {
        skipCommandNormalize: opts?.skipCommandNormalize,
      })
      const commandText = resolved.commandText
      if (
        opts?.typedCommandUserMessageId &&
        (resolved.typedNormalizeProcessingMs != null || commandText !== text.trim())
      ) {
        const patchUser = (prev: ChatMessage[]) =>
          prev.map((m) =>
            m.id === opts.typedCommandUserMessageId
              ? withTypedNormalizeProcessing(
                  { ...m, content: commandText },
                  resolved.typedNormalizeProcessingMs,
                )
              : m,
          )
        messagesRef.current = patchUser(messagesRef.current)
        setMessages(patchUser)
      }
      const docCtx = getActiveDocumentContext()
      const hasOpenDoc = Boolean(docCtx.id?.trim() || docCtx.url?.trim())
      const creds = getMedicalLinkCredentialsForApi()
      const chatAbort = new AbortController()
      const chatTimeout = window.setTimeout(() => chatAbort.abort(), 90_000)
      let res: Response
      try {
        res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(creds.piBearerToken.trim()
              ? { Authorization: `Bearer ${creds.piBearerToken.trim()}` }
              : {}),
          },
          body: JSON.stringify({
            text: commandText,
            skip_asr_normalization: resolved.skipAsrNormalization,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(creds),
            ...clientReminderRuntimePayload(),
            ...(hasOpenDoc
              ? {
                  active_document_id: docCtx.id?.trim() || undefined,
                  active_document_name: docCtx.name?.trim() || undefined,
                  active_document_url: docCtx.url?.trim() || undefined,
                  ...(docCtx.mime?.trim() ? { active_document_mime_type: docCtx.mime.trim() } : {}),
                  ...(docCtx.examDate?.trim() ? { active_document_exam_date: docCtx.examDate.trim() } : {}),
                  ...(docCtx.folderName?.trim() ? { active_document_folder: docCtx.folderName.trim() } : {}),
                }
              : {}),
          }),
          signal: chatAbort.signal,
        })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw new Error('chat/command timed out — the server may be overloaded or restarting')
        }
        throw err
      } finally {
        window.clearTimeout(chatTimeout)
      }
      if (voicnavSessionClientDebug()) {
        // eslint-disable-next-line no-console
        console.info('[voicnav_debug_session] chat/command response', {
          httpStatus: res.status,
          ok: res.ok,
        })
      }
      if (!res.ok) {
        throw new Error(`chat/command HTTP ${res.status}`)
      }
      const data = (await res.json()) as {
        assistant_messages?: ApiAssistantRow[]
        llm_token_usage?: unknown
      }
      const rawRows = data.assistant_messages ?? []
      const rows = filterAssistantRowsForPatientShowMoreCommand(rawRows, commandText, historyForMock)
      lastHostedAssistantRowsRef.current = rows
      if (voicnavSessionClientDebug()) {
        // eslint-disable-next-line no-console
        console.info('[voicnav_debug_session] chat/command rows', {
          textLen: commandText.length,
          rawAssistantCount: rawRows.length,
          afterPatientShowMoreFilter: rows.length,
          usingMockFallback: rows.length === 0,
        })
      }
      if (rows.length === 0) {
        if (shouldUseHostedChatForNoteTask(commandText, historyForMock)) {
          const frHint =
            /[àâäéèêëïîôùûçœ]|crée|créer|tâche|note\b/i.test(commandText) ||
            (isFrenchLanguage())
          appendAssistantLine(
            frHint
              ? 'Je n’ai pas pu traiter la demande de note — réessayez.'
              : 'I could not process the note request — please try again.',
          )
          cancelIntentNavigation()
          return
        }
        const fallback = await mockReplyFor({
          text: commandText,
          clientId: config.clientId,
          tenantId: config.tenantId,
          apiBaseUrl: resolvedApiBaseUrl,
          patientContext,
          history: historyForMock,
        })
        const fallbackMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: String(fallback),
          timestamp: Date.now(),
        }
        const fallbackMs = elapsedCommandProcessingMs(opts?.commandStartedAt)
        setMessages((prev) => {
          let next = mergePatientOptionsRefresh(prev, [
            fallbackMs != null ? withTypedCommandProcessing(fallbackMsg, fallbackMs) : fallbackMsg,
          ])
          if (fallbackMs != null && opts?.typedCommandUserMessageId) {
            next = stampTypedCommandProcessingOnTurn(next, [fallbackMsg], fallbackMs)
          }
          persistSessionChatHistoryNow(next)
          return next
        })
        cancelIntentNavigation()
        return
      }
      const applied = await applyHostedChatAssistantRows(rows, historyForMock, {
        skipIntentNavigation: opts?.skipIntentNavigation,
        llmTokenUsage: data.llm_token_usage,
        turnKind: 'typed',
        commandStartedAt: opts?.commandStartedAt,
        typedCommandUserMessageId: opts?.typedCommandUserMessageId,
      })
      if (!applied) {
        appendAssistantLine(
          sportsNocFollowupFr()
            ? 'Aucune réponse du serveur — réessayez.'
            : 'No response from the server — please try again.',
        )
      }
      } finally {
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      resolvedApiBaseUrl,
      getActiveDocumentContext,
      getMedicalLinkCredentialsForApi,
      config.clientId,
      config.tenantId,
      auditSessionId,
      patientContext,
      cancelIntentNavigation,
      persistSessionChatHistoryNow,
      applyHostedChatAssistantRows,
      sportsNocFollowupFr,
      appendAssistantLine,
    ],
  )

  const fulfillPatientSwitchConfirm = useCallback(
    async (
      messageId: string,
      choice: 'yes' | 'no',
      opts?: { voiceAssistantRows?: ApiAssistantRow[]; llmTokenUsage?: unknown },
    ) => {
      if (patientSwitchConfirmInFlightRef.current) return
      if (lastHandledPatientSwitchConfirmIdRef.current === messageId) return
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (!src || src.patientSwitchConfirmResolved || !patientSwitchConfirmIsActionable(src, false)) {
        return
      }
      const confirmIdx = messagesRef.current.findIndex((m) => m.id === messageId)
      if (confirmIdx >= 0 && hasPatientSwitchUserAnswerAfter(messagesRef.current, confirmIdx)) {
        lastHandledPatientSwitchConfirmIdRef.current = messageId
        return
      }
      if (isSending) return

      patientSwitchConfirmInFlightRef.current = true
      lastHandledPatientSwitchConfirmIdRef.current = messageId
      setIsSending(true)
      const commandStartedAt = performance.now()

      const apiText = resolvePatientSwitchConfirmation(
        choice,
        isFrenchLanguage(),
      ).apiCommandText
      const bubbleText = patientSwitchConfirmBubbleText(choice)

      const marked = markAllPatientSwitchConfirmationsResolved(messagesRef.current)
      const newUserMsgId = uid()
      const withUser = chatHasPatientSwitchYesReply(marked)
        ? marked
        : appendChatUserMessages(marked, [
            {
              id: newUserMsgId,
              role: 'user',
              content: bubbleText,
              timestamp: Date.now(),
            },
          ])
      const typedCommandUserMessageId = chatHasPatientSwitchYesReply(marked)
        ? undefined
        : newUserMsgId
      markPatientSwitchUserCommitted()
      const nextHistory = dedupePatientSwitchYesUserMessages(withUser)
      messagesRef.current = nextHistory
      setMessages(nextHistory)

      try {
        const voiceRows = opts?.voiceAssistantRows ?? []
        if (
          voiceRows.length > 0 &&
          websocketFulfilledPatientSwitchConfirm(voiceRows, choice)
        ) {
          await applyHostedChatAssistantRows(voiceRows, nextHistory, {
            llmTokenUsage: opts?.llmTokenUsage,
            turnKind: 'voice',
          })
        } else {
          await runHostedChatCommand(apiText, nextHistory, {
            commandStartedAt,
            typedCommandUserMessageId,
          })
        }
      } catch {
        cancelIntentNavigation()
      } finally {
        setIsSending(false)
        patientSwitchConfirmInFlightRef.current = false
      }
    },
    [isSending, runHostedChatCommand, applyHostedChatAssistantRows, cancelIntentNavigation],
  )

  const onPatientSwitchConfirm = useCallback(
    (messageId: string, choice: 'yes' | 'no') => {
      void fulfillPatientSwitchConfirm(messageId, choice)
    },
    [fulfillPatientSwitchConfirm],
  )

  useLayoutEffect(() => {
    fulfillPatientSwitchConfirmRef.current = fulfillPatientSwitchConfirm
  }, [fulfillPatientSwitchConfirm])

  const handlePatientOptionsShowMore = useCallback(async () => {
    if (isSending) return
    const pending = findPendingPatientOptionsMessage(messagesRef.current)
    if (!pending?.patientOptionsPaging?.hasMore) return
    setIsSending(true)
    const text = 'show more'
    try {
      await runHostedChatCommand(text, messagesRef.current, { skipIntentNavigation: true })
    } catch {
      cancelIntentNavigation()
    } finally {
      setIsSending(false)
    }
  }, [isSending, runHostedChatCommand, cancelIntentNavigation])

  useLayoutEffect(() => {
    const last = messages[messages.length - 1]
    if (last?.role !== 'assistant' || !last.closeModalityPreview) return
    if (closePreviewHandledIdsRef.current.has(last.id)) return
    closePreviewHandledIdsRef.current.add(last.id)
    closeModalityPreview()
    cancelIntentNavigation()
  }, [messages, cancelIntentNavigation, closeModalityPreview])

  useEffect(() => {
    const last = messages[messages.length - 1]
    if (last?.role !== 'assistant' || !last.closeMessageThread) return
    if (closeMessageThreadHandledIdsRef.current.has(last.id)) return
    closeMessageThreadHandledIdsRef.current.add(last.id)
    emitCloseChatThread({ source: 'close-message-thread-command', messageId: last.id })
    cancelIntentNavigation()
  }, [messages, cancelIntentNavigation, emitCloseChatThread])

  const voice = useVoiceStreaming({
    websocketUrl,
    apiBaseUrl: resolvedApiBaseUrl,
    transcribeMode,
    clientId: config.clientId,
    tenantId: config.tenantId,
    sessionId: auditSessionId,
    getMedicalLinkCredentials: getMedicalLinkCredentialsForApi,
    getActiveDocumentContext,
    languageOptions: ['fr-FR'],
    preferredLanguage: 'fr-FR',
    sampleRate: 48000,
    finishingTranscriptionHint: chatUi.finishingTranscription,
    intentProcessingHint: chatUi.intentProcessing,
    onTranscriptReady: (displayText, _fullRaw, transcribeProcessingMs) => {
      const trimmed = displayText.trim()
      if (!trimmed) return
      voiceTurnDisplayTextRef.current = trimmed
      voiceTurnUserInChatRef.current = true
      setVoiceIntentProcessing(true)
      const userMsg = withVoiceTranscribeProcessing(
        {
          id: uid(),
          role: 'user',
          content: trimmed,
          timestamp: Date.now(),
        },
        transcribeProcessingMs,
      )
      setVoiceProcessingUserMessageId(userMsg.id)
      const next = appendChatUserMessages(messagesRef.current, [userMsg])
      messagesRef.current = next
      setMessages(next)
      setVoiceError(null)
    },
    onFinal: async (text, assistantMessages, llmTokenUsage, transcribeProcessingMs, intentProcessingMs) => {
      try {
      const trimmed = (voiceTurnDisplayTextRef.current || text).trim()
      const voiceUserInChat = voiceTurnUserInChatRef.current
      const voiceAssistantTiming = {
        transcribeProcessingMs: voiceUserInChat ? undefined : transcribeProcessingMs,
        intentProcessingMs,
      }
      const hasAssistant = Boolean(assistantMessages && assistantMessages.length > 0)
      const sportsReviewEditField = trimmed ? parseSportsNocReviewEditIntent(trimmed) : null

      // Per UX requirement: sports/comments edits are click-only on preview cards.
      // Ignore spoken "edit sports/comments" (no chat bubble, no assistant follow-up).
      if (sportsReviewEditField === 'sports' || sportsReviewEditField === 'comments') {
        setVoiceError(null)
        llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
        cancelIntentNavigation()
        return
      }

      if (trimmed && transcriptIsGlobalIntentCancel(trimmed) && !hasAssistant) {
        await fulfillGlobalIntentCancelRef.current({ displayText: trimmed })
        return
      }

      if (trimmed && isCloseModalityCommand(trimmed)) {
        const userMsg = withVoiceTranscribeProcessing(
          {
            id: uid(),
            role: 'user',
            content: trimmed,
            timestamp: Date.now(),
          },
          transcribeProcessingMs,
        )
        closeModalityPreview()
        setVoiceError(null)
        setIsSending(true)
        try {
          await runHostedChatCommand(trimmed, appendChatUserMessages(messagesRef.current, [userMsg]), {
            skipIntentNavigation: true,
          })
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
        return
      }

      if (trimmed && (await tryOpenSportsNocReviewEditFromTextRef.current(trimmed))) {
        setVoiceError(null)
        llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
        cancelIntentNavigation()
        return
      }

      if (!trimmed && !hasAssistant) {
        cancelIntentNavigation()
        return
      }

      // Patient list paging ("show more" / "voir plus"): in-chat merge only — never intent tabs / nav.
      if (trimmed && hasAssistant && assistantMessages) {
        const pendingPm = findPendingPatientOptionsMessage(messagesRef.current)
        if (pendingPm?.options?.length && transcriptIsShowMorePatients(trimmed)) {
          const filtered = filterAssistantRowsForPatientShowMoreCommand(
            assistantMessages as ApiAssistantRow[],
            trimmed,
            messagesRef.current,
          )
          if (filtered.length === 0) {
            cancelIntentNavigation()
            return
          }
          setVoiceError(null)
          const assistantMsgsPaging = stampVoiceAssistantProcessing(
            mapAssistantApiRows(filtered, {
              loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
              apiBaseUrl: resolvedApiBaseUrl,
            }),
            voiceAssistantTiming,
          )
          void persistServerActivePatientFromRows(filtered)
          setMessages((prev) => mergePatientOptionsRefresh(prev, assistantMsgsPaging))
          llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
          cancelIntentNavigation()
          return
        }
      }

      if (
        trimmed &&
        transcriptIsShowMorePatients(trimmed) &&
        findPendingPatientOptionsMessage(messagesRef.current)?.options?.length &&
        !hasAssistant
      ) {
        cancelIntentNavigation()
        return
      }

      if (trimmed) {
        const pendingFolder = findPendingFolderOptionsMessage(messagesRef.current)
        const folderIdx = parseOrdinalChoice(trimmed)
        if (
          pendingFolder?.options &&
          folderIdx !== null &&
          folderIdx >= 0 &&
          folderIdx < pendingFolder.options.length
        ) {
          setVoiceError(null)
          const opt = pendingFolder.options[folderIdx]
          markFolderOptionsResolved()
          llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
          void submitChatText(String(folderIdx + 1), {
            displayText: formatFolderSelectionMessage(opt, inferSelectionLangFromText(trimmed)),
          })
          cancelIntentNavigation()
          return
        }

        const pending = findPendingPatientOptionsMessage(messagesRef.current)
        const idx = parseOrdinalChoice(trimmed)
        if (
          pending?.options &&
          idx !== null &&
          idx >= 0 &&
          idx < pending.options.length
        ) {
          setVoiceError(null)
          const selectionId = pending.options[idx].selection_id
          const userMsg = withVoiceTranscribeProcessing(
            {
              id: uid(),
              role: 'user',
              content: formatPatientSelectionMessage(
                pending.options[idx],
                inferSelectionLangFromText(trimmed),
              ),
              timestamp: Date.now(),
            },
            transcribeProcessingMs,
          )
          // Server may have already resolved the ordinal via the voice pipeline; do not POST again.
          if (websocketOpenedPatientMatchingSelection(assistantMessages, selectionId)) {
            markPatientOptionsResolved()
            const assistantMsgs = stampVoiceAssistantProcessing(
              mapAssistantApiRows(assistantMessages as ApiAssistantRow[], {
                loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
                apiBaseUrl: resolvedApiBaseUrl,
              }),
              voiceAssistantTiming,
            )
            void persistServerActivePatientFromRows(assistantMessages as ApiAssistantRow[])
            setMessages((prev) => {
              const base = voiceUserInChat ? prev : appendChatUserMessages(prev, [userMsg])
              const next = mergePatientOptionsRefresh(base, assistantMsgs)
              persistSessionChatHistoryNow(next)
              return next
            })
            llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
            completeIntentNavigation(assistantMsgs)
            return
          }
          setMessages((prev) => appendChatUserMessages(prev, [userMsg]))
          llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
          void sendPatientSelectionRef.current(selectionId)
          return
        }
      }

      if (trimmed && !sportsNocShouldBypassPatientSwitch(messagesRef.current, trimmed) && !checkExamReceiptShouldBypassPatientSwitch(messagesRef.current, trimmed)) {
        const pendingMailSendVoice = findPendingMailSendConfirm(messagesRef.current)
        const switchAnswer = pendingMailSendVoice ? null : parsePatientSwitchVoiceAnswer(trimmed)
        if (switchAnswer) {
          const pendingSwitchEarly = findPendingPatientSwitchConfirm(messagesRef.current)
          const reminderAwaitingVoice = hasPendingRemindConfirmation(messagesRef.current)
          const noteAwaitingVoice = hasPendingNoteConfirmation(messagesRef.current)
          if (isPatientSwitchUserCommitted() && !pendingSwitchEarly && !reminderAwaitingVoice && !noteAwaitingVoice) {
            llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
            cancelIntentNavigation()
            return
          }
          setVoiceError(null)
          const pendingSwitch = pendingSwitchEarly
          const voiceRows = hasAssistant ? (assistantMessages as ApiAssistantRow[]) : []
          if (pendingSwitch) {
            await fulfillPatientSwitchConfirmRef.current(pendingSwitch.messageId, switchAnswer, {
              voiceAssistantRows: voiceRows,
              llmTokenUsage,
            })
            cancelIntentNavigation()
            return
          }
          if (
            voiceRows.length > 0 &&
            websocketFulfilledPatientSwitchConfirm(voiceRows, switchAnswer)
          ) {
            const bubbleText = patientSwitchConfirmBubbleText(switchAnswer)
            const marked = markAllPatientSwitchConfirmationsResolved(messagesRef.current)
            const withUser = chatHasPatientSwitchYesReply(marked)
              ? marked
              : appendChatUserMessages(marked, [
                  withVoiceTranscribeProcessing(
                    { id: uid(), role: 'user', content: bubbleText, timestamp: Date.now() },
                    transcribeProcessingMs,
                  ),
                ])
            markPatientSwitchUserCommitted()
            const historyWithUser = dedupePatientSwitchYesUserMessages(withUser)
            messagesRef.current = historyWithUser
            await applyHostedChatAssistantRows(voiceRows, historyWithUser, {
              llmTokenUsage,
              turnKind: 'voice',
              ...voiceAssistantTiming,
            })
            cancelIntentNavigation()
            return
          }
          if (
            !reminderAwaitingVoice &&
            !noteAwaitingVoice &&
            !pendingMailSendVoice &&
            !checkExamReceiptShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
            !readModalityShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
            chatHasPatientSwitchYesReply(messagesRef.current) &&
            !findPendingBinaryConfirm(messagesRef.current)
          ) {
            llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
            cancelIntentNavigation()
            return
          }
        }
      }

      if (
        trimmed &&
        !findPendingBinaryConfirm(messagesRef.current) &&
        !findPendingPatientOptionsMessage(messagesRef.current)
      ) {
        const unreadListMid = findPendingUnreadReadAloudMessageId(messagesRef.current)
        const unreadOpenIndex = unreadListMid ? parseUnreadThreadOpenIndex(trimmed) : null
        if (unreadListMid && unreadOpenIndex !== null) {
          setVoiceError(null)
          tryOpenUnreadSelection(unreadListMid, unreadOpenIndex, 'unread-voice')
          llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
          cancelIntentNavigation()
          return
        }
        if (unreadListMid && parseUnreadReadAloudIntent(trimmed)) {
          const rows = unreadRowsByMessageIdRef.current.get(unreadListMid)
          setVoiceError(null)
          const userMsg = withVoiceTranscribeProcessing(
            {
              id: uid(),
              role: 'user',
              content: trimmed,
              timestamp: Date.now(),
            },
            transcribeProcessingMs,
          )
          setMessages((prev) => appendChatUserMessages(prev, [userMsg]))
          const listFr = unreadListLanguageIsFrench(unreadListMid)
          if (rows === undefined) {
            appendAssistantLine(
              listFr
                ? 'La liste se charge encore — réessayez dans un instant.'
                : 'That list is still loading — please try again in a moment.',
            )
          } else if (rows.length === 0) {
            appendAssistantLine(
              listFr
                ? 'Il n’y a aucun message à lire dans cette liste.'
                : 'There are no messages in that list to read.',
            )
          } else {
            void readAloud.synthesizeAndPlay(segmentsFromUnreadRows(rows, listFr ? 'fr' : 'en'))
            setMessages((prev) =>
              prev.map((m) => (m.id === unreadListMid ? { ...m, unreadMessagesReadAloudPlayed: true } : m)),
            )
          }
          llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
          cancelIntentNavigation()
          return
        }
      }

      setVoiceError(null)

      const assistantMsgs: ChatMessage[] = hasAssistant
        ? stampVoiceAssistantProcessing(
            mapAssistantApiRows(assistantMessages as ApiAssistantRow[], {
              loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
              apiBaseUrl: resolvedApiBaseUrl,
            }),
            voiceAssistantTiming,
          )
        : stampVoiceAssistantProcessing(
            [
              {
                id: uid(),
                role: 'assistant',
                content: 'No assistant response was returned.',
                timestamp: Date.now(),
              },
            ],
            voiceAssistantTiming,
          )

      if (hasAssistant) {
        await persistServerActivePatientFromRows(assistantMessages as ApiAssistantRow[])
      }

      llmTokenSessionLogRef.current.logTurn('voice', llmTokenUsage)
      const assistantOpenedSecretaryInlineEdit =
        hasAssistant &&
        assistantMsgs.some((m) => m.role === 'assistant' && Boolean(m.awaitingMessageEditField))

      if (trimmed) {
        if (
          assistantOpenedSecretaryInlineEdit ||
          shouldSilenceSecretaryInlineEditTrigger(trimmed, messagesRef.current)
        ) {
          if (hasAssistant) {
            chatCommandInFlightRef.current += 1
            try {
              await applyHostedChatAssistantRows(
                assistantMessages as ApiAssistantRow[],
                messagesRef.current,
                {
                  llmTokenUsage,
                  turnKind: 'voice',
                  ...voiceAssistantTiming,
                },
              )
            } finally {
              chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
            }
          } else {
            await runHostedChatCommand(trimmed, messagesRef.current, { skipIntentNavigation: true })
          }
          cancelIntentNavigation()
          return
        }
        const pendingShowMore = findPendingPatientOptionsMessage(messagesRef.current)
        if (
          !findPendingMailSendConfirm(messagesRef.current) &&
          !sportsNocShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
          !checkExamReceiptShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
          !readModalityShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
          parsePatientSwitchVoiceAnswer(trimmed) &&
          !hasPendingRemindConfirmation(messagesRef.current) &&
          !hasPendingNoteConfirmation(messagesRef.current) &&
          (chatHasPatientSwitchYesReply(messagesRef.current) || isPatientSwitchUserCommitted()) &&
          !findPendingBinaryConfirm(messagesRef.current)
        ) {
          cancelIntentNavigation()
          return
        }
        if (pendingShowMore?.options?.length && transcriptIsShowMorePatients(trimmed)) {
          setMessages((prev) => {
            const next = mergePatientOptionsRefresh(prev, assistantMsgs)
            persistSessionChatHistoryNow(next)
            return next
          })
        } else {
          let historyWithUser = messagesRef.current
          if (!voiceUserInChat) {
            const userMsg = withVoiceTranscribeProcessing(
              {
                id: uid(),
                role: 'user',
                content: trimmed,
                timestamp: Date.now(),
              },
              transcribeProcessingMs,
            )
            historyWithUser = markSportsNocYesNoResolvedInMessages(
              dedupePatientSwitchYesUserMessages(appendChatUserMessages(messagesRef.current, [userMsg])),
            )
            messagesRef.current = historyWithUser
            setMessages(historyWithUser)
          }
          if (hasAssistant) {
            chatCommandInFlightRef.current += 1
            try {
              await applyHostedChatAssistantRows(
                assistantMessages as ApiAssistantRow[],
                historyWithUser,
                {
                  llmTokenUsage,
                  turnKind: 'voice',
                  ...voiceAssistantTiming,
                },
              )
            } finally {
              chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
            }
          }
        }
      } else {
        setMessages((prev) => {
          const next = mergePatientOptionsRefresh(prev, assistantMsgs)
          persistSessionChatHistoryNow(next)
          return next
        })
      }
      if (transcriptIsShowMorePatients(trimmed) && findPendingPatientOptionsMessage(messagesRef.current)) {
        cancelIntentNavigation()
      } else {
        completeIntentNavigation(assistantMsgs)
      }
      } finally {
        voiceTurnUserInChatRef.current = false
        setVoiceProcessingUserMessageId(null)
        voiceTurnDisplayTextRef.current = ''
        setVoiceIntentProcessing(false)
      }
    },
    onError: (message) => {
      voiceTurnUserInChatRef.current = false
      setVoiceProcessingUserMessageId(null)
      voiceTurnDisplayTextRef.current = ''
      setVoiceIntentProcessing(false)
      cancelIntentNavigation()
      setVoiceError(message)
      const errMsg: ChatMessage = {
        id: uid(),
        role: 'assistant',
        content: `Voice transcription failed: ${message}`,
        timestamp: Date.now(),
      }
      setMessages((prev) => [...prev, errMsg])
    },
  })

  const gttsTest = useGttsSpeakAndTranscribe({
    apiBaseUrl: resolvedApiBaseUrl,
    micStatus: voice.status,
    onStartMic: voice.startRecording,
    onStopMic: voice.stopRecording,
  })

  const onGttsClick = useCallback(() => {
    gttsTest.clearGttsError()
    void gttsTest.speakAndTranscribe(draft)
  }, [draft, gttsTest.clearGttsError, gttsTest.speakAndTranscribe])

  /** Suspended while sending or during an active voice session so Web Speech does not fight streaming. */
  const wakeListenSuspended =
    isSending ||
    voice.status === 'requesting_permission' ||
    voice.status === 'connecting' ||
    voice.status === 'recording' ||
    voice.status === 'transcribing'

  const onWakeHeyMedico = useCallback(() => {
    setVoiceError(null)
    voice.toggleMic()
  }, [voice.toggleMic])

  const wakePhrase = useWakePhraseRecognition({
    suspend: wakeListenSuspended,
    panelOpen: open,
    onOpenAssistant: openPanel,
    onCloseAssistant: closePanel,
    onHeyMedico: onWakeHeyMedico,
  })

  const theme = useMemo<WidgetTheme>(() => {
    return { ...defaultTheme, ...(config.theme || {}) }
  }, [config.theme])

  const statusLine = (() => {
    if (isSending) return chatUi.sending
    if (gttsTest.gttsError) return gttsTest.gttsError
    if (voiceError) return voiceError
    return ''
  })()
  const activePatientFromMessages = useMemo(() => {
    const sessionName = (
      sessionActivePatient?.patient_name ||
      sessionServerActivePatientName ||
      ''
    ).trim()
    if (sessionName) return { name: sessionName, blockedByNoActiveSignal: false }
    const chatSignal = latestActivePatientSignalFromChatMessages(messages)
    if (chatSignal.name || chatSignal.blockedByNoActiveSignal) return chatSignal
    return { name: '', blockedByNoActiveSignal: false }
  }, [messages, sessionActivePatient, sessionServerActivePatientName])
  const activePatientName = activePatientFromMessages.blockedByNoActiveSignal
    ? ''
    : activePatientFromMessages.name
  const activePatientDisplay = activePatientName || chatUi.noPatientSelected

  const mainVoiceOccupiesMic =
    voice.status === 'recording' ||
    voice.status === 'connecting' ||
    voice.status === 'requesting_permission' ||
    voice.status === 'transcribing'
  const micDisabled =
    isSending ||
    gttsTest.gttsBusy ||
    voice.status === 'transcribing' ||
    reminderInlineVoiceBusy ||
    noteInlineVoiceBusy ||
    secretaryInlineVoiceBusy ||
    sportsNocInlineVoiceBusy

  const notePreviewVoiceBlocked =
    isSending ||
    mainVoiceOccupiesMic ||
    reminderInlineVoiceBusy ||
    secretaryInlineVoiceBusy ||
    sportsNocInlineVoiceBusy ||
    mailDocumentInlineVoiceBusy

  const position = config.position ?? 'bottom-right'

  const scrollToBottom = (behavior: ScrollBehavior) => {
    const end = messageEndRef.current
    if (!end) return
    end.scrollIntoView({ behavior, block: 'end' })
  }

  useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, closePanel])

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => scrollToBottom('auto'))
  }, [open])

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]?.id : ''
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => scrollToBottom('smooth'))
  }, [messages.length, lastMessageId, open])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || typeof data !== 'object') return
      const payload = data as { type?: unknown; data?: unknown }
      if (payload.type === 'OPEN_CHAT_CREATED_WITH_ID') {
        console.debug('[VoiceNav] received OPEN_CHAT_CREATED_WITH_ID on window', payload.data)
        return
      }
      if (payload.type === 'CLOSE_CHAT_THREAD') {
        console.debug('[VoiceNav] received CLOSE_CHAT_THREAD on window', payload.data)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    for (const m of messages) {
      if (m.role !== 'assistant' || m.historySnapshot) continue
      const sm = m.secretaryMessage
      if (!sm) continue
      if ((sm.message_sent_status || '').trim().toLowerCase() !== 'success') continue
      const discussionId = (sm.discussion_id || '').trim()
      if (!discussionId) continue
      if (postedCreatedDiscussionMessageIdsRef.current.has(m.id)) continue
      postedCreatedDiscussionMessageIdsRef.current.add(m.id)
      const messageText = (sm.message || '').trim()
      try {
        emitOpenChatCreatedWithId(discussionId, messageText, {
          source: 'message-intent-success',
          messageId: m.id,
        })
      } catch (error) {
        postedCreatedDiscussionMessageIdsRef.current.delete(m.id)
        console.error('[VoiceNav] Failed to handle message intent thread open', { messageId: m.id, error })
      }
    }
  }, [emitOpenChatCreatedWithId, messages])

  const markPatientOptionsResolved = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uiType === 'patient_options' && !m.patientOptionsResolved
          ? { ...m, patientOptionsResolved: true }
          : m,
      ),
    )
  }, [])

  const markFolderOptionsResolved = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uiType === 'folder_options' && !m.folderOptionsResolved
          ? { ...m, folderOptionsResolved: true }
          : m,
      ),
    )
  }, [])

  const markAssigneeOptionsResolved = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uiType === 'assignee_options' && !m.assigneeOptionsResolved
          ? { ...m, assigneeOptionsResolved: true }
          : m,
      ),
    )
  }, [])

  const markRecipientTypeOptionsResolved = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uiType === 'recipient_type_options' && !m.recipientTypeOptionsResolved
          ? { ...m, recipientTypeOptionsResolved: true }
          : m,
      ),
    )
  }, [])

  const markDeliveryChannelOptionsResolved = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.uiType === 'delivery_channel_options' && !m.deliveryChannelOptionsResolved
          ? { ...m, deliveryChannelOptionsResolved: true }
          : m,
      ),
    )
  }, [])

  const sendPatientSelection = useCallback(
    async (selectionId: string) => {
      const sid = selectionId.trim()
      if (!sid || isSending) return

      setIsSending(true)
      try {
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'patient_selection',
            selected_patient_id: sid,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        markPatientOptionsResolved()
        const rows = data.assistant_messages ?? []
        const assistantMsgs = mapAssistantApiRows(rows, {
          loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
          apiBaseUrl: resolvedApiBaseUrl,
        })
        void persistServerActivePatientFromRows(rows)
        setMessages((prev) => {
          const next = mergePatientOptionsRefresh(prev, assistantMsgs)
          persistSessionChatHistoryNow(next)
          return next
        })
        llmTokenSessionLogRef.current.logTurn('patient_selection', data.llm_token_usage)
        completeIntentNavigation(assistantMsgs)
      } catch {
        cancelIntentNavigation()
        markPatientOptionsResolved()
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: 'Could not confirm patient selection. Please try again.',
            timestamp: Date.now(),
          },
        ])
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      markPatientOptionsResolved,
      completeIntentNavigation,
      cancelIntentNavigation,
      persistServerActivePatientFromRows,
    ],
  )

  const sendFolderSelection = useCallback(
    async (folderSlug: string) => {
      const slug = folderSlug.trim()
      if (!slug || isSending) return

      setIsSending(true)
      try {
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'folder_selection',
            selected_folder_slug: slug,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        markFolderOptionsResolved()
        const rows = data.assistant_messages ?? []
        const assistantMsgs = mapAssistantApiRows(rows, {
          loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
          apiBaseUrl: resolvedApiBaseUrl,
        })
        setMessages((prev) => {
          const next = [...prev, ...assistantMsgs]
          persistSessionChatHistoryNow(next)
          return next
        })
        llmTokenSessionLogRef.current.logTurn('folder_selection', data.llm_token_usage)
        completeIntentNavigation(assistantMsgs)
      } catch {
        cancelIntentNavigation()
        markFolderOptionsResolved()
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: sportsNocFollowupFr()
              ? 'Impossible de confirmer ce document. Réessayez.'
              : 'Could not confirm that document. Please try again.',
            timestamp: Date.now(),
          },
        ])
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      markFolderOptionsResolved,
      completeIntentNavigation,
      cancelIntentNavigation,
      sportsNocFollowupFr,
    ],
  )

  const sendAssigneeSelection = useCallback(
    async (selectionId: string) => {
      const sid = selectionId.trim()
      if (!sid || isSending) return false

      setIsSending(true)
      try {
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'assignee_selection',
            selected_assignee_id: sid,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        markAssigneeOptionsResolved()
        const rows = data.assistant_messages ?? []
        const assistantMsgs = mapAssistantApiRows(rows, {
          loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
          apiBaseUrl: resolvedApiBaseUrl,
        })
        setMessages((prev) => {
          const next = [
            ...prev.map((m) =>
              m.uiType === 'assignee_options' && !m.assigneeOptionsResolved
                ? { ...m, assigneeOptionsResolved: true }
                : m,
            ),
            ...assistantMsgs,
          ]
          persistSessionChatHistoryNow(next)
          return next
        })
        llmTokenSessionLogRef.current.logTurn('assignee_selection', data.llm_token_usage)
        completeIntentNavigation(assistantMsgs)
        return true
      } catch {
        cancelIntentNavigation()
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: 'Could not confirm recipient selection. Please try again.',
            timestamp: Date.now(),
          },
        ])
        return false
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      markAssigneeOptionsResolved,
      completeIntentNavigation,
      cancelIntentNavigation,
    ],
  )

  const activeDocumentChatPayload = useCallback(() => {
    const docCtx = getActiveDocumentContext()
    const hasOpenDoc = Boolean(docCtx.id?.trim() || docCtx.url?.trim())
    if (!hasOpenDoc) return {}
    return {
      active_document_id: docCtx.id?.trim() || undefined,
      active_document_name: docCtx.name?.trim() || undefined,
      active_document_url: docCtx.url?.trim() || undefined,
      ...(docCtx.mime?.trim() ? { active_document_mime_type: docCtx.mime.trim() } : {}),
      ...(docCtx.examDate?.trim() ? { active_document_exam_date: docCtx.examDate.trim() } : {}),
      ...(docCtx.folderName?.trim() ? { active_document_folder: docCtx.folderName.trim() } : {}),
    }
  }, [getActiveDocumentContext])

  const sendRecipientTypeSelection = useCallback(
    async (selectionId: string) => {
      const sid = selectionId.trim()
      if (!sid || isSending) return false

      setIsSending(true)
      try {
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'recipient_type_selection',
            selected_recipient_type_id: sid,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        markRecipientTypeOptionsResolved()
        const rows = data.assistant_messages ?? []
        const assistantMsgs = mapAssistantApiRows(rows, {
          loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
          apiBaseUrl: resolvedApiBaseUrl,
        })
        setMessages((prev) => {
          const next = [
            ...prev.map((m) =>
              m.uiType === 'recipient_type_options' && !m.recipientTypeOptionsResolved
                ? { ...m, recipientTypeOptionsResolved: true }
                : m,
            ),
            ...assistantMsgs,
          ]
          persistSessionChatHistoryNow(next)
          return next
        })
        llmTokenSessionLogRef.current.logTurn('recipient_type_selection', data.llm_token_usage)
        completeIntentNavigation(assistantMsgs)
        return true
      } catch {
        cancelIntentNavigation()
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: 'Could not confirm recipient type. Please try again.',
            timestamp: Date.now(),
          },
        ])
        return false
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      markRecipientTypeOptionsResolved,
      completeIntentNavigation,
      cancelIntentNavigation,
    ],
  )

  const sendDeliveryChannelSelection = useCallback(
    async (selectionId: string) => {
      const sid = selectionId.trim()
      if (!sid || isSending) return false

      const pendingMsg = [...messagesRef.current]
        .reverse()
        .find(
          (m) =>
            m.uiType === 'delivery_channel_options' && !m.deliveryChannelOptionsResolved,
        )
      const attachmentDeliveryPending = pendingMsg?.attachmentDeliveryPending

      setIsSending(true)
      try {
        const creds = getMedicalLinkCredentialsForApi()
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(creds.piBearerToken.trim()
              ? { Authorization: `Bearer ${creds.piBearerToken.trim()}` }
              : {}),
          },
          body: JSON.stringify({
            type: 'delivery_channel_selection',
            selected_delivery_channel_id: sid,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(creds),
            ...activeDocumentChatPayload(),
            ...(attachmentDeliveryPending
              ? { attachment_delivery_pending: attachmentDeliveryPending }
              : {}),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        markDeliveryChannelOptionsResolved()
        const rows = data.assistant_messages ?? []
        const resolvedBase = messagesRef.current.map((m) =>
          m.uiType === 'delivery_channel_options' && !m.deliveryChannelOptionsResolved
            ? { ...m, deliveryChannelOptionsResolved: true }
            : m,
        )
        messagesRef.current = resolvedBase
        setMessages(resolvedBase)
        const applied = await applyHostedChatAssistantRows(rows, resolvedBase, {
          turnKind: 'typed',
          llmTokenUsage: data.llm_token_usage,
        })
        if (!applied && rows.length > 0) {
          const assistantMsgs = mapAssistantApiRows(rows, {
            loggedUserId: getMedicalLinkCredentialsForApi().loggedUserId,
            apiBaseUrl: resolvedApiBaseUrl,
          })
          const next = [...resolvedBase, ...assistantMsgs]
          messagesRef.current = next
          setMessages(next)
          persistSessionChatHistoryNow(next)
          completeIntentNavigation(assistantMsgs)
        }
        if (!applied) {
          llmTokenSessionLogRef.current.logTurn('delivery_channel_selection', data.llm_token_usage)
        }
        return true
      } catch {
        cancelIntentNavigation()
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: 'Could not confirm delivery channel. Please try again.',
            timestamp: Date.now(),
          },
        ])
        return false
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      activeDocumentChatPayload,
      markDeliveryChannelOptionsResolved,
      applyHostedChatAssistantRows,
      completeIntentNavigation,
      cancelIntentNavigation,
      persistSessionChatHistoryNow,
    ],
  )

  const sendSecretaryUrgentToggle = useCallback(
    async (urgent: boolean) => {
      chatCommandInFlightRef.current += 1
      try {
        const res = await fetch(buildVoiceNavUrl(resolvedApiBaseUrl, 'chatCommand'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'secretary_urgent_toggle',
            secretary_urgent: urgent,
            session_id: auditSessionId,
            ...(config.tenantId?.trim() ? { tenant_id: config.tenantId.trim() } : {}),
            ...medicalLinkCredentialsToApiPayload(getMedicalLinkCredentialsForApi()),
          }),
        })
        if (!res.ok) {
          throw new Error(`chat/command HTTP ${res.status}`)
        }
        const data = (await res.json()) as {
          assistant_messages?: ApiAssistantRow[]
          llm_token_usage?: unknown
        }
        const rows = data.assistant_messages ?? []
        if (rows.length > 0) {
          await applyHostedChatAssistantRows(rows, messagesRef.current, { skipIntentNavigation: true })
        }
        llmTokenSessionLogRef.current.logTurn('secretary_urgent_toggle', data.llm_token_usage)
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] secretary urgent toggle failed', error)
        cancelIntentNavigation()
        return false
      } finally {
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      resolvedApiBaseUrl,
      auditSessionId,
      config.tenantId,
      getMedicalLinkCredentialsForApi,
      applyHostedChatAssistantRows,
      cancelIntentNavigation,
    ],
  )

  const sendSportsNocLocationSelection = useCallback(
    async (locationId: number) => {
      if (!Number.isFinite(locationId) || locationId <= 0 || isSending) return false
      chatCommandInFlightRef.current += 1
      setIsSending(true)
      try {
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action: 'select_location',
            location_id: locationId,
          },
        )
        if (!result?.ok) {
          throw new Error(result?.error || 'workflow_action_failed')
        }
        const rows = (result.assistant_messages ?? []) as ApiAssistantRow[]
        if (rows.length > 0) {
          await applyHostedChatAssistantRows(rows, messagesRef.current, { skipIntentNavigation: true })
        }
        llmTokenSessionLogRef.current.logTurn('sports_noc_location_selection', undefined)
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports NOC location selection failed', error)
        cancelIntentNavigation()
        return false
      } finally {
        setIsSending(false)
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      applyHostedChatAssistantRows,
      cancelIntentNavigation,
    ],
  )

  const sendSportsNocSportToggle = useCallback(
    async (fieldId: number) => {
      if (!Number.isFinite(fieldId) || fieldId <= 0) return false
      const inlineSportsEdit = sportsNocSummaryInlineEditRef.current?.field === 'sports'
      if (isSending && !inlineSportsEdit) return false

      if (inlineSportsEdit) {
        setSportsNocSummaryInlineEdit((prev) => {
          if (!prev || prev.field !== 'sports') return prev
          const cur = new Set(prev.pendingSportIds)
          if (cur.has(fieldId)) cur.delete(fieldId)
          else cur.add(fieldId)
          return { ...prev, pendingSportIds: [...cur], error: null }
        })
      }

      if (!inlineSportsEdit) {
        chatCommandInFlightRef.current += 1
        setIsSending(true)
      }
      try {
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action: 'toggle_sport',
            field_id: fieldId,
          },
        )
        if (!result?.ok) {
          throw new Error(result?.error || 'workflow_action_failed')
        }
        if (inlineSportsEdit) {
          const pending = result?.pending_selected_sport_ids
          if (Array.isArray(pending)) {
            setSportsNocSummaryInlineEdit((prev) =>
              prev && prev.field === 'sports'
                ? {
                    ...prev,
                    pendingSportIds: pending.filter((n) => Number.isFinite(n) && n > 0),
                    error: null,
                  }
                : prev,
            )
          }
        } else {
          const rows = (result.assistant_messages ?? []) as ApiAssistantRow[]
          if (rows.length > 0) {
            await applyHostedChatAssistantRows(rows, messagesRef.current, { skipIntentNavigation: true })
          }
        }
        llmTokenSessionLogRef.current.logTurn('sports_noc_sport_toggle', undefined)
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports NOC sport toggle failed', error)
        if (inlineSportsEdit) {
          setSportsNocSummaryInlineEdit((prev) =>
            prev && prev.field === 'sports'
              ? {
                  ...prev,
                  error: sportsNocFollowupFr()
                    ? 'Impossible de mettre à jour la sélection.'
                    : 'Could not update selection.',
                }
              : prev,
          )
        } else {
          cancelIntentNavigation()
        }
        return false
      } finally {
        if (!inlineSportsEdit) {
          setIsSending(false)
          chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
        }
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      applyHostedChatAssistantRows,
      cancelIntentNavigation,
      sportsNocFollowupFr,
    ],
  )

  const sendSportsNocSportSubmit = useCallback(
    async (opts?: { force?: boolean }) => {
      if (isSending && !opts?.force) return false
      chatCommandInFlightRef.current += 1
      setIsSending(true)
      try {
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action: 'submit_sports',
          },
        )
        if (!result?.ok) {
          throw new Error(result?.error || 'workflow_action_failed')
        }
        const rows = (result.assistant_messages ?? []) as ApiAssistantRow[]
        if (rows.length > 0) {
          await applyHostedChatAssistantRows(rows, messagesRef.current, { skipIntentNavigation: true })
        }
        if (result?.workflow?.active === true) {
          sportsNocBackendActiveRef.current = result.workflow.use_hosted_chat !== false
        }
        llmTokenSessionLogRef.current.logTurn('sports_noc_sport_submit', undefined)
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports NOC sport submit failed', error)
        cancelIntentNavigation()
        return false
      } finally {
        setIsSending(false)
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      applyHostedChatAssistantRows,
      cancelIntentNavigation,
    ],
  )

  const sendSportsNocCommentsSubmit = useCallback(
    async (text: string) => {
      if (isSending) return false
      chatCommandInFlightRef.current += 1
      setIsSending(true)
      try {
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action: 'submit_comments',
            comments_text: text,
          },
        )
        if (!result?.ok) {
          throw new Error(result?.error || 'workflow_action_failed')
        }
        const rows = (result.assistant_messages ?? []) as ApiAssistantRow[]
        if (rows.length > 0) {
          await applyHostedChatAssistantRows(rows, messagesRef.current, { skipIntentNavigation: true })
        }
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports NOC comments submit failed', error)
        cancelIntentNavigation()
        return false
      } finally {
        setIsSending(false)
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      applyHostedChatAssistantRows,
      cancelIntentNavigation,
    ],
  )

  const openSportsNocSummaryInlineEdit = useCallback(
    async (
      messageId: string,
      action: 'edit_location' | 'edit_sports' | 'edit_comments',
      opts?: { force?: boolean },
    ) => {
      if (isSending && !opts?.force) return false
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (
        !src?.sportsNocSummary ||
        src.historySnapshot ||
        src.sportsNocFlowCancelled
      ) {
        return false
      }
      chatCommandInFlightRef.current += 1
      setIsSending(true)
      try {
        const result = await postVoiceNavWorkflowAction(
          resolvedApiBaseUrl,
          getMedicalLinkCredentialsForApi(),
          {
            workflow: 'sports_noc',
            action,
            inline: true,
          },
        )
        if (!result?.ok) {
          throw new Error(result?.error || 'workflow_action_failed')
        }
        const ctx = normalizeSportsNocEditContextFromApi(result.edit_context)
        if (!ctx) {
          throw new Error('sports_noc_edit_context_missing')
        }
        const field = action.replace('edit_', '') as SportsNocSummaryEditField
        setSportsNocSummaryInlineEdit({
          messageId,
          field,
          locationOptions: ctx.locationOptions,
          sportOptions: ctx.sportOptions,
          pendingSportIds: ctx.pendingSelectedSportIds,
          locationDraftId:
            ctx.currentLocationId && ctx.currentLocationId > 0
              ? ctx.currentLocationId
              : src.sportsNocSummary.locationId || undefined,
          commentsDraft:
            ctx.currentComments != null
              ? String(ctx.currentComments)
              : src.sportsNocSummary.comments != null
                ? String(src.sportsNocSummary.comments)
                : '',
          saving: false,
          error: null,
        })
        if (result?.workflow?.active === true) {
          sportsNocBackendActiveRef.current = result.workflow.use_hosted_chat !== false
        }
        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceNav] sports NOC inline edit open failed', { action, error })
        cancelIntentNavigation()
        return false
      } finally {
        setIsSending(false)
        chatCommandInFlightRef.current = Math.max(0, chatCommandInFlightRef.current - 1)
      }
    },
    [
      isSending,
      resolvedApiBaseUrl,
      getMedicalLinkCredentialsForApi,
      cancelIntentNavigation,
    ],
  )

  const onSportsNocSummaryInlineCancel = useCallback(async () => {
    const edit = sportsNocSummaryInlineEditRef.current
    if (!edit || edit.saving || isSending) return
    setSportsNocSummaryInlineEdit({ ...edit, saving: true, error: null })
    try {
      const result = await postVoiceNavWorkflowAction(
        resolvedApiBaseUrl,
        getMedicalLinkCredentialsForApi(),
        { workflow: 'sports_noc', action: 'cancel_summary_edit' },
      )
      const summary = normalizeSportsNocSummaryFromApi(result?.summary)
      if (summary) {
        const next = messagesRef.current.map((m) =>
          m.id === edit.messageId ? { ...m, sportsNocSummary: summary } : m,
        )
        messagesRef.current = next
        setMessages(next)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[VoiceNav] sports NOC inline edit cancel failed', error)
    } finally {
      setSportsNocInlineVoiceBusy(false)
      setSportsNocSummaryInlineEdit(null)
    }
  }, [isSending, resolvedApiBaseUrl, getMedicalLinkCredentialsForApi])

  const onSportsNocSummaryInlineSave = useCallback(async () => {
    const edit = sportsNocSummaryInlineEditRef.current
    if (!edit || edit.saving || isSending) return
    const fr = sportsNocFollowupFr()
    if (edit.field === 'location' && (!edit.locationDraftId || edit.locationDraftId <= 0)) {
      setSportsNocSummaryInlineEdit({
        ...edit,
        error: fr ? 'Choisissez une localisation.' : 'Choose a location.',
      })
      return
    }
    if (edit.field === 'sports' && edit.pendingSportIds.length === 0) {
      setSportsNocSummaryInlineEdit({
        ...edit,
        error: fr ? 'Sélectionnez au moins un sport.' : 'Select at least one sport.',
      })
      return
    }
    setSportsNocSummaryInlineEdit({ ...edit, saving: true, error: null })
    try {
      if (edit.field === 'location') {
        const ok = await sendSportsNocLocationSelection(edit.locationDraftId!)
        if (!ok) throw new Error('location_save_failed')
      } else if (edit.field === 'sports') {
        const ok = await sendSportsNocSportSubmit({ force: true })
        if (!ok) throw new Error('sports_save_failed')
      } else {
        const text = edit.commentsDraft.trim()
        const ok = await sendSportsNocCommentsSubmit(text || (fr ? 'non' : 'no'))
        if (!ok) throw new Error('comments_save_failed')
      }
      setSportsNocSummaryInlineEdit(null)
      setSportsNocInlineVoiceBusy(false)
    } catch {
      setSportsNocSummaryInlineEdit({
        ...edit,
        saving: false,
        error: fr ? 'Enregistrement impossible. Réessayez.' : 'Could not save. Try again.',
      })
    }
  }, [
    isSending,
    sportsNocFollowupFr,
    sendSportsNocLocationSelection,
    sendSportsNocSportSubmit,
    sendSportsNocCommentsSubmit,
  ])

  const tryOpenSportsNocReviewEditFromText = useCallback(
    async (text: string) => {
      const actionFor = (field: 'location' | 'sports' | 'comments') =>
        sportsNocReviewEditWorkflowAction(field)
      const tryOpen = async () => {
        const field = parseSportsNocReviewEditIntent(text)
        if (!field) return false
        let messageId: string | null = null
        for (let i = messagesRef.current.length - 1; i >= 0; i--) {
          const m = messagesRef.current[i]
          if (
            m.role === 'assistant' &&
            !m.historySnapshot &&
            !m.sportsNocFlowCancelled &&
            Boolean(m.sportsNocSummary)
          ) {
            messageId = m.id
            break
          }
        }
        if (!messageId) return false
        return await openSportsNocSummaryInlineEdit(messageId, actionFor(field), { force: true })
      }

      // In voice turns, the preview row can land slightly after transcript finalization.
      // Retry briefly so first utterance ("edit the sports/comments") opens inline reliably.
      for (let attempt = 0; attempt < 6; attempt++) {
        if (attempt > 0) {
          await new Promise<void>((resolve) => {
            window.setTimeout(() => resolve(), 140)
          })
        }
        if (await tryOpen()) return true
      }
      return false
    },
    [openSportsNocSummaryInlineEdit],
  )

  useLayoutEffect(() => {
    openSportsNocSummaryInlineEditRef.current = openSportsNocSummaryInlineEdit
    tryOpenSportsNocReviewEditFromTextRef.current = tryOpenSportsNocReviewEditFromText
  }, [openSportsNocSummaryInlineEdit, tryOpenSportsNocReviewEditFromText])

  useEffect(() => {
    sendPatientSelectionRef.current = sendPatientSelection
  }, [sendPatientSelection])

  const handlePatientCardSelect = useCallback(
    (selectionId: string) => {
      const pending = findPendingPatientOptionsMessage(messagesRef.current)
      const opts = pending?.options
      const idx = opts?.findIndex((o) => o.selection_id === selectionId) ?? -1
      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content:
          idx >= 0 && opts?.[idx]
            ? formatPatientSelectionMessage(opts[idx], selectionLangForUiClick())
            : formatPatientSelectionMessage(undefined, selectionLangForUiClick()),
        timestamp: Date.now(),
      }
      setMessages((prev) => appendChatUserMessages(prev, [userMsg]))
      void sendPatientSelection(selectionId)
    },
    [sendPatientSelection],
  )

  const handleAssigneeCardSelect = useCallback(
    async (selectionId: string) => {
      const ok = await sendAssigneeSelection(selectionId)
      if (ok && secretaryInlineEdit?.field === 'recipient_name') {
        setSecretaryInlineVoiceBusy(false)
        setSecretaryInlineEdit(null)
      }
      if (ok && mailDocumentInlineEdit?.field === 'recipient_name') {
        setMailDocumentInlineVoiceBusy(false)
        setMailDocumentInlineEdit(null)
      }
    },
    [sendAssigneeSelection, secretaryInlineEdit, mailDocumentInlineEdit],
  )

  const handleRecipientTypeCardSelect = useCallback(
    async (selectionId: string) => {
      await sendRecipientTypeSelection(selectionId)
    },
    [sendRecipientTypeSelection],
  )

  const handleDeliveryChannelCardSelect = useCallback(
    async (selectionId: string) => {
      await sendDeliveryChannelSelection(selectionId)
    },
    [sendDeliveryChannelSelection],
  )

  const fulfillGlobalIntentCancel = useCallback(
    async (opts?: { displayText?: string }) => {
      if (isSending) return
      const fr =
        isFrenchLanguage()
      const bubbleText = (opts?.displayText ?? globalIntentCancelDisplayText()).trim()
      setIsSending(true)
      const commandStartedAt = performance.now()

      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: bubbleText || globalIntentCancelDisplayText(),
        timestamp: Date.now(),
      }
      const nextHistory = appendChatUserMessages(messagesRef.current, [userMsg])
      messagesRef.current = nextHistory
      setMessages(nextHistory)

      try {
        const creds = getMedicalLinkCredentialsForApi()
        let serverApplied = false

        try {
          const result = await postVoiceNavWorkflowAction(resolvedApiBaseUrl, creds, {
            workflow: 'session',
            action: 'cancel_intent',
            language: fr ? 'french' : 'english',
          })
          if (result?.ok) {
            const rows = (result.assistant_messages ?? []) as ApiAssistantRow[]
            if (rows.length > 0) {
              await applyHostedChatAssistantRows(rows, nextHistory, {
                skipIntentNavigation: true,
                turnKind: 'typed',
                commandStartedAt,
                typedCommandUserMessageId: userMsg.id,
              })
              serverApplied = true
            }
          }
        } catch {
          // Try /chat/command next, then local fallback.
        }

        if (!serverApplied) {
          try {
            await runHostedChatCommand(globalIntentCancelCommandText(), nextHistory, {
              skipIntentNavigation: true,
              commandStartedAt,
              typedCommandUserMessageId: userMsg.id,
            })
            serverApplied = true
          } catch {
            // Local fallback below.
          }
        }

        if (!serverApplied) {
          setMessages((prev) => {
            const next = applyClientSideGlobalIntentCancel(prev, { fr })
            messagesRef.current = next
            persistSessionChatHistoryNow(next)
            return next
          })
          cancelIntentNavigation()
        }
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      getMedicalLinkCredentialsForApi,
      resolvedApiBaseUrl,
      applyHostedChatAssistantRows,
      runHostedChatCommand,
      persistSessionChatHistoryNow,
      cancelIntentNavigation,
    ],
  )

  useLayoutEffect(() => {
    fulfillGlobalIntentCancelRef.current = fulfillGlobalIntentCancel
  }, [fulfillGlobalIntentCancel])

  const submitChatText = useCallback(
    async (text: string, opts?: { displayText?: string }) => {
      const trimmed = text.trim()
      if (!trimmed || isSending) return
      const bubbleText = (opts?.displayText ?? trimmed).trim() || trimmed

      if (transcriptIsGlobalIntentCancel(trimmed)) {
        await fulfillGlobalIntentCancel({ displayText: bubbleText })
        return
      }

      if (isCloseModalityCommand(trimmed)) {
        const commandStartedAt = performance.now()
        const userMsg: ChatMessage = {
          id: uid(),
          role: 'user',
          content: bubbleText,
          timestamp: Date.now(),
        }
        closeModalityPreview()
        setIsSending(true)
        try {
          await runHostedChatCommand(trimmed, appendChatUserMessages(messagesRef.current, [userMsg]), {
            skipIntentNavigation: true,
            commandStartedAt,
            typedCommandUserMessageId: userMsg.id,
          })
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      const pendingMailSend = findPendingMailSendConfirm(messagesRef.current)
      const pendingCheckExamReceiptOpen = findPendingCheckExamReceiptConfirm(messagesRef.current)
      const checkExamReceiptOpenAnswer = parseCheckExamReceiptOpenConfirmAnswer(trimmed)
      if (pendingCheckExamReceiptOpen && checkExamReceiptOpenAnswer !== null) {
        const commandStartedAt = performance.now()
        setIsSending(true)
        const userMsg: ChatMessage = {
          id: uid(),
          role: 'user',
          content: bubbleText,
          timestamp: Date.now(),
        }
        const nextHistory = appendChatUserMessages(messagesRef.current, [userMsg])
        messagesRef.current = nextHistory
        setMessages(nextHistory)
        try {
          await runHostedChatCommand(trimmed, nextHistory, {
            commandStartedAt,
            typedCommandUserMessageId: userMsg.id,
          })
          setMessages((prev) =>
            markCheckExamReceiptConfirmResolved(prev, pendingCheckExamReceiptOpen.id),
          )
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      const pendingReadModality = findPendingReadModalityConfirm(messagesRef.current)
      const readModalityAnswer = parseReadModalityConfirmAnswer(trimmed)
      if (pendingReadModality && readModalityAnswer !== null) {
        const commandStartedAt = performance.now()
        setIsSending(true)
        const userMsg: ChatMessage = {
          id: uid(),
          role: 'user',
          content: bubbleText,
          timestamp: Date.now(),
        }
        const nextHistory = appendChatUserMessages(messagesRef.current, [userMsg])
        messagesRef.current = nextHistory
        setMessages(nextHistory)
        try {
          await runHostedChatCommand(trimmed, nextHistory, {
            commandStartedAt,
            typedCommandUserMessageId: userMsg.id,
          })
          setMessages((prev) => markReadModalityConfirmResolved(prev, pendingReadModality.id))
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      const pendingNoteConfirm = findPendingNoteConfirm(messagesRef.current)
      const noteConfirmAnswer = parseNoteConfirmAnswer(trimmed) ?? parseNoteConfirmAnswer(bubbleText)
      if (pendingNoteConfirm && noteConfirmAnswer !== null) {
        const commandStartedAt = performance.now()
        setIsSending(true)
        const userMsg: ChatMessage = {
          id: uid(),
          role: 'user',
          content: bubbleText,
          timestamp: Date.now(),
        }
        const nextHistory = appendChatUserMessages(messagesRef.current, [userMsg])
        messagesRef.current = nextHistory
        setMessages(nextHistory)
        try {
          await runHostedChatCommand(trimmed, nextHistory, {
            commandStartedAt,
            typedCommandUserMessageId: userMsg.id,
            skipCommandNormalize: true,
          })
          setMessages((prev) => markNoteConfirmResolved(prev, pendingNoteConfirm.id))
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      const pendingSwitchTyped = findPendingPatientSwitchConfirm(messagesRef.current)
      const switchAnswerTyped =
        pendingMailSend ? null : parsePatientSwitchVoiceAnswer(trimmed)
      const reminderAwaiting = hasPendingRemindConfirmation(messagesRef.current)
      const noteAwaiting = hasPendingNoteConfirmation(messagesRef.current)
      if (
        switchAnswerTyped &&
        !sportsNocShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
        !checkExamReceiptShouldBypassPatientSwitch(messagesRef.current, trimmed) &&
        !readModalityShouldBypassPatientSwitch(messagesRef.current, trimmed)
      ) {
        if (pendingSwitchTyped) {
          await fulfillPatientSwitchConfirm(pendingSwitchTyped.messageId, switchAnswerTyped)
          return
        }
        // Bare "yes"/"no" also means secretary/mail/note/reminder send confirm — do not swallow them just because
        // the transcript already contains a patient-switch "yes".
        if (
          !reminderAwaiting &&
          !noteAwaiting &&
          !pendingMailSend &&
          !pendingCheckExamReceiptOpen &&
          chatHasPatientSwitchYesReply(messagesRef.current) &&
          !findPendingBinaryConfirm(messagesRef.current)
        ) {
          return
        }
      }

      const pendingPatientShowMoreEarly = findPendingPatientOptionsMessage(messagesRef.current)
      if (pendingPatientShowMoreEarly?.options?.length && transcriptIsShowMorePatients(trimmed)) {
        const commandStartedAt = performance.now()
        setIsSending(true)
        try {
          const sendMessage = config.callbacks?.sendMessage
          if (sendMessage && !shouldUseHostedChatForSportsNoc(trimmed, messagesRef.current, sportsNocBackendActiveRef.current)) {
            const assistantText = await sendMessage({
              text: trimmed,
              clientId: config.clientId,
              tenantId: config.tenantId,
              apiBaseUrl: resolvedApiBaseUrl,
              patientContext,
              history: messagesRef.current,
            })
            const assistantMs = elapsedCommandProcessingMs(commandStartedAt)
            const assistantMsg: ChatMessage = {
              id: uid(),
              role: 'assistant',
              content: String(assistantText),
              timestamp: Date.now(),
            }
            setMessages((prev) =>
              mergePatientOptionsRefresh(prev, [
                assistantMs != null ? withTypedCommandProcessing(assistantMsg, assistantMs) : assistantMsg,
              ]),
            )
            cancelIntentNavigation()
          } else {
            await runHostedChatCommand(trimmed, messagesRef.current, {
              skipIntentNavigation: true,
              commandStartedAt,
            })
          }
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      const unreadListMidTyped = findPendingUnreadReadAloudMessageId(messagesRef.current)
      const unreadOpenIndexTyped = unreadListMidTyped ? parseUnreadThreadOpenIndex(trimmed) : null
      const unreadReadAloudRequested =
        unreadListMidTyped && unreadOpenIndexTyped === null ? parseUnreadReadAloudIntent(trimmed) : false
      if (
        !findPendingBinaryConfirm(messagesRef.current) &&
        unreadListMidTyped &&
        (unreadOpenIndexTyped !== null || unreadReadAloudRequested) &&
        !findPendingPatientOptionsMessage(messagesRef.current)
      ) {
        setIsSending(true)
        try {
          if (unreadOpenIndexTyped !== null) {
            tryOpenUnreadSelection(unreadListMidTyped, unreadOpenIndexTyped, 'unread-typed')
          } else if (unreadReadAloudRequested) {
            const rows = unreadRowsByMessageIdRef.current.get(unreadListMidTyped)
            const userMsg: ChatMessage = {
              id: uid(),
              role: 'user',
              content: trimmed,
              timestamp: Date.now(),
            }
            setMessages((prev) => appendChatUserMessages(prev, [userMsg]))
            const listFr = unreadListLanguageIsFrench(unreadListMidTyped)
            if (rows === undefined) {
              appendAssistantLine(
                listFr
                  ? 'La liste se charge encore — réessayez dans un instant.'
                  : 'That list is still loading — please try again in a moment.',
              )
            } else if (rows.length === 0) {
              appendAssistantLine(
                listFr
                  ? 'Il n’y a aucun message à lire dans cette liste.'
                  : 'There are no messages in that list to read.',
              )
            } else {
              void readAloud.synthesizeAndPlay(segmentsFromUnreadRows(rows, listFr ? 'fr' : 'en'))
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === unreadListMidTyped ? { ...m, unreadMessagesReadAloudPlayed: true } : m,
                ),
              )
            }
          }
        } finally {
          setIsSending(false)
        }
        cancelIntentNavigation()
        return
      }

      if (shouldSilenceSecretaryInlineEditTrigger(trimmed, messagesRef.current)) {
        const commandStartedAt = performance.now()
        setIsSending(true)
        try {
          await runHostedChatCommand(trimmed, messagesRef.current, {
            skipIntentNavigation: true,
            commandStartedAt,
          })
        } catch {
          cancelIntentNavigation()
        } finally {
          setIsSending(false)
        }
        return
      }

      if (await tryOpenSportsNocReviewEditFromTextRef.current(trimmed)) {
        cancelIntentNavigation()
        return
      }

      setIsSending(true)
      let commandText = trimmed
      let userContent = bubbleText
      let typedNormalizeProcessingMs: number | undefined
      let commandStartedAt = performance.now()
      const skipTypedNormalize = !shouldNormalizeTypedCommand(trimmed, opts)

      if (shouldNormalizeTypedCommand(trimmed, opts)) {
        const resolved = await resolveTypedCommandForHostedChat(trimmed, resolvedApiBaseUrl)
        commandText = resolved.commandText
        userContent = commandText
        typedNormalizeProcessingMs = resolved.typedNormalizeProcessingMs
        commandStartedAt = performance.now()
      }

      const userMsgId = uid()

      let nextHistory = appendChatUserMessages(messagesRef.current, [
        withTypedNormalizeProcessing(
          {
            id: userMsgId,
            role: 'user',
            content: userContent,
            timestamp: Date.now(),
          },
          typedNormalizeProcessingMs,
        ),
      ])
      if (
        findPendingMailSendConfirm(messagesRef.current) &&
        parseMailSendConfirmAnswer(trimmed) === 'no'
      ) {
        nextHistory = clearPendingMailSendConfirmInMessages(nextHistory)
      }
      nextHistory = markSportsNocYesNoResolvedInMessages(nextHistory)
      const renewFr = sportsNocFollowupFr()
      if (utteranceLooksLikeVidalRenewCommand(trimmed)) {
        nextHistory = [
          ...stripRenewOrdonnanceRenewingMessages(nextHistory),
          renewOrdonnanceRenewingMessage(renewFr),
        ]
      }
      messagesRef.current = nextHistory
      setMessages(nextHistory)

      try {
        const sendMessage = config.callbacks?.sendMessage
        const useHostedPath =
          shouldUseHostedChatForSportsNoc(trimmed, messagesRef.current, sportsNocBackendActiveRef.current) ||
          shouldUseHostedChatForReminder(trimmed, messagesRef.current) ||
          shouldUseHostedChatForNoteTask(trimmed, messagesRef.current) ||
          shouldUseHostedChatForNoteConfirm(trimmed, messagesRef.current) ||
          shouldUseHostedChatForCalendarTimeRetrieval(trimmed, messagesRef.current) ||
          shouldUseHostedChatForMessage(trimmed, messagesRef.current) ||
          shouldUseHostedChatForMailConfirm(trimmed, messagesRef.current) ||
          shouldUseHostedChatForCheckExamReceiptConfirm(trimmed, messagesRef.current) ||
          shouldUseHostedChatForReadModalityConfirm(trimmed, messagesRef.current) ||
          utteranceHasMailOrEmailKeyword(trimmed) ||
          utteranceLooksLikeHostedModalityOpen(trimmed)
        if (sendMessage && !useHostedPath) {
          const assistantText = await sendMessage({
            text: commandText,
            clientId: config.clientId,
            tenantId: config.tenantId,
            apiBaseUrl: resolvedApiBaseUrl,
            ...clientReminderRuntimePayload(),
            patientContext,
            history: nextHistory,
          })
          const assistantMs = elapsedCommandProcessingMs(commandStartedAt)
          const assistantMsg: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: String(assistantText),
            timestamp: Date.now(),
          }
          setMessages((prev) => {
            const marked = applyInteractivePreviewSupersedeToEarlierPreviews(prev, [assistantMsg])
            const stampedAssistant =
              assistantMs != null ? withTypedCommandProcessing(assistantMsg, assistantMs) : assistantMsg
            const next = [...marked, stampedAssistant]
            return next
          })
          cancelIntentNavigation()
        } else {
          await runHostedChatCommand(commandText, nextHistory, {
            commandStartedAt,
            typedCommandUserMessageId: userMsgId,
            skipCommandNormalize:
              skipTypedNormalize ||
              typedNormalizeProcessingMs != null ||
              hasPendingNoteConfirmation(messagesRef.current),
          })
          const afterHosted = messagesRef.current
          const strippedRenew = stripRenewOrdonnanceRenewingMessages(afterHosted)
          if (strippedRenew.length !== afterHosted.length) {
            messagesRef.current = strippedRenew
            setMessages(strippedRenew)
          }
        }
      } catch (err) {
        cancelIntentNavigation()
        const detail = err instanceof Error ? err.message : String(err)
        appendAssistantLine(
          sportsNocFollowupFr()
            ? `Désolé, la commande a échoué : ${detail}`
            : `Sorry, the command failed: ${detail}`,
        )
      } finally {
        setIsSending(false)
      }
    },
    [
      isSending,
      config.callbacks,
      sportsNocFollowupFr,
      appendAssistantLine,
      config.clientId,
      config.tenantId,
      resolvedApiBaseUrl,
      patientContext,
      fulfillPatientSwitchConfirm,
      fulfillGlobalIntentCancel,
      runHostedChatCommand,
      cancelIntentNavigation,
      appendAssistantLine,
      readAloud.synthesizeAndPlay,
      sportsNocFollowupFr,
    ],
  )

  const handleFolderOptionCardSelect = useCallback(
    (selectionId: string) => {
      const pending = findPendingFolderOptionsMessage(messagesRef.current)
      const idx = Number.parseInt(selectionId, 10)
      const opt =
        pending?.options && Number.isFinite(idx) && idx >= 0 && idx < pending.options.length
          ? pending.options[idx]
          : pending?.options?.find((o) => o.selection_id === selectionId)
      if (!opt || isSending) return
      const pickIdx =
        pending?.options?.findIndex((o) => o.selection_id === opt.selection_id) ??
        (Number.isFinite(idx) ? idx : -1)
      if (pickIdx < 0) return
      const folderSlug = (opt.folder_slug || '').trim()
      if (!folderSlug) return
      markFolderOptionsResolved()
      void sendFolderSelection(folderSlug)
    },
    [isSending, sendFolderSelection, markFolderOptionsResolved],
  )

  const handlePrescriptionTypeCardSelect = useCallback(
    (selectionId: string) => {
      const pending = [...messagesRef.current]
        .reverse()
        .find(
          (m) =>
            m.role === 'assistant' &&
            m.uiType === 'prescription_type_options' &&
            m.options?.length &&
            !m.prescriptionTypeOptionsResolved &&
            m.options.some((o) => o.selection_id === selectionId),
        )
      const opt = pending?.options?.find((o) => o.selection_id === selectionId)
      const answer = (selectionId || opt?.label || '').trim()
      if (!answer) return
      messagesRef.current = messagesRef.current.map((m) =>
        m.uiType === 'prescription_type_options' && m.options?.length && !m.prescriptionTypeOptionsResolved
          ? { ...m, prescriptionTypeOptionsResolved: true }
          : m,
      )
      void submitChatText(answer, { displayText: (opt?.label || selectionId).trim() })
    },
    [submitChatText],
  )

  const handleSportsNocLocationCardSelect = useCallback(
    async (locationId: number) => {
      const pending = findPendingSportsNocLocationOptions(messagesRef.current)
      const opt = pending?.options.find((o) => o.locationId === locationId)
      if (!opt || isSending) return
      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: opt.label,
        timestamp: Date.now(),
      }
      const next = appendChatUserMessages(messagesRef.current, [userMsg])
      messagesRef.current = next
      setMessages(next)
      await sendSportsNocLocationSelection(locationId)
    },
    [isSending, sendSportsNocLocationSelection],
  )

  const handleSportsNocSportToggle = useCallback(
    async (messageId: string, fieldId: number) => {
      if (sportsNocSummaryInlineEditRef.current?.field !== 'sports') {
        const next = toggleSportsNocSportSelectedInMessages(messagesRef.current, messageId, fieldId)
        messagesRef.current = next
        setMessages(next)
      }
      await sendSportsNocSportToggle(fieldId)
    },
    [sendSportsNocSportToggle],
  )

  const handleSportsNocSportsConfirm = useCallback(
    async (messageId: string) => {
      if (isSending) return
      const msg = messagesRef.current.find((m) => m.id === messageId)
      if (!msg) return
      const text = buildSportsNocSportsSubmitText(msg)
      if (text) {
        const userMsg: ChatMessage = {
          id: uid(),
          role: 'user',
          content: text,
          timestamp: Date.now(),
        }
        const next = appendChatUserMessages(messagesRef.current, [userMsg])
        messagesRef.current = next
        setMessages(next)
      }
      await sendSportsNocSportSubmit()
    },
    [isSending, sendSportsNocSportSubmit],
  )

  const onSportsNocYesNoSelect = useCallback(
    async (messageId: string, kind: SportsNocYesNoKind, choice: 'yes' | 'no') => {
      if (isSending) return
      const fr = sportsNocFollowupFr()
      // Keep API payloads short and skip Nova Lite rewrite.
      // Voice "envoyer" already works; long phrases can be normalized away or
      // (for no) hit ``_is_flow_cancel`` via "annuler"/"cancel".
      let apiText: string
      if (kind === 'mail_certificate') {
        apiText =
          choice === 'yes'
            ? fr
              ? 'envoyer'
              : 'send it'
            : fr
              ? 'non'
              : 'no'
      } else {
        apiText = choice === 'yes' ? (fr ? 'oui' : 'yes') : fr ? 'non' : 'no'
      }
      const mailLabels = sportsNocMailConfirmLabels(fr)
      const label =
        kind === 'mail_certificate'
          ? choice === 'yes'
            ? mailLabels.yes
            : mailLabels.no
          : choice === 'yes'
            ? fr
              ? 'Oui, générer'
              : 'Yes, generate'
            : fr
              ? 'Non, annuler'
              : 'No, cancel'
      // Use the same input pipeline as voice so command routing/cancel handling matches.
      // (submitChatText also marks sports_noc yes/no as resolved.)
      void messageId
      await submitChatText(apiText, { displayText: label })
    },
    [submitChatText, sportsNocFollowupFr, isSending],
  )

  const onSportsNocEditLocation = useCallback(
    (summaryMessageId: string) => {
      void openSportsNocSummaryInlineEdit(summaryMessageId, 'edit_location')
    },
    [openSportsNocSummaryInlineEdit],
  )

  const onSportsNocEditSports = useCallback(
    (summaryMessageId: string) => {
      void openSportsNocSummaryInlineEdit(summaryMessageId, 'edit_sports')
    },
    [openSportsNocSummaryInlineEdit],
  )

  const onSportsNocEditComments = useCallback(
    (summaryMessageId: string) => {
      void openSportsNocSummaryInlineEdit(summaryMessageId, 'edit_comments')
    },
    [openSportsNocSummaryInlineEdit],
  )

  const openNoteInlineEdit = useCallback((messageId: string) => {
    const src = messagesRef.current.find((m) => m.id === messageId)
    if (
      !src?.notePreview ||
      src.notePreviewResolved ||
      !src.awaitingNoteConfirmation ||
      src.noteConfirmed ||
      src.noteCancelled
    ) {
      return false
    }
    if (
      voice.status === 'recording' ||
      voice.status === 'connecting' ||
      voice.status === 'requesting_permission' ||
      voice.status === 'transcribing'
    ) {
      voice.toggleMic()
    }
    setNoteInlineVoiceBusy(false)
    const draft = (src.notePreview.content || '').trim()
    setNoteInlineEdit({
      messageId,
      draft,
      originalDraft: draft,
      saving: false,
      error: null,
    })
    return true
  }, [voice.status, voice.toggleMic])

  const onNotePreviewAction = useCallback(
    (messageId: string, action: 'yes' | 'no' | 'edit') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (src?.notePreviewResolved || !src?.awaitingNoteConfirmation) return
      if (action === 'edit') {
        openNoteInlineEdit(messageId)
        return
      }
      const langTag = (src?.notePreview?.language || '').toLowerCase()
      const fr =
        langTag === 'french' ||
        langTag === 'fr' ||
        (isFrenchLanguage())
      const text = action === 'yes' ? (fr ? 'oui continuer' : 'yes proceed') : fr ? 'non annuler' : 'no cancel'
      const label = action === 'yes' ? (fr ? 'Oui, continuer' : 'Yes, proceed') : fr ? 'Non, annuler' : 'No, cancel'
      void submitChatText(text, { displayText: label })
    },
    [submitChatText, openNoteInlineEdit],
  )

  const onReadModalityYesNo = useCallback(
    (messageId: string, choice: 'yes' | 'no') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (!src || !readModalityConfirmIsActionable(src, false)) return
      const fr = readModalityUiFrench(src)
      const text =
        choice === 'yes' ? (fr ? 'oui continuer' : 'yes proceed') : fr ? 'non annuler' : 'no cancel'
      const label =
        choice === 'yes'
          ? fr
            ? 'Oui, continuer'
            : 'Yes, proceed'
          : fr
            ? 'Non, annuler'
            : 'No, cancel'
      void submitChatText(text, { displayText: label })
    },
    [submitChatText],
  )

  const onCheckExamReceiptYesNo = useCallback(
    (messageId: string, choice: 'yes' | 'no') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (!src || !checkExamReceiptConfirmIsActionable(src, false)) return
      const fr = checkExamReceiptUiFrench(src)
      const text =
        choice === 'yes' ? (fr ? 'oui ouvrir' : 'yes open') : fr ? 'non annuler' : 'no cancel'
      const label =
        choice === 'yes'
          ? fr
            ? 'Oui, ouvrir'
            : 'Yes, open'
          : fr
            ? 'Non, annuler'
            : 'No, cancel'
      void submitChatText(text, { displayText: label })
    },
    [submitChatText],
  )

  const onNoteInlineDraftChange = useCallback((value: string) => {
    setNoteInlineEdit((prev) => (prev ? { ...prev, draft: value, error: null } : prev))
  }, [])

  const onNoteInlineCancel = useCallback(() => {
    setNoteInlineVoiceBusy(false)
    setNoteInlineEdit(null)
  }, [])

  const onNoteInlineSave = useCallback(async () => {
    const edit = noteInlineEdit
    if (!edit?.messageId || edit.saving || isSending) return
    const content = edit.draft.trim()
    if (!content) {
      const src = messagesRef.current.find((m) => m.id === edit.messageId)
      const fr =
        (src?.notePreview?.language || '').toLowerCase() === 'french' ||
        (src?.notePreview?.language || '').toLowerCase() === 'fr' ||
        (isFrenchLanguage())
      setNoteInlineEdit((prev) =>
        prev
          ? {
              ...prev,
              error: fr ? 'Saisissez le texte de la tâche.' : 'Enter the task text.',
            }
          : prev,
      )
      return
    }
    const src = messagesRef.current.find((m) => m.id === edit.messageId)
    const fr =
      (src?.notePreview?.language || '').toLowerCase() === 'french' ||
      (src?.notePreview?.language || '').toLowerCase() === 'fr' ||
      (isFrenchLanguage())
    setNoteInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
    setIsSending(true)
    try {
      await runHostedChatCommand(content, messagesRef.current, { skipIntentNavigation: true })
      setNoteInlineVoiceBusy(false)
      setNoteInlineEdit(null)
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      setNoteInlineEdit((prev) =>
        prev
          ? {
              ...prev,
              saving: false,
              error: detail || (fr ? 'Impossible de modifier la tâche.' : 'Could not update the task.'),
            }
          : prev,
      )
    } finally {
      setIsSending(false)
    }
  }, [noteInlineEdit, isSending, runHostedChatCommand])

  const onReminderPreviewAction = useCallback(
    (messageId: string, action: 'yes' | 'no' | 'edit_purpose' | 'edit_day' | 'edit_time') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (
        src?.reminderYesNoSuperseded ||
        src?.reminderPreviewResolved ||
        !src?.awaitingRemindConfirmation
      ) {
        return
      }
      const langTag = (src?.reminderPreview?.language || '').toLowerCase()
      const fr =
        langTag === 'french' ||
        langTag === 'fr' ||
        (isFrenchLanguage())
      if (action === 'edit_purpose' || action === 'edit_day' || action === 'edit_time') {
        const field = action.replace('edit_', '') as ReminderPreviewEditField
        openReminderInlineEdit(messageId, field)
        return
      }
      const textByAction: Record<'yes' | 'no' | 'edit_purpose' | 'edit_day' | 'edit_time', string> = {
        yes: fr ? 'oui continuer' : 'yes proceed',
        no: fr ? 'non annuler' : 'no cancel',
        edit_purpose: fr ? "modifier l'objet" : 'edit purpose',
        edit_day: fr ? 'modifier le jour' : 'edit day',
        edit_time: fr ? "modifier l'heure" : 'edit time',
      }
      const labelByAction: Record<'yes' | 'no' | 'edit_purpose' | 'edit_day' | 'edit_time', string> = {
        yes: fr ? 'Oui, continuer' : 'Yes, proceed',
        no: fr ? 'Non, annuler' : 'No, cancel',
        edit_purpose: fr ? "Modifier l'objet" : 'Edit purpose',
        edit_day: fr ? 'Modifier jour' : 'Edit day',
        edit_time: fr ? 'Modifier heure' : 'Edit time',
      }
      void submitChatText(textByAction[action], { displayText: labelByAction[action] })
    },
    [submitChatText, openReminderInlineEdit],
  )

  const onReminderInlineDraftChange = useCallback((value: string) => {
    setReminderInlineEdit((prev) => (prev ? { ...prev, draft: value, error: null } : prev))
  }, [])

  const onReminderInlineCancel = useCallback(async () => {
    const edit = reminderInlineEdit
    if (!edit?.messageId || edit.saving || isSending) return
    if (edit.source === 'local') {
      setReminderInlineVoiceBusy(false)
      setReminderInlineEdit(null)
      return
    }
    const src = messagesRef.current.find((m) => m.id === edit.messageId)
    const fr =
      (src?.reminderPreview?.language || '').toLowerCase() === 'french' ||
      (src?.reminderPreview?.language || '').toLowerCase() === 'fr' ||
      (isFrenchLanguage())
    setReminderInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
    setIsSending(true)
    try {
      await runHostedChatCommand(fr ? 'annuler modification' : 'cancel edit', messagesRef.current, {
        skipIntentNavigation: true,
      })
      setReminderInlineVoiceBusy(false)
      setReminderInlineEdit(null)
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      setReminderInlineEdit((prev) =>
        prev ? { ...prev, saving: false, error: detail || 'Could not cancel reminder edit.' } : prev,
      )
    } finally {
      setIsSending(false)
    }
  }, [reminderInlineEdit, isSending, runHostedChatCommand])

  const onReminderInlineSave = useCallback(async () => {
    const edit = reminderInlineEdit
    if (!edit?.messageId || edit.saving || isSending) return
    const src = messagesRef.current.find((m) => m.id === edit.messageId)
    const built = buildReminderInlineEditCommand(edit.field, edit.draft, {
      language: src?.reminderPreview?.language,
    })
    if (!built) {
      const fr =
        (src?.reminderPreview?.language || '').toLowerCase() === 'french' ||
        (src?.reminderPreview?.language || '').toLowerCase() === 'fr' ||
        (isFrenchLanguage())
      const err =
        edit.field === 'purpose'
          ? fr
            ? 'Saisissez un objet pour le rappel.'
            : 'Enter a purpose for the reminder.'
          : edit.field === 'day'
            ? fr
              ? 'Choisissez une date valide.'
              : 'Choose a valid date.'
            : fr
              ? 'Choisissez une heure valide.'
              : 'Choose a valid time.'
      setReminderInlineEdit((prev) => (prev ? { ...prev, error: err } : prev))
      return
    }
    setReminderInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
    setIsSending(true)
    try {
      await runHostedChatCommand(built.command, messagesRef.current, { skipIntentNavigation: true })
      setReminderInlineVoiceBusy(false)
      setReminderInlineEdit(null)
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      setReminderInlineEdit((prev) =>
        prev ? { ...prev, saving: false, error: detail || 'Could not update reminder.' } : prev,
      )
    } finally {
      setIsSending(false)
    }
  }, [reminderInlineEdit, isSending, runHostedChatCommand])

  const onSecretaryInlineDraftChange = useCallback((value: string) => {
    setSecretaryInlineEdit((prev) =>
      prev
        ? {
            ...prev,
            draft: value,
            error: null,
            assigneeOptions: prev.field === 'recipient_name' ? [] : prev.assigneeOptions,
          }
        : prev,
    )
  }, [])

  const onSecretaryInlineCancel = useCallback(() => {
    const edit = secretaryInlineEdit
    if (!edit?.messageId || edit.saving) return
    setSecretaryInlineVoiceBusy(false)
    setSecretaryInlineEdit(null)
  }, [secretaryInlineEdit])

  const onSecretaryInlineSave = useCallback(async () => {
    const edit = secretaryInlineEdit
    if (!edit?.messageId || edit.saving || isSending) return
    const fr =
      isFrenchLanguage()
    const command = buildSecretaryInlineEditCommand(edit.field, edit.draft, fr)
    if (!command) {
      const err =
        edit.field === 'purpose'
          ? fr
            ? "Saisissez un objet pour le message."
            : 'Enter a purpose for the message.'
          : edit.field === 'message'
            ? fr
              ? 'Saisissez un message.'
              : 'Enter a message.'
            : fr
              ? 'Saisissez un nom de destinataire.'
              : 'Enter a recipient name.'
      setSecretaryInlineEdit((prev) => (prev ? { ...prev, error: err } : prev))
      return
    }
    setSecretaryInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
    setIsSending(true)
    try {
      await runHostedChatCommand(command, messagesRef.current, { skipIntentNavigation: true })
      const keepInlineRecipientPicker =
        edit.field === 'recipient_name' && hostedRowsHaveAssigneeOptions(lastHostedAssistantRowsRef.current)
      setSecretaryInlineVoiceBusy(false)
      if (keepInlineRecipientPicker) {
        const inlineOptions = assigneeOptionsFromApiRows(
          lastHostedAssistantRowsRef.current,
          getMedicalLinkCredentialsForApi().loggedUserId,
        )
        setSecretaryInlineEdit((prev) =>
          prev
            ? {
                ...prev,
                originalDraft: prev.draft,
                assigneeOptions: inlineOptions,
                saving: false,
                error: null,
              }
            : prev,
        )
      } else {
        if (edit.field === 'recipient_name') {
          setMessages((prev) => {
            const next = prev.map((m) =>
              m.uiType === 'assignee_options' && !m.assigneeOptionsResolved
                ? { ...m, assigneeOptionsResolved: true }
                : m,
            )
            persistSessionChatHistoryNow(next)
            return next
          })
        }
        setSecretaryInlineEdit(null)
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      setSecretaryInlineEdit((prev) =>
        prev ? { ...prev, saving: false, error: detail || 'Could not update message preview.' } : prev,
      )
    } finally {
      setIsSending(false)
    }
  }, [
    secretaryInlineEdit,
    isSending,
    runHostedChatCommand,
    persistSessionChatHistoryNow,
    getMedicalLinkCredentialsForApi,
  ])

  const patchMailDocumentOnMessage = useCallback(
    (messageId: string, field: MailDocumentInlineEditField, value: string) => {
      setMessages((prev) => {
        const next = prev.map((m) =>
          m.id === messageId && m.mailDocument
            ? { ...m, mailDocument: applyMailDocumentFieldUpdate(m.mailDocument, field, value) }
            : m,
        )
        persistSessionChatHistoryNow(next)
        return next
      })
    },
    [persistSessionChatHistoryNow],
  )

  const openMailDocumentInlineEdit = useCallback((messageId: string, field: MailDocumentInlineEditField) => {
    const src = messagesRef.current.find((m) => m.id === messageId)
    if (!src?.mailDocument || !isMailDocumentPreviewEditable(src, Boolean(src.historySnapshot))) {
      return false
    }
    const draft = mailDocumentDraftForField(src.mailDocument, field)
    setMailDocumentInlineEdit({
      messageId,
      field,
      draft,
      originalDraft: draft,
      assigneeOptions: [],
      saving: false,
      error: null,
    })
    return true
  }, [])

  const onMailPreviewEdit = useCallback(
    (messageId: string, action: 'edit_subject' | 'edit_message' | 'edit_recipient_name') => {
      const field: MailDocumentInlineEditField =
        action === 'edit_subject' ? 'subject' : action === 'edit_message' ? 'message' : 'recipient_name'
      openMailDocumentInlineEdit(messageId, field)
    },
    [openMailDocumentInlineEdit],
  )

  const onMailDocumentInlineDraftChange = useCallback((value: string) => {
    setMailDocumentInlineEdit((prev) =>
      prev
        ? {
            ...prev,
            draft: value,
            error: null,
            assigneeOptions: prev.field === 'recipient_name' ? [] : prev.assigneeOptions,
          }
        : prev,
    )
  }, [])

  const onMailDocumentInlineCancel = useCallback(() => {
    const edit = mailDocumentInlineEdit
    if (!edit?.messageId || edit.saving) return
    setMailDocumentInlineVoiceBusy(false)
    setMailDocumentInlineEdit(null)
  }, [mailDocumentInlineEdit])

  const onMailDocumentInlineSave = useCallback(async () => {
    const edit = mailDocumentInlineEdit
    if (!edit?.messageId || edit.saving || isSending) return
    const src = messagesRef.current.find((m) => m.id === edit.messageId)
    const fr =
      isFrenchLanguage()
    const command = buildMailInlineEditCommand(edit.field, edit.draft, fr)
    if (!command) {
      const err =
        edit.field === 'subject'
          ? fr
            ? "Saisissez un objet pour le courriel."
            : 'Enter a subject for the email.'
          : edit.field === 'message'
            ? fr
              ? 'Saisissez un message.'
              : 'Enter a message.'
            : fr
              ? 'Saisissez un nom de destinataire.'
              : 'Enter a recipient name.'
      setMailDocumentInlineEdit((prev) => (prev ? { ...prev, error: err } : prev))
      return
    }
    setMailDocumentInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
    patchMailDocumentOnMessage(edit.messageId, edit.field, edit.draft)
    const syncGenericMail = src && isGenericMailDocumentPreviewMessage(src)
    if (!syncGenericMail) {
      setMailDocumentInlineVoiceBusy(false)
      setMailDocumentInlineEdit(null)
      return
    }
    setIsSending(true)
    try {
      await runHostedChatCommand(command, messagesRef.current, { skipIntentNavigation: true })
      const keepInlineRecipientPicker =
        edit.field === 'recipient_name' && hostedRowsHaveAssigneeOptions(lastHostedAssistantRowsRef.current)
      setMailDocumentInlineVoiceBusy(false)
      if (keepInlineRecipientPicker) {
        const inlineOptions = assigneeOptionsFromApiRows(
          lastHostedAssistantRowsRef.current,
          getMedicalLinkCredentialsForApi().loggedUserId,
        )
        setMailDocumentInlineEdit((prev) =>
          prev
            ? {
                ...prev,
                originalDraft: prev.draft,
                assigneeOptions: inlineOptions,
                saving: false,
                error: null,
              }
            : prev,
        )
      } else {
        setMailDocumentInlineEdit(null)
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      setMailDocumentInlineEdit((prev) =>
        prev ? { ...prev, saving: false, error: detail || 'Could not update mail preview.' } : prev,
      )
    } finally {
      setIsSending(false)
    }
  }, [
    mailDocumentInlineEdit,
    isSending,
    patchMailDocumentOnMessage,
    runHostedChatCommand,
    getMedicalLinkCredentialsForApi,
  ])

  const onMailDocumentInlineAssigneeSelect = useCallback(
    async (selectionId: string) => {
      const edit = mailDocumentInlineEdit
      if (!edit || edit.field !== 'recipient_name' || edit.saving) return
      const opt = edit.assigneeOptions.find((o) => o.selection_id === selectionId)
      if (opt?.label?.trim()) {
        patchMailDocumentOnMessage(edit.messageId, 'recipient_name', opt.label.trim())
      }
      const src = messagesRef.current.find((m) => m.id === edit.messageId)
      if (src && isGenericMailDocumentPreviewMessage(src)) {
        setMailDocumentInlineEdit((prev) => (prev ? { ...prev, saving: true, error: null } : prev))
        setIsSending(true)
        try {
          const ok = await sendAssigneeSelection(selectionId)
          if (!ok) {
            setMailDocumentInlineEdit((prev) =>
              prev ? { ...prev, saving: false, error: 'Could not select recipient.' } : prev,
            )
            return
          }
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err)
          setMailDocumentInlineEdit((prev) =>
            prev ? { ...prev, saving: false, error: detail || 'Could not select recipient.' } : prev,
          )
          return
        } finally {
          setIsSending(false)
        }
      }
      setMailDocumentInlineVoiceBusy(false)
      setMailDocumentInlineEdit(null)
    },
    [mailDocumentInlineEdit, patchMailDocumentOnMessage, sendAssigneeSelection],
  )

  const onSecretaryPreviewAction = useCallback(
    (
      messageId: string,
      action:
        | 'edit_purpose'
        | 'edit_message'
        | 'edit_recipient_name'
        | 'edit_recipient_type'
        | 'toggle_urgent',
    ) => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (!src || !isSecretaryPreviewActionable(src)) return
      if (action === 'toggle_urgent') {
        if (isSending || secretaryUrgentToggleBusyMessageId === messageId) return
        const urgentOn = src.secretaryMessage?.urgent === true
        setSecretaryUrgentToggleBusyMessageId(messageId)
        void sendSecretaryUrgentToggle(!urgentOn).finally(() => {
          setSecretaryUrgentToggleBusyMessageId((current) => (current === messageId ? null : current))
        })
        return
      }
      const fr = secretaryPreviewFrFromMessage(src)
      const textByAction: Record<typeof action, string> = {
        edit_purpose: fr ? 'modifier objet' : 'edit subject',
        edit_message: fr ? 'modifier message' : 'edit message',
        edit_recipient_name: fr ? 'modifier nom destinataire' : 'edit recipient name',
        edit_recipient_type: fr ? 'modifier type destinataire' : 'edit recipient type',
        toggle_urgent: '',
      }
      const labelByAction: Record<typeof action, string> = {
        edit_purpose: fr ? "Modifier l'objet" : 'Edit subject',
        edit_message: fr ? 'Modifier message' : 'Edit message',
        edit_recipient_name: fr ? 'Modifier nom destinataire' : 'Edit recipient name',
        edit_recipient_type: fr ? 'Modifier type destinataire' : 'Edit recipient type',
        toggle_urgent: '',
      }
      void submitChatText(textByAction[action], { displayText: labelByAction[action] })
    },
    [isSending, secretaryUrgentToggleBusyMessageId, sendSecretaryUrgentToggle, submitChatText],
  )

  const onSecretarySendConfirm = useCallback(
    (messageId: string, choice: 'yes' | 'no') => {
      const src = messagesRef.current.find((m) => m.id === messageId)
      if (!src || !isSecretaryPreviewActionable(src)) return
      const fr = secretaryPreviewFrFromMessage(src)
      const labels = fr
        ? { yes: 'Oui, envoyer', no: 'Ne pas envoyer' }
        : { yes: 'Yes, send it', no: 'No, do not send' }
      void submitChatText(binaryConfirmSubmittedText('secretary', choice), {
        displayText: choice === 'yes' ? labels.yes : labels.no,
      })
    },
    [submitChatText],
  )

  const onGlobalIntentCancel = useCallback(() => {
    void fulfillGlobalIntentCancel()
  }, [fulfillGlobalIntentCancel])

  const binaryConfirmKind = useMemo(() => findPendingBinaryConfirm(messages), [messages])

  const onSend = async () => {
    const text = draft.trim()
    if (!text || isSending) return

    const pendingFolder = findPendingFolderOptionsMessage(messagesRef.current)
    const folderOrdinalIdx = parseOrdinalChoice(text)
    if (
      pendingFolder?.options &&
      folderOrdinalIdx !== null &&
      folderOrdinalIdx >= 0 &&
      folderOrdinalIdx < pendingFolder.options.length
    ) {
      setDraft('')
      const opt = pendingFolder.options[folderOrdinalIdx]
      markFolderOptionsResolved()
      const folderSlug = (opt.folder_slug || '').trim()
      if (folderSlug) {
        await sendFolderSelection(folderSlug)
      } else {
        await submitChatText(String(folderOrdinalIdx + 1), {
          displayText: formatFolderSelectionMessage(opt, inferSelectionLangFromText(text)),
        })
      }
      return
    }

    const pendingOptions = findPendingPatientOptionsMessage(messagesRef.current)
    const ordinalIdx = parseOrdinalChoice(text)
    if (
      pendingOptions?.options &&
      ordinalIdx !== null &&
      ordinalIdx >= 0 &&
      ordinalIdx < pendingOptions.options.length
    ) {
      setDraft('')
      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: formatPatientSelectionMessage(
          pendingOptions.options[ordinalIdx],
          inferSelectionLangFromText(text),
        ),
        timestamp: Date.now(),
      }
      setMessages((prev) => appendChatUserMessages(prev, [userMsg]))
      await sendPatientSelection(pendingOptions.options[ordinalIdx].selection_id)
      return
    }

    setDraft('')
    await submitChatText(text)
  }

  const onMicClick = () => {
    if (micDisabled) return
    setVoiceError(null)
    voice.toggleMic()
  }

  const themeVars = {
    '--vnw-primary': theme.primary,
    '--vnw-primaryText': theme.primaryText,
    '--vnw-panelBg': theme.panelBg,
    '--vnw-launcherBg': theme.launcherBg,
    '--vnw-text': theme.text,
    '--vnw-mutedText': theme.mutedText,
    '--vnw-border': theme.border,
    '--vnw-shadow': theme.shadow,
    '--vnw-textareaBg': theme.textareaBg,
    '--vnw-textareaBorder': theme.textareaBorder,
    '--vnw-buttonBg': theme.buttonBg,
    '--vnw-buttonText': theme.buttonText,
    '--vnw-buttonDisabledBg': theme.buttonDisabledBg,
    '--vnw-buttonDisabledText': theme.buttonDisabledText,
  } as CSSProperties

  const modalityPreviewTitle = modalityPreview
    ? modalityPreview.previewTitle?.trim() ||
      (modalityPreview.previewKind === 'prescription' ? 'Prescription' : 'Exam file')
    : 'Exam file'

  const showReadAloudBar = Boolean(
    readAloud.phase !== 'idle' || readAloud.canReplay || readAloud.error,
  )
  const readAloudBarLabel = (() => {
    const fr =
      isFrenchLanguage()
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role !== 'assistant') continue
      if (m.readModalityYesNoPrompt || m.generalConfirmationContext === 'read_modality') {
        return fr ? 'Compte rendu — lecture à voix haute' : 'Report — read aloud'
      }
      if (m.unreadMessagesUi) {
        return fr ? 'Messages non lus — lecture' : 'Unread messages — read aloud'
      }
    }
    return fr ? 'Messages non lus — lecture' : 'Unread messages — read aloud'
  })()

  return (
    <div className="vnw-widgetRoot" data-position={position} style={themeVars}>
      {modalityPreview ? (
        <section
          className={
            modalityPreviewMaximized
              ? 'vnw-modalityPreview vnw-modalityPreview--maximized'
              : 'vnw-modalityPreview'
          }
          aria-label={`${modalityPreviewTitle} preview`}
        >
          <header className="vnw-modalityPreviewHeader">
            <span className="vnw-modalityPreviewTitle">{modalityPreviewTitle}</span>
            <div className="vnw-modalityPreviewActions">
              <button
                type="button"
                className="vnw-modalityPreviewHeaderBtn"
                aria-label={
                  modalityPreviewMaximized
                    ? `Restore ${modalityPreviewTitle.toLowerCase()} preview size`
                    : `Maximize ${modalityPreviewTitle.toLowerCase()} preview`
                }
                onClick={() => setModalityPreviewMaximized((v) => !v)}
              >
                {modalityPreviewMaximized ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
              <button
                type="button"
                className="vnw-modalityPreviewHeaderBtn"
                aria-label={`Close ${modalityPreviewTitle.toLowerCase()} preview`}
                onClick={closeModalityPreview}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </header>
          <div className="vnw-modalityPreviewFrame">
            <iframe
              key={modalityPreview.frameKey}
              className="vnw-modalityPreviewIframe"
              src={modalityPreview.url}
              title={modalityPreviewTitle}
              onLoad={
                modalityPreview.sportsNocNotifyPreviewOpened ||
                modalityPreview.renewOrdonnanceNotifyPreviewOpened
                  ? onModalityPreviewIframeLoad
                  : undefined
              }
            />
          </div>
        </section>
      ) : null}
      {!open && wakePhrase.debugHeard ? (
        <div className="vnw-wakeHeardDebug" role="status" aria-live="polite">
          <span className="vnw-wakeHeardDebugLabel">Wake heard:</span> {wakePhrase.debugHeard}
        </div>
      ) : null}
      <LauncherButton
        isOpen={open}
        onClick={togglePanel}
        disabled={false}
      />

      <ChatPanel
        open={open}
        title={panelTitle}
        subtitle={`${chatUi.activePatientLabel}: ${activePatientDisplay}`}
        onClose={closePanel}
        onCancelIntent={onGlobalIntentCancel}
        cancelIntentDisabled={isSending}
        cancelIntentLabel={globalIntentCancelDisplayText()}
        cancelIntentAriaLabel={globalIntentCancelAriaLabel()}
        closeAriaLabel={chatUi.closePanel}
      >
        {open && wakePhrase.panel ? (
          <div
            className={
              wakePhrase.panel.tone === 'error'
                ? 'vnw-wakePhraseNotice vnw-wakePhraseNotice--inPanel vnw-wakePhraseNotice--error'
                : 'vnw-wakePhraseNotice vnw-wakePhraseNotice--inPanel'
            }
            role="status"
            aria-live="polite"
          >
            <div className="vnw-wakePhraseNoticeMain">{wakePhrase.panel.main}</div>
            {wakePhrase.panel.sub ? (
              <div className="vnw-wakePhraseNoticeSub">{wakePhrase.panel.sub}</div>
            ) : null}
          </div>
        ) : null}
        {open && wakePhrase.debugHeard ? (
          <div className="vnw-wakeHeardDebug vnw-wakeHeardDebug--inPanel" role="status" aria-live="polite">
            <span className="vnw-wakeHeardDebugLabel">Wake heard:</span> {wakePhrase.debugHeard}
          </div>
        ) : null}
        {statusLine ? (
          <div className="vnw-statusLine" aria-live="polite">
            {statusLine}
          </div>
        ) : null}
        <MessageList
          messages={messages}
          isSending={isSending}
          voiceIntentProcessing={voiceIntentProcessing}
          voiceProcessingUserMessageId={voiceProcessingUserMessageId}
          listRef={messageListRef}
          endRef={messageEndRef}
          onPatientOptionSelect={handlePatientCardSelect}
          onFolderOptionSelect={handleFolderOptionCardSelect}
          onAssigneeOptionSelect={handleAssigneeCardSelect}
          onRecipientTypeOptionSelect={handleRecipientTypeCardSelect}
          onDeliveryChannelOptionSelect={handleDeliveryChannelCardSelect}
          onPrescriptionTypeOptionSelect={handlePrescriptionTypeCardSelect}
          onSportsNocLocationSelect={handleSportsNocLocationCardSelect}
          onSportsNocSportToggle={handleSportsNocSportToggle}
          onSportsNocSportsConfirm={handleSportsNocSportsConfirm}
          onSportsNocCommentsSubmit={(text) => {
            void sendSportsNocCommentsSubmit(text)
          }}
          onPatientOptionsShowMore={handlePatientOptionsShowMore}
          getMedicalLinkCredentials={getMedicalLinkCredentialsForApi}
          sportsNocActions={{
            onEditLocation: onSportsNocEditLocation,
            onEditSports: onSportsNocEditSports,
            onEditComments: onSportsNocEditComments,
          }}
          sportsNocSummaryInlineEdit={
            sportsNocSummaryInlineEdit
              ? {
                  ...sportsNocSummaryInlineEdit,
                  isSending,
                  onLocationDraftChange: (locationId) => {
                    setSportsNocSummaryInlineEdit((prev) =>
                      prev ? { ...prev, locationDraftId: locationId, error: null } : prev,
                    )
                  },
                  onSportToggle: (fieldId) => {
                    void sendSportsNocSportToggle(fieldId)
                  },
                  onCommentsDraftChange: (text) => {
                    setSportsNocSummaryInlineEdit((prev) =>
                      prev ? { ...prev, commentsDraft: text, error: null } : prev,
                    )
                  },
                  onSave: () => void onSportsNocSummaryInlineSave(),
                  onCancel: () => void onSportsNocSummaryInlineCancel(),
                  voiceTranscribe:
                    sportsNocSummaryInlineEdit.field === 'comments'
                      ? {
                          ...inlineVoiceTranscribe,
                          blocked: isSending || mainVoiceOccupiesMic,
                          onBusyChange: setSportsNocInlineVoiceBusy,
                        }
                      : undefined,
                }
              : null
          }
          onSportsNocYesNo={onSportsNocYesNoSelect}
          onUnreadRowsLoaded={handleUnreadRowsLoaded}
          onUnreadMessageOpen={handleUnreadMessageOpen}
          onUnreadReadAloudRequest={handleUnreadReadAloudRequest}
          onUnreadReadAloudDecline={handleUnreadReadAloudDecline}
          onRenewOrdonnanceMailYesNo={handleRenewOrdonnanceMailYesNo}
          renewOrdonnanceMailConfirmUnlockedIds={renewOrdonnanceMailConfirmUnlockedIds}
          renewOrdonnanceSuccessUnlockedIds={renewOrdonnanceSuccessUnlockedIds}
          onReminderPreviewAction={onReminderPreviewAction}
          onNotePreviewAction={onNotePreviewAction}
          notePreviewInlineEdit={
            noteInlineEdit
              ? {
                  messageId: noteInlineEdit.messageId,
                  draft: noteInlineEdit.draft,
                  saving: noteInlineEdit.saving,
                  error: noteInlineEdit.error,
                  voiceTranscribe: {
                    ...inlineVoiceTranscribe,
                    blocked: notePreviewVoiceBlocked,
                    onBusyChange: setNoteInlineVoiceBusy,
                  },
                  onDraftChange: onNoteInlineDraftChange,
                  onSave: () => void onNoteInlineSave(),
                  onCancel: onNoteInlineCancel,
                }
              : undefined
          }
          reminderPreviewInlineEdit={
            reminderInlineEdit
              ? {
                  messageId: reminderInlineEdit.messageId,
                  field: reminderInlineEdit.field,
                  draft: reminderInlineEdit.draft,
                  saving: reminderInlineEdit.saving,
                  error: reminderInlineEdit.error,
                  voiceTranscribe: {
                    ...inlineVoiceTranscribe,
                    blocked: isSending || mainVoiceOccupiesMic,
                    onBusyChange: setReminderInlineVoiceBusy,
                  },
                  onDraftChange: onReminderInlineDraftChange,
                  onSave: () => void onReminderInlineSave(),
                  onCancel: onReminderInlineCancel,
                }
              : undefined
          }
          secretaryPreviewInlineEdit={
            secretaryInlineEdit
              ? {
                  messageId: secretaryInlineEdit.messageId,
                  field: secretaryInlineEdit.field,
                  draft: secretaryInlineEdit.draft,
                  saving: secretaryInlineEdit.saving,
                  error: secretaryInlineEdit.error,
                  assigneeOptions: secretaryInlineEdit.assigneeOptions,
                  showAssigneeOptions:
                    secretaryInlineEdit.field === 'recipient_name' &&
                    secretaryInlineEdit.draft.trim() === secretaryInlineEdit.originalDraft.trim(),
                  voiceTranscribe: {
                    ...inlineVoiceTranscribe,
                    blocked: isSending || mainVoiceOccupiesMic,
                    onBusyChange: setSecretaryInlineVoiceBusy,
                  },
                  onDraftChange: onSecretaryInlineDraftChange,
                  onSave: () => void onSecretaryInlineSave(),
                  onCancel: onSecretaryInlineCancel,
                }
              : undefined
          }
          onSecretaryPreviewAction={onSecretaryPreviewAction}
          secretaryUrgentToggleBusyMessageId={secretaryUrgentToggleBusyMessageId}
          onSecretarySendConfirm={onSecretarySendConfirm}
          onMailSendConfirm={(_messageId, choice) => {
            const labels = mailSendConfirmLabels()
            void submitChatText(binaryConfirmSubmittedText('mail', choice), {
              displayText: choice === 'yes' ? labels.yes : labels.no,
            })
          }}
          onMailPreviewEdit={onMailPreviewEdit}
          mailDocumentPreviewInlineEdit={
            mailDocumentInlineEdit
              ? {
                  messageId: mailDocumentInlineEdit.messageId,
                  field: mailDocumentInlineEdit.field,
                  draft: mailDocumentInlineEdit.draft,
                  saving: mailDocumentInlineEdit.saving,
                  error: mailDocumentInlineEdit.error,
                  assigneeOptions: mailDocumentInlineEdit.assigneeOptions,
                  voiceTranscribe: {
                    ...inlineVoiceTranscribe,
                    blocked: isSending || mainVoiceOccupiesMic || mailDocumentInlineVoiceBusy,
                    onBusyChange: setMailDocumentInlineVoiceBusy,
                  },
                  onDraftChange: onMailDocumentInlineDraftChange,
                  onSave: () => void onMailDocumentInlineSave(),
                  onCancel: onMailDocumentInlineCancel,
                  onAssigneeOptionSelect: (selectionId) => void onMailDocumentInlineAssigneeSelect(selectionId),
                }
              : undefined
          }
          onPatientSwitchConfirm={onPatientSwitchConfirm}
          onReadModalityYesNo={onReadModalityYesNo}
          onCheckExamReceiptYesNo={onCheckExamReceiptYesNo}
        />
        {binaryConfirmKind === 'general' ? (
          <div
            className="vnw-binaryConfirmBar"
            role="group"
            aria-label="Confirm action"
          >
            <button
              type="button"
              className="vnw-binaryConfirmBtn vnw-binaryConfirmBtn--yes"
              disabled={isSending}
              onClick={() => void submitChatText(binaryConfirmSubmittedText(binaryConfirmKind, 'yes'))}
            >
              {binaryConfirmButtonLabels(binaryConfirmKind).yes}
            </button>
            <button
              type="button"
              className="vnw-binaryConfirmBtn vnw-binaryConfirmBtn--no"
              disabled={isSending}
              onClick={() => void submitChatText(binaryConfirmSubmittedText(binaryConfirmKind, 'no'))}
            >
              {binaryConfirmButtonLabels(binaryConfirmKind).no}
            </button>
          </div>
        ) : null}
        {showReadAloudBar ? (
          <ReadAloudBar
            phase={readAloud.phase}
            error={readAloud.error}
            canReplay={readAloud.canReplay}
            onPlay={readAloud.play}
            onPause={readAloud.pause}
            onStop={readAloud.stop}
            audioRef={readAloud.audioRef}
            barLabel={readAloudBarLabel}
          />
        ) : null}
        {navigationOpenFailedUrl ? (
          <div className="vnw-navFallback" role="alert">
            <p className="vnw-navFallbackText">
              Automatic navigation did not run (for example the link was blocked or the page is in a
              restrictive iframe). Use the button below to open the MediKaLLink page in this tab.
            </p>
            <button
              type="button"
              className="vnw-navFallbackBtn"
              onClick={() => {
                const u = navigationOpenFailedUrl
                if (u) {
                  const ok = tryNavigateMedilinkMenuSameTab(u)
                  if (!ok) {
                    try {
                      window.open(u, '_blank', 'noopener,noreferrer')
                    } catch {
                      // ignore
                    }
                  }
                }
                setNavigationOpenFailedUrl(null)
              }}
            >
              Open MediKaLLink page
            </button>
          </div>
        ) : null}
        <Composer
          value={draft}
          placeholder={composerPlaceholder}
          listeningPlaceholder={chatUi.listening}
          connectingPlaceholder={chatUi.connectingVoice}
          onChange={setDraft}
          onSend={onSend}
          onMicClick={onMicClick}
          micStatus={voice.status}
          voiceStatusHint={voice.statusHint}
          micDisabled={micDisabled}
          isSending={isSending}
          bottomLabel={composerBottomLabel}
          sendAriaLabel={chatUi.sendMessage}
          sendTitle={chatUi.send}
          messageInputAriaLabel={chatUi.messageInput}
          liveTranscriptionAriaLabel={chatUi.liveTranscription}
          showGttsButton={config.enableGttsTest !== false}
          gttsBusy={gttsTest.gttsBusy}
          onGttsClick={onGttsClick}
        />
      </ChatPanel>
    </div>
  )
}
